import { Centrifuge } from 'centrifuge';
import { mergeRoomChat } from '../../features/room/chatMessages';
import { fetchMe } from '../../features/auth/api';
import { createSettlementRetryGate } from '../../features/bets/settlementRetry';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Crown,
  GripHorizontal,
  History,
  Lock,
  MessageCircle,
  Mic,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Ticket,
  UsersRound,
  X,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent, type ReactNode } from 'react';
import type { Round } from '../../entities/round/types';
import type { GameCode, Room } from '../../entities/room/types';
import { BetHistorySheet } from '../../features/bets/BetHistorySheet';
import { GuestAuthSheet } from '../../features/auth/GuestAuthSheet';
import { fetchSocialFollowing } from '../../features/leaderboard/api';
import { roomOnlineCount } from '../../entities/room/types';
import { userName, userVipLevel, userWalletBrl, userWalletFiat } from '../../entities/user/types';
import { resolveAvatarUrl } from '../../shared/profile/avatarDefaults';
import { VipLevelBadge } from '../../shared/profile/VipLevelBadge';
import { roundSeqFromRound } from '../../shared/game/roundLabel';
import { localDayKey } from '../../shared/lib/datetime';
import type { PlayerUser } from '../../entities/user/types';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { DrawDetailContent } from '../../features/archive/DrawDetailContent';
import { fetchArchiveRounds, type ArchiveRound } from '../../features/archive/api';
import { fetchStatus } from '../../features/live-round/api';
import { fetchRooms } from '../../features/lobby/api';
import { fetchMaintenance } from '../../features/maintenance/api';
import {
  fetchActivityDrum,
  updateActivityDrumState,
  type ActivityDrumItem,
} from '../../features/profile/api';
import { fetchRaffleData, type RaffleData } from '../../features/wallet/api';
import {
  fetchChatMessages,
  fetchMyBets,
  fetchRoomDistribution,
  heartbeatRoom,
  placeBet,
  placeBetsBatch,
  sendChatMessage,
  sendVoiceMessage,
  settleMyRound,
  type BetItem,
  type ChatMessage,
  type ChatMessagesResponse,
  type ChipLine,
  type RoomDistribution,
  type RoomDistributionBet,
  type RoundSummaryResponse,
} from '../../features/room/api';
import { useSocialConfig } from '../../features/social/heatConfig';
import { selectHeatHighlights, type HeatEntry } from '../../features/social/heat';
import { BET_TABS, GAME_LABELS, SICBO_SUM_ODDS, defaultBetTab, findBetOption, gameNameKey, optionLabel, pickLabel, tabFor, type BetOption, type BetTarget } from '../../shared/game/betOptions';
import {
  batchWinRange,
  defaultOddsForBet,
  evalBetAgainstDraw,
  rouletteColor,
  rouletteLineNumbers,
  type DrawOutcome as DanmakuEvalDraw,
} from '../../shared/game/winPreview';
import { betNetAmount, signedNet } from '../../shared/game/betOutcomeText';
import { applyConsensus, deriveConsensus } from '../../shared/game/consensus';
import { betChipPreferenceKey, parseRememberedBetChip, resolveInitialBetChip } from '../../shared/game/betChipPreference';
import { rouletteConfirmLabel } from '../../shared/game/betConfirmLabel';
import { GameIcon } from '../../shared/game/GameIcon';
import { diceFacePips } from '../archive/rulesExample';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { defaultAvatarUrl } from '../../shared/profile/avatarDefaults';
import type { MessageKey } from '../../shared/i18n/messages';
import { fmtAmount, fmtCurrency, fmtInt, fmtMoney, fmtPercent } from '../../shared/lib/format';
import { roomDisplayName } from '../../shared/room/roomDisplayName';
import { initialOverlayState, reduceOverlay, type OverlayBanner, type OverlayEvent, type OverlayState } from '../../shared/room/roundOverlayMachine';
import { heroDrawFor, netByCurrency, type CardDraw } from '../../shared/room/resultCardView';
import { RoundResultBanner, type BannerView } from './RoundResultBanner';
import { RoundResultCard, drawValueText } from './RoundResultCard';
import { useHistoryLayers } from '../../shared/navigation/useHistoryLayers';
import { raffleJourneyTargetForStatus } from '../../shared/lib/raffleJourneyNavigation';
import { readStorage, writeStorage } from '../../shared/lib/storage';
import { useCountryStore } from '../../shared/country/countryStore';

const ROOM_ENTRY_INTENT_KEY = 'sorteia:roomEntryIntent';

const GUEST_ID_KEY = 'fd:guestId';
const FAB_SIDE_KEY = 'sorteia:betFabSide';
const FAB_TOP_KEY = 'sorteia:betFabTop';
const WIDGET_TOP_KEY = 'sorteia:roomWidgetTop';
const FAB_SIZE = 64;
const FAB_MARGIN = 12;
const ROOM_WIDGET_MIN_TOP = 164;
const DANMAKU_MODE_KEY = 'fd:danmakuMode';
const LEGACY_DANMAKU_ENABLED_KEY = 'fd:danmaku';
const LEGACY_DANMAKU_MIN_KEY = 'fd:danmakuMin';
type DanmakuMode = 'on' | 'min' | 'off';
type DanmakuItem = { id: string; text: string; row: number; kind: 'bet' | 'mine' | 'win' };
type RoomRevealDraw = {
  lastdigit?: number;
  sicbo?: number[];
  roulette?: number;
};
type RoomRevealState = {
  round: number;
  game: GameCode;
  draw: RoomRevealDraw;
  slotEnd: number;
  wheelEnd: number;
  exiting: boolean;
};

function readRememberedBetChip(storage: Storage | undefined, key: string) {
  if (!key) return null;
  return parseRememberedBetChip(readStorage(storage, key));
}

function handleTablistKey<T extends string>(
  event: ReactKeyboardEvent<HTMLButtonElement>,
  keys: readonly T[],
  current: T,
  onSelect: (key: T) => void,
  idFor: (key: T) => string,
) {
  const currentIndex = keys.indexOf(current);
  let nextIndex: number | null = null;
  if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % keys.length;
  if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + keys.length) % keys.length;
  if (event.key === 'Home') nextIndex = 0;
  if (event.key === 'End') nextIndex = keys.length - 1;
  if (nextIndex == null) return;
  event.preventDefault();
  const next = keys[nextIndex];
  onSelect(next);
  window.requestAnimationFrame(() => document.getElementById(idFor(next))?.focus());
}

export function RoomPage() {
  const activeRoomId = useAppStore((state) => state.activeRoomId);
  const setRoute = useAppStore((state) => state.setRoute);
  const setActiveRoomId = useAppStore((state) => state.setActiveRoomId);
  const setActiveProfileUserId = useAppStore((state) => state.setActiveProfileUserId);
  const setProfileReturn = useAppStore((state) => state.setProfileReturn);
  const openWalletTicketsFlow = useAppStore((state) => state.openWalletTicketsFlow);
  const showToast = useAppStore((state) => state.showToast);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const user = useAuthStore((state) => state.user);
  const fiatCurrency = useCountryStore((state) => state.country.fiatCurrency);
  const setUser = useAuthStore((state) => state.setUser);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const queryClient = useQueryClient();
  const settlementRetry = useRef(createSettlementRetryGate());
  const graceWalletRefresh = useRef<number | null>(null);
  const [walletRefreshCount, setWalletRefreshCount] = useState(0);
  useEffect(() => { settlementRetry.current = createSettlementRetryGate(); }, [token, activeRoomId]);
  const revealInitialized = useRef(false);
  const revealedRound = useRef(0);
  const danmakuSeen = useRef(new Set<string>());
  const danmakuWinnerSeen = useRef(new Set<string>());
  const danmakuRecentRows = useRef<Array<{ row: number; at: number }>>([]);
  const voiceRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceStreamRef = useRef<MediaStream | null>(null);
  const voiceChunksRef = useRef<Blob[]>([]);
  const voiceMimeTypeRef = useRef('');
  const voiceSecondsRef = useRef(0);

  const roomsQuery = useQuery({
    queryKey: ['rooms'],
    queryFn: fetchRooms,
    refetchInterval: 60_000,
  });

  const statusQuery = useQuery({
    queryKey: ['status'],
    queryFn: fetchStatus,
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
  });

  const maintenanceQuery = useQuery({
    queryKey: ['maintenance'],
    queryFn: fetchMaintenance,
    refetchInterval: 30_000,
    retry: false,
  });

  const room = roomsQuery.data?.rooms.find((item) => item.id === activeRoomId || item.code === activeRoomId) || null;
  const currentRound = statusQuery.data?.current || null;
  const roundNumber = Number(currentRound?.round_number || 0);

  const distributionQuery = useQuery({
    queryKey: ['room-distribution', room?.code, roundNumber, token],
    queryFn: () => fetchRoomDistribution(room?.code || '', roundNumber, token),
    enabled: !!room?.code && !!roundNumber,
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const [betOpen, setBetOpen] = useState(false);
  // Set when the bet sheet is opened from a read-the-table tile: the sheet
  // lands on that market's tab and points at the pick. Cleared on close so a
  // later plain "Bet" tap never re-highlights a stale cell.
  const [betTarget, setBetTarget] = useState<BetTarget | null>(null);
  // The market armed from a Distribution tile, shown on the panel's bet bar so
  // reading the table and acting on it are one motion. Cleared when the panel
  // closes so a stale pick can never survive into the next visit.
  const [tablePick, setTablePick] = useState<BetTarget | null>(null);
  // Unified "read the table" panel — 势 Tendência / 盘 Distribuição / 人 Jogadores
  // in one sheet with a persistent bet bar (replaces the separate analytics +
  // room-bets sheets and the inline heat rail).
  const [tableOpen, setTableOpen] = useState(false);
  const [tableTab, setTableTab] = useState<'trend' | 'dist' | 'ppl'>('dist');
  const [myBetsOpen, setMyBetsOpen] = useState(false);
  const [basketOpen, setBasketOpen] = useState(false);
  const [guestAuthOpen, setGuestAuthOpen] = useState(false);
  // A copy handed over from the board / a profile / the follows list. Consumed
  // once, on arrival, so discovery lands on the copy sheet instead of dropping
  // the player into a room with no idea what they came for.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedDraw, setSelectedDraw] = useState<ArchiveRound | null>(null);
  const [danmakuMode, setDanmakuMode] = useState<DanmakuMode>(() => initialDanmakuMode());
  const [danmakuItems, setDanmakuItems] = useState<DanmakuItem[]>([]);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);

  // Every sheet on this page is a full screen of UI that used to live only in
  // useState — so the system back button skipped past all of them and left the
  // room instead. Listed bottom-to-top: 'bet' can have 'basket' over it, so back
  // closes the basket first. The plus menu and the history drawer hand off to
  // another sheet inside a single tap; useHistoryLayers reads that as a swap and
  // rewrites the entry rather than stacking a second one.
  useHistoryLayers([
    { id: 'room.switch', open: switchOpen, onClose: () => setSwitchOpen(false) },
    { id: 'room.plus', open: plusOpen, onClose: () => setPlusOpen(false) },
    { id: 'room.table', open: tableOpen, onClose: () => setTableOpen(false) },
    { id: 'room.history', open: historyOpen, onClose: () => setHistoryOpen(false) },
    { id: 'room.draw', open: !!selectedDraw, onClose: () => setSelectedDraw(null) },
    { id: 'room.mybets', open: myBetsOpen, onClose: () => setMyBetsOpen(false) },
    { id: 'room.guestauth', open: guestAuthOpen, onClose: () => setGuestAuthOpen(false) },
    { id: 'room.bet', open: betOpen, onClose: () => { setBetOpen(false); setBetTarget(null); } },
    { id: 'room.basket', open: basketOpen, onClose: () => setBasketOpen(false) },
  ]);
  // The round the UI is "pinned" to while its 3 ETH anchors are drawn and the
  // result reveals. Betting freezes while pinned (faithful to the old web
  // WAITING pin). A hard 55s safety release below guarantees it can never
  // strand betting if the draw stalls or the round voids.
  const [drawingRound, setDrawingRound] = useState<{ round: number; endsAt: number } | null>(null);
  // A round the user bet on was voided (stake auto-refunded) — tell them why.
  const [voidNotice, setVoidNotice] = useState<{ round: number; reason: string; amount: number; currency: string } | null>(null);
  const shownVoids = useRef<Set<number>>(new Set());
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [reveal, setReveal] = useState<RoomRevealState | null>(null);
  // One overlay per round — see roundOverlayMachine.ts. The ref is the source
  // of truth so several events in one tick reduce in order; the state mirrors
  // it for rendering.
  const overlayRef = useRef<OverlayState>(initialOverlayState());
  const [overlay, setOverlay] = useState<OverlayState>(overlayRef.current);
  // The public draw of the round the overlay is about (from latest_settled), for the settling card and the no-bet banner.
  const [overlayDraw, setOverlayDraw] = useState<{ round: number; draw: CardDraw } | null>(null);
  // settle-my responses by round, for the settled card and the banner's "View".
  const [settledSummaries, setSettledSummaries] = useState<Record<number, RoundSummaryResponse>>({});
  const [banner, setBanner] = useState<BannerView | null>(null);
  // A settled card reopened from a banner: no auto-close.
  const [pinnedRound, setPinnedRound] = useState<number | null>(null);
  const [chatDraft, setChatDraft] = useState('');
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceSeconds, setVoiceSeconds] = useState(0);
  const [voiceSending, setVoiceSending] = useState(false);
  const [fabSide, setFabSide] = useState<'left' | 'right'>(() => (readStorage(typeof window === 'undefined' ? undefined : window.localStorage, FAB_SIDE_KEY) === 'left' ? 'left' : 'right'));
  const [fabTop, setFabTop] = useState<number | null>(() => {
    const stored = readStorage(typeof window === 'undefined' ? undefined : window.localStorage, FAB_TOP_KEY);
    return stored ? Number(stored) : null;
  });
  const [fabDrag, setFabDrag] = useState<{ left: number; top: number } | null>(null);
  const fabStart = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const fabPos = useRef({ left: 0, top: 0 });
  const fabMoved = useRef(false);
  const fabDragging = useRef(false);
  // Floating widget stack (countdown/pending/last-5) — draggable vertically like legacy
  const [widgetTop, setWidgetTop] = useState<number>(() => {
    const stored = Number(readStorage(typeof window === 'undefined' ? undefined : window.localStorage, WIDGET_TOP_KEY) || 0);
    return stored >= ROOM_WIDGET_MIN_TOP ? stored : ROOM_WIDGET_MIN_TOP;
  });
  const [widgetDragging, setWidgetDragging] = useState(false);
  const [widgetHintPulse, setWidgetHintPulse] = useState(false);
  const widgetStart = useRef({ y: 0, top: 0 });
  const widgetMoved = useRef(false);
  const widgetDragActive = useRef(false);

  // One-time "arraste" nudge per session (legacy fd:dragHinted)
  useEffect(() => {
    const storage = typeof window === 'undefined' ? undefined : window.sessionStorage;
    if (readStorage(storage, 'fd:dragHinted') === '1') return;
    writeStorage(storage, 'fd:dragHinted', '1');
    setWidgetHintPulse(true);
    const timer = setTimeout(() => setWidgetHintPulse(false), 1700);
    return () => clearTimeout(timer);
  }, []);

  const roomBetsDistributionQuery = useQuery({
    queryKey: ['room-distribution-bets', room?.code, roundNumber, token],
    queryFn: () => fetchRoomDistribution(room?.code || '', roundNumber, token, { includeBets: true }),
    enabled: tableOpen && !!room?.code && !!roundNumber,
    staleTime: 3_000,
    retry: false,
  });

  // Previous round's distribution — lets the table show "vs last round" so a
  // player can judge whether this round's crowd signal is unusually busy/quiet.
  const prevDistributionQuery = useQuery({
    queryKey: ['room-distribution-prev', room?.code, roundNumber, token],
    queryFn: () => fetchRoomDistribution(room?.code || '', roundNumber - 1, token),
    enabled: tableOpen && !!room?.code && roundNumber > 1,
    staleTime: 60_000,
    retry: false,
  });

  const myBetsQuery = useQuery({
    queryKey: ['my-bets', room?.code, token],
    queryFn: () => fetchMyBets(token, { roomCode: room?.code, limit: 100 }),
    enabled: !!token && !guestMode && !!room?.code,
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const chatQuery = useQuery({
    queryKey: ['room-chat', room?.code],
    queryFn: async () => {
      const code = room?.code || '';
      const snapshot = await fetchChatMessages(code);
      // A snapshot started before a send/publication must not erase that message.
      return mergeRoomChat(code, queryClient.getQueryData<ChatMessagesResponse>(['room-chat', code]), snapshot.messages || []);
    },
    enabled: !!room?.code,
    refetchInterval: 8_000,
    refetchIntervalInBackground: true,
    retry: false,
  });

  useEffect(() => {
    const code = room?.code;
    if (!code) return;
    const endpoint = import.meta.env.VITE_CHAT_WS_URL || 'wss://chat.vgmable.baby/connection/websocket';
    const client = new Centrifuge(endpoint);
    const subscription = client.newSubscription(`room:${code}`);
    subscription.on('publication', ({ data }) => {
      queryClient.setQueryData<ChatMessagesResponse>(['room-chat', code], (previous) => mergeRoomChat(code, previous, [data]));
    });
    // Fetch messages missed while disconnected, including on first subscribe.
    subscription.on('subscribed', () => {
      void queryClient.invalidateQueries({ queryKey: ['room-chat', code] });
    });
    subscription.subscribe();
    client.connect();
    return () => {
      subscription.unsubscribe();
      client.disconnect();
    };
  }, [room?.code, queryClient]);

  const analyticsQuery = useQuery({
    queryKey: ['room-analytics-rounds'],
    queryFn: fetchAnalyticsRounds,
    enabled: tableOpen,
    staleTime: 45_000,
  });

  const historyQuery = useQuery({
    queryKey: ['room-history-rounds'],
    queryFn: fetchRoomHistoryRounds,
    // Always loaded in-room: feeds both the drawer and the LAST-5 widget.
    //
    // Deliberately NOT polled. This fetches two whole days of draws — 2 × 288
    // rounds, ~98 kB gzip, and the API sends `no-store` so nothing is cached —
    // and the widget it feeds shows five of them. On a 20s interval that was
    // ~294 kB gzip a minute, continuing while the tab was hidden, with a
    // JSON.parse of ~400 kB landing on the main thread three times a minute.
    //
    // The refresh is event-driven and always was: the reveal effect calls
    // refetch() the instant statusQuery (3s) reports a new latest_settled, so
    // Recent draws updates on the draw rather than on a timer. That refetch is
    // explicit, so it ignores staleTime; what staleTime covers is re-entering
    // the room, where the cached list is good enough for a few seconds.
    staleTime: 15_000,
  });
  const lastFive = useMemo(() => {
    const rounds = (historyQuery.data || []).slice().sort((a, b) => Number(b.cutoff_ts || b.ends_at || 0) - Number(a.cutoff_ts || a.ends_at || 0));
    return rounds.slice(0, 5);
  }, [historyQuery.data]);

  const sendChatMutation = useMutation({
    mutationFn: (text: string) => {
      if (!room?.code) throw new Error('INVALID_ROOM');
      return sendChatMessage(room.code, token, text);
    },
    onSuccess: (response) => {
      const code = response.message?.room_id;
      if (code && response.message) {
        queryClient.setQueryData<ChatMessagesResponse>(['room-chat', code], (previous) => mergeRoomChat(code, previous, [response.message!]));
      }
      setChatDraft('');
    },
    onError: (error) => showToast(chatErrorMessage(error, t), 'warn'),
  });

  useEffect(() => {
    if (!voiceRecording) return undefined;
    voiceSecondsRef.current = 0;
    setVoiceSeconds(0);
    const timer = window.setInterval(() => {
      voiceSecondsRef.current += 1;
      setVoiceSeconds(voiceSecondsRef.current);
      if (voiceSecondsRef.current >= 30) {
        stopVoice(true);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [voiceRecording]);

  useEffect(() => () => {
    cleanupVoiceStream();
  }, []);

  useEffect(() => {
    // replace, not push: this fires for '#/room' with no id (a truncated deep
    // link). Pushing '#/hall' meant back returned to '#/room', which re-ran this
    // and pushed again — a loop the back button could never escape.
    if (!activeRoomId) setRoute('lobby', { replace: true });
  }, [activeRoomId, setRoute]);

  // 1s heartbeat so every countdown (widget, banner, bet sheet) ticks per
  // second — without it they only moved when a 5s poll happened to re-render.
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setClockTick((value) => (value + 1) % 86_400), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!room?.code) return;
    const visitorId = readStorage(localStorage, GUEST_ID_KEY) || `g_${Math.random().toString(36).slice(2, 12)}`;
    writeStorage(localStorage, GUEST_ID_KEY, visitorId);
    // Presence = who's actually watching. Only heartbeat while the tab is
    // visible AND the user has interacted in the last few minutes — otherwise a
    // backgrounded or walked-away-from tab would keep pinging and inflate the
    // room's online count forever. When it stops, the 45s server TTL drops them.
    const IDLE_MS = 4 * 60 * 1000;
    const ACTIVITY = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    let lastActiveAt = Date.now();
    const markActive = () => { lastActiveAt = Date.now(); };
    const isPresent = () => document.visibilityState === 'visible' && Date.now() - lastActiveAt < IDLE_MS;
    const ping = () => { if (isPresent()) heartbeatRoom(room.code, token, visitorId).catch(() => undefined); };
    const onVisibility = () => { if (document.visibilityState === 'visible') { lastActiveAt = Date.now(); ping(); } };
    ping();
    const timer = window.setInterval(ping, 25_000);
    const opts: AddEventListenerOptions = { passive: true };
    ACTIVITY.forEach((evt) => window.addEventListener(evt, markActive, opts));
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      ACTIVITY.forEach((evt) => window.removeEventListener(evt, markActive));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [room?.code, token]);

  const poolBrl = Number(distributionQuery.data?.pools?.[fiatCurrency]?.amount || 0);
  const poolCap = Number(distributionQuery.data?.pools?.[fiatCurrency]?.cap || room?.poolCapBrl || 0);
  const recentBets = distributionQuery.data?.recentBets || distributionQuery.data?.recent_bets || [];
  // Heat discovery: curate the round's bets into 🔥 hot / 🐋 whale highlights so
  // a busy room surfaces "the few worth watching" instead of 50 equal rows.
  const socialConfig = useSocialConfig();
  const heat = useMemo(() => selectHeatHighlights(recentBets, socialConfig), [recentBets, socialConfig]);
  const myBets = useMemo(() => (myBetsQuery.data?.items || []).map(normalizeBet), [myBetsQuery.data]);
  const chatMessages = useMemo(
    () => (chatQuery.data?.messages || []).map((message) => normalizeChatMessage(message, user)),
    [chatQuery.data?.messages, user],
  );
  const currentRoundBets = myBets.filter((bet) => bet.round === roundNumber);
  const currentRoundStakeLines = currencyLines([
    { currency: fiatCurrency, amount: sumBets(currentRoundBets.filter((bet) => bet.currency === fiatCurrency), 'amount') },
    { currency: 'USDT', amount: sumBets(currentRoundBets.filter((bet) => bet.currency === 'USDT'), 'amount') },
  ]);
  const pendingBets = myBets.filter((bet) => bet.result === 'pending');
  const pendingBetSignature = pendingBets.map((bet) => `${bet.id}:${bet.round}`).join('|');
  // Round timing compares against server-issued epochs (ends_at), so it has to
  // be measured on the server's clock, not the handset's. The bet window itself
  // is enforced server-side — BetController rejects with ROUND_LOCKED off its
  // own clock — so a skewed device can never sneak a late bet through. What it
  // can do is lie to the player: a handset running fast closes the countdown
  // early and quietly takes betting time the player still had.
  //
  // /api/status carries server_time on every 3s poll and dataUpdatedAt is when
  // we received it, so their difference is the device skew net of latency.
  // Below the deadband we return 0 and behave exactly as before: at a couple of
  // seconds the handset is close enough that "correcting" it would only import
  // network jitter into the countdown.
  const serverSkewSec = useMemo(() => {
    const server = serverEpochSeconds(statusQuery.data?.server_time);
    if (!server || !statusQuery.dataUpdatedAt) return 0;
    const skew = server - Math.floor(statusQuery.dataUpdatedAt / 1000);
    if (Math.abs(skew) <= 3) return 0;
    // Beyond half a day it is likelier a malformed payload than a real clock,
    // and trusting it would break the countdown for everyone on that response.
    return Math.abs(skew) > 12 * 3600 ? 0 : skew;
  }, [statusQuery.data?.server_time, statusQuery.dataUpdatedAt]);
  const serverNowSec = () => Math.floor(Date.now() / 1000) + serverSkewSec;

  const secondsLeft = currentRound ? Math.max(0, Number(currentRound.ends_at || 0) - serverNowSec()) : 0;
  const lockBufferSec = Number(statusQuery.data?.config?.lock_buffer_sec ?? 5);
  const lockSeconds = Math.max(0, secondsLeft - lockBufferSec);
  // Dragon-chaser gate cap: hold the new round's betting until the previous
  // round's result is public (latest_settled catches up — see the reveal-clear
  // effect) or this many seconds elapse. Mirrors the backend PREV_ROUND_DRAWING
  // window so the two never disagree.
  const prevResultGraceSec = Number(statusQuery.data?.config?.prev_result_grace_sec ?? 50);
  // Betting is closed while a draw is pinned (the ~40s reveal window), even if
  // the server has already rotated `current` to the next open round.
  const roundAcceptsBets = !drawingRound && currentRound?.status === 'OPEN' && lockSeconds > 0;

  // Cosmetic 3-block reveal state, timed off the pinned round's ends_at (the
  // backend writes anchors atomically, so — like the old web engine — the dots
  // are a ~12s/block estimate; the real result still lands via latest_settled).
  const drawElapsed = drawingRound ? serverNowSec() - drawingRound.endsAt : 0;
  const waitingSub = !drawingRound
    ? null
    : drawElapsed < 0
      ? 'lockbuffer'
      : drawElapsed < 12
        ? 'b1'
        : drawElapsed < 24
          ? 'b2'
          : drawElapsed < 36
            ? 'b3'
            : 'computing';
  const waitingConfirmed = waitingSub === 'b2' ? 1 : waitingSub === 'b3' ? 2 : waitingSub === 'computing' ? 3 : 0;
  const waitingPill =
    waitingSub === 'lockbuffer'
      ? formatClock(Math.max(0, -drawElapsed))
      : waitingSub === 'computing'
        ? '···'
        : waitingSub
          ? `${waitingConfirmed + 1}/3`
          : '';
  // Unified countdown for the room widget + every bet panel (see roundPhaseDisplay).
  const phase = roundPhaseDisplay({ drawingRound: !!drawingRound, waitingSub, waitingConfirmed, waitingPill, lockSeconds, t });
  // Draw-service (ops-node) liveness: it pre-creates current+next rounds every
  // tick, so a healthy service always leaves at least a current or pending
  // round. If both are gone AND the last settle is stale, the writer has
  // stopped — surface a calm "recovering" hint rather than a frozen countdown.
  const drawStatus = statusQuery.data;
  const drawServiceStalled =
    !!drawStatus &&
    !drawStatus.current &&
    !(drawStatus.pending && drawStatus.pending.length > 0) &&
    (!drawStatus.latest_settled ||
      Number(drawStatus.server_time?.unix || 0) - Number(drawStatus.latest_settled.ends_at || 0) > 60);
  const gameMaintenanceBlocked = maintenanceBlocksGame(maintenanceQuery.data?.level);

  function pushDanmakuItem(text: string, kind: DanmakuItem['kind']) {
    if (danmakuMode === 'off' || !text) return;
    const item: DanmakuItem = {
      id: `${kind}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
      text,
      row: pickDanmakuRow(danmakuRecentRows.current),
      kind,
    };
    setDanmakuItems((items) => [...items.slice(-10), item]);
    window.setTimeout(() => setDanmakuItems((items) => items.filter((current) => current.id !== item.id)), 9300);
  }

  function roundLabelFor(round: number): string {
    // Rounds are one per 300 s, so any known settled round anchors the daily
    // #001-288 seq of any other round. Anchor on starts_at (inside the window).
    const anchor = statusQuery.data?.latest_settled;
    const anchorRound = Number(anchor?.round_number || 0);
    const anchorStart = Number(((anchor ?? {}) as { starts_at?: number; cutoff_ts?: number }).starts_at
      || ((anchor ?? {}) as { cutoff_ts?: number }).cutoff_ts
      || (Number(anchor?.ends_at || 0) ? Number(anchor?.ends_at) - 300 : 0));
    if (!anchorRound || !anchorStart || !round) return round ? `#${round}` : '---';
    const seq = dailySeq({ starts_at: anchorStart - (anchorRound - round) * 300 });
    return seq !== '' ? String(Number(seq)) : `#${round}`;
  }

  function bannerFor(item: OverlayBanner): BannerView {
    const label = roundLabelFor(item.round);
    const amount = (nets: Record<string, number>) => Object.entries(nets)
      .sort(([a], [b]) => (a === fiatCurrency ? -1 : b === fiatCurrency ? 1 : a.localeCompare(b)))
      .map(([currency, net]) => signedNet(net, (abs) => fmtCurrency(abs, currency)))
      .join(' · ');
    const id = `${item.kind}:${item.round}:${Date.now()}`;
    if (item.kind === 'result') {
      const hero = room?.game && overlayDraw?.round === item.round ? heroDrawFor(room.game, overlayDraw.draw) : null;
      const value = hero ? drawValueText(hero, t) : '';
      return { id, round: item.round, kind: 'result', tone: 'neutral', canOpen: false, ms: 4000, text: t('bannerRoundResult').replace('%round%', label).replace('%value%', value) };
    }
    if (item.kind === 'settling') {
      return { id, round: item.round, kind: 'settling', tone: 'neutral', canOpen: false, ms: 8000, text: t('bannerSettling').replace('%round%', label) };
    }
    const values = Object.values(item.netByCurrency);
    const positive = values.some((net) => net > 0) && !values.some((net) => net < 0);
    const key = item.kind === 'credited' ? 'bannerCredited' : 'bannerCorrected';
    return { id, round: item.round, kind: item.kind, tone: positive ? 'gold' : 'neutral', canOpen: true, ms: positive || item.kind === 'corrected' ? 8000 : 4000, text: t(key).replace('%round%', label).replace('%amount%', amount(item.netByCurrency)) };
  }
  // The tick interval closes over one render; read the latest bannerFor through a ref.
  const bannerForRef = useRef(bannerFor);
  bannerForRef.current = bannerFor;

  function dispatchOverlay(event: OverlayEvent) {
    const { state, banner: emitted } = reduceOverlay(overlayRef.current, event);
    overlayRef.current = state;
    setOverlay(state);
    if (emitted) setBanner(bannerForRef.current(emitted));
  }

  function dismissReveal() {
    dispatchOverlay({ type: 'dismiss', now: Date.now() });
  }

  function showReveal(round: number, game: GameCode, draw: RoomRevealDraw) {
    const slotEnd = game === 'lastdigit' && draw.lastdigit != null
      ? -((5 * 10 + Number(draw.lastdigit)) * 96)
      : 0;
    // The painted wheel is 40 decorative 9° segments, not the real 37-pocket
    // order — aim the fixed top pointer at a segment matching the pocket color.
    const wheelEnd = game === 'roulette' && draw.roulette != null
      ? (() => {
          const tone = rouletteColor(Number(draw.roulette));
          const mid = tone === 'green' ? 85.5 : tone === 'red' ? 13.5 : 22.5;
          return (4 * 360) + (360 - mid);
        })()
      : 0;
    setReveal({ round, game, draw, slotEnd, wheelEnd, exiting: false });
  }

  useEffect(() => {
    revealInitialized.current = false;
    revealedRound.current = 0;
    setReveal(null);
    setDrawingRound(null);
    setVoidNotice(null);
    dispatchOverlay({ type: 'reset' });
    setOverlayDraw(null);
    setSettledSummaries({});
    setBanner(null);
    setPinnedRound(null);
  }, [room?.code, room?.game]);

  // Drive the machine's clock while an overlay is open.
  useEffect(() => {
    if (overlay.stage === 'idle') return undefined;
    const timer = window.setInterval(() => dispatchOverlay({ type: 'tick', now: Date.now() }), 250);
    return () => window.clearInterval(timer);
  }, [overlay.stage]);

  // The reveal visuals live only during the REVEAL stage.
  useEffect(() => {
    if (overlay.stage !== 'reveal') setReveal(null);
  }, [overlay.stage]);

  // Pin the round the moment its betting window closes, so the UI holds on it
  // through the draw + reveal instead of jumping straight to the next round.
  useEffect(() => {
    if (!currentRound || currentRound.status !== 'OPEN') return;
    if (lockSeconds > 0 || secondsLeft <= 0) return; // only during the lock buffer
    const rn = Number(currentRound.round_number || 0);
    const endsAt = Number(currentRound.ends_at || 0);
    if (!rn || !endsAt) return;
    setDrawingRound((prev) => (prev && prev.round >= rn ? prev : { round: rn, endsAt }));
  }, [currentRound, lockSeconds, secondsLeft]);

  async function refreshSettlementWallet() {
    if (!token || guestMode) return;
    await queryClient.cancelQueries({ queryKey: ['me', token] });
    const result = await queryClient.fetchQuery({
      queryKey: ['me', token], queryFn: () => fetchMe(token), staleTime: 0,
    });
    if (useAuthStore.getState().token === token) setUser(result.user);
  }

  // Safety-cap release: normally the pin drops the instant the previous round's
  // result lands (latest_settled catches up — the reveal-clear effect below).
  // This is the hard cap for a slow/stalled/VOID draw so betting always resumes,
  // matching the backend PREV_ROUND_DRAWING grace window.
  useEffect(() => {
    if (!drawingRound) return;
    if (serverNowSec() - drawingRound.endsAt >= prevResultGraceSec && graceWalletRefresh.current !== drawingRound.round) {
      const round = drawingRound.round;
      graceWalletRefresh.current = round;
      void refreshSettlementWallet().catch(() => undefined).finally(() => {
        setDrawingRound((current) => current?.round === round ? null : current);
      });
    }
  }, [drawingRound, clockTick, prevResultGraceSec]);

  // A round the user bet on was voided → tell them (stake already auto-refunded
  // by the backend). Fire once per round; a bet on a voided round appears in
  // recent_void, and the refund engine has cancelled the bet + credited it back.
  useEffect(() => {
    if (!token || guestMode) return;
    const voids = statusQuery.data?.recent_void || [];
    if (!voids.length || myBets.length === 0) return;
    for (const round of voids) {
      const rn = Number(round.round_number || 0);
      if (!rn || shownVoids.current.has(rn)) continue;
      const mine = myBets.filter((bet) => Number(bet.round) === rn);
      if (mine.length === 0) continue;
      shownVoids.current.add(rn);
      const amount = mine.reduce((sum, bet) => sum + Number(bet.amount || 0), 0);
      setVoidNotice({
        round: dailySeq(round) !== '' ? Number(dailySeq(round)) : rn,
        reason: round.void_reason || '',
        amount,
        currency: mine[0].currency || 'BRL',
      });
      // Balance was credited by the refund; pull the fresh number in.
      queryClient.invalidateQueries({ queryKey: ['my-bets'] });
      break;
    }
  }, [statusQuery.data?.recent_void, myBets, token, guestMode, queryClient]);

  // `target` is the read-the-table deep link. It runs through the same guest /
  // maintenance gates as every other way into the sheet — a Missing tile is a
  // shortcut through the navigation, not around the rules.
  function openBetSheet(target?: BetTarget | null) {
    if (guestMode) {
      setGuestAuthOpen(true);
      return;
    }
    if (gameMaintenanceBlocked) {
      showToast(t('maintenanceGame'), 'warn');
      return;
    }
    setBetTarget(room && findBetOption(room.game, target) ? target || null : null);
    setBetOpen(true);
  }

  function openTable(tab: 'trend' | 'dist' | 'ppl' = 'dist') {
    setTableTab(tab);
    setTablePick(null);
    setTableOpen(true);
  }

  /**
   * Peek at whoever placed a bet in this room.
   *
   * The public profile lives on the leaderboard page, so this leaves the room
   * to show it — and closing it used to drop the player on Top Players, a page
   * they never asked for and had no way back from. It now records where they
   * came from, including the room id: routing away from 'room' nulls
   * activeRoomId, so the return trip has to carry it.
   */
  function openProfileFromRoom(profileId: string) {
    setProfileReturn({ view: null, resultId: null, origin: { route: 'room', roomId: room?.code || activeRoomId } });
    setActiveProfileUserId(profileId);
    setRoute('leaderboard');
  }

  useEffect(() => {
    const storage = typeof window === 'undefined' ? undefined : window.sessionStorage;
    if (readStorage(storage, ROOM_ENTRY_INTENT_KEY) !== 'trend') return;
    writeStorage(storage, ROOM_ENTRY_INTENT_KEY, '');
    openTable('trend');
  }, []);

  function onFabPointerDown(event: PointerEvent<HTMLButtonElement>) {
    const button = event.currentTarget;
    const parent = button.offsetParent as HTMLElement | null;
    if (!parent) return;
    try { button.setPointerCapture(event.pointerId); } catch { /* non-capturable pointer */ }
    const rect = button.getBoundingClientRect();
    const prect = parent.getBoundingClientRect();
    fabStart.current = { x: event.clientX, y: event.clientY, left: rect.left - prect.left, top: rect.top - prect.top };
    fabMoved.current = false;
    fabDragging.current = true;
    fabPos.current = { left: fabStart.current.left, top: fabStart.current.top };
    setFabDrag({ left: fabStart.current.left, top: fabStart.current.top });
  }

  function onFabPointerMove(event: PointerEvent<HTMLButtonElement>) {
    if (!fabDragging.current) return;
    const dx = event.clientX - fabStart.current.x;
    const dy = event.clientY - fabStart.current.y;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) fabMoved.current = true;
    fabPos.current = { left: fabStart.current.left + dx, top: fabStart.current.top + dy };
    setFabDrag({ ...fabPos.current });
  }

  function onFabPointerUp(event: PointerEvent<HTMLButtonElement>) {
    if (!fabDragging.current) return;
    fabDragging.current = false;
    const parent = event.currentTarget.offsetParent as HTMLElement | null;
    const drag = fabPos.current;
    setFabDrag(null);
    if (!fabMoved.current || !parent) {
      openBetSheet();
      return;
    }
    const prect = parent.getBoundingClientRect();
    const side: 'left' | 'right' = drag.left + FAB_SIZE / 2 < prect.width / 2 ? 'left' : 'right';
    const top = Math.max(FAB_MARGIN, Math.min(prect.height - FAB_SIZE - FAB_MARGIN, drag.top));
    setFabSide(side);
    setFabTop(top);
    const storage = typeof window === 'undefined' ? undefined : window.localStorage;
    writeStorage(storage, FAB_SIDE_KEY, side);
    writeStorage(storage, FAB_TOP_KEY, String(Math.round(top)));
  }

  // Widget stack drag: vertical only, 5px tap-vs-drag threshold, clamped like legacy.
  // IMPORTANT: capture the pointer only once the threshold is crossed — capturing on
  // pointerdown retargets the eventual click to the container and kills button taps.
  function onWidgetPointerDown(event: PointerEvent<HTMLDivElement>) {
    widgetStart.current = { y: event.clientY, top: widgetTop };
    widgetMoved.current = false;
    widgetDragActive.current = true;
    setWidgetDragging(true);
  }

  function onWidgetPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (!widgetDragActive.current) return;
    const dy = event.clientY - widgetStart.current.y;
    if (!widgetMoved.current && Math.abs(dy) > 5) {
      widgetMoved.current = true;
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* non-capturable pointer */ }
    }
    if (widgetMoved.current) {
      setWidgetTop(Math.max(ROOM_WIDGET_MIN_TOP, Math.min(window.innerHeight - 220, widgetStart.current.top + dy)));
    }
  }

  function onWidgetPointerUp() {
    if (!widgetDragActive.current) return;
    widgetDragActive.current = false;
    setWidgetDragging(false);
    if (widgetMoved.current) {
      setWidgetTop((top) => {
        writeStorage(typeof window === 'undefined' ? undefined : window.localStorage, WIDGET_TOP_KEY, String(Math.round(top)));
        return top;
      });
    }
  }

  function onWidgetClickCapture(event: ReactMouseEvent<HTMLDivElement>) {
    if (!widgetMoved.current) return;
    widgetMoved.current = false;
    event.preventDefault();
    event.stopPropagation();
  }

  const fabStyle: CSSProperties = fabDrag
    ? { left: fabDrag.left, top: fabDrag.top, right: 'auto', bottom: 'auto' }
    : {
        ...(fabTop != null ? { top: fabTop, bottom: 'auto' } : {}),
        ...(fabSide === 'left' ? { left: FAB_MARGIN, right: 'auto' } : { right: FAB_MARGIN, left: 'auto' }),
      };

  useEffect(() => {
    if (!gameMaintenanceBlocked || !betOpen) return;
    setBetOpen(false);
    showToast(t('maintenanceGame'), 'warn');
  }, [betOpen, gameMaintenanceBlocked, showToast, t]);

  useEffect(() => {
    if (danmakuMode === 'off' || recentBets.length === 0) return;
    for (const bet of [...recentBets].reverse()) {
      const key = String(bet.id || bet.betId || `${bet.ts || bet.placed_at}:${bet.user?.username}:${bet.pick}:${bet.amount}`);
      if (danmakuSeen.current.has(key)) continue;
      danmakuSeen.current.add(key);
      if (danmakuSeen.current.size > 200) danmakuSeen.current = new Set(Array.from(danmakuSeen.current).slice(-80));
      const name = bettorName(bet.user?.name || bet.user?.username, t);
      const pick = pickLabel(bet.game || room?.game || 'lastdigit', bet.tab, bet.rawPick ?? bet.pick, t);
      const text = formatTicker(t('tickerBet'), { name, amount: fmtMoney(Number(bet.amount || 0)), pick }).replace('💰', bet.mine ? '💸' : '💰');
      pushDanmakuItem(text, bet.mine ? 'mine' : 'bet');
    }
  }, [danmakuMode, recentBets, room?.game, t]);

  // Legacy scrolls chat to the newest message on load and on every merge.
  useEffect(() => {
    const node = chatScrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [chatMessages.length, chatMessages[chatMessages.length - 1]?.id]);

  useEffect(() => {
    if (!room?.game || !statusQuery.data?.latest_settled) return;
    const settled = statusQuery.data.latest_settled;
    const round = Number(settled.round_number || 0);
    if (!round) return;
    if (!revealInitialized.current) {
      revealInitialized.current = true;
      revealedRound.current = round;
      return;
    }
    if (round <= revealedRound.current) return;
    revealedRound.current = round;
    const draw = revealDrawForGame(settled, room.game);
    // Display the daily seq (#207) like everywhere else; the dedup guard above
    // still runs on the global round number.
    if (draw) showReveal(dailySeq(settled) !== '' ? Number(dailySeq(settled)) : round, room.game, draw);
    const publicDraw = summaryDrawFromRound(settled);
    if (publicDraw) setOverlayDraw({ round, draw: publicDraw });
    dispatchOverlay({ type: 'result', round, hasBets: myBets.some((bet) => bet.round === round), now: Date.now() });
    // The pinned round (or an even later one) has settled — release the pin so
    // the widget flips back to the live round once the reveal overlay clears.
    setDrawingRound((prev) => (prev && round >= prev.round ? null : prev));
    // statusQuery (3s) just saw a fresh result — pull the archive now so Recent
    // draws + the LAST-N widget show it immediately instead of waiting for their
    // own slow poll (was a 60s lag: the result was live but the drawer stayed
    // one round behind).
    historyQuery.refetch();
  }, [room?.game, statusQuery.data?.latest_settled]);

  useEffect(() => {
    if (danmakuMode === 'off' || !room?.game || !statusQuery.data?.latest_settled || recentBets.length === 0) return;
    const settled = statusQuery.data.latest_settled;
    const round = Number(settled.round_number || 0);
    if (!round) return;
    const distributionRound = Number(distributionQuery.data?.roundNumber || distributionQuery.data?.round_number || roundNumber || 0);
    if (distributionRound !== round) return;
    const key = `${room.code || room.id || 'room'}:${round}:${room.game}`;
    if (danmakuWinnerSeen.current.has(key)) return;
    const draw = danmakuDrawFromRound(settled);
    if (!draw) return;
    const winners = recentBets
      .map((bet) => {
        const result = evaluateDanmakuBet(room.game, bet.tab, bet.rawPick ?? bet.pick, draw);
        return { bet, result };
      })
      .filter((item) => item.result.won)
      .sort((a, b) => Number(b.bet.amount || 0) - Number(a.bet.amount || 0))
      .slice(0, 5);
    if (winners.length === 0) return;
    danmakuWinnerSeen.current.add(key);
    if (danmakuWinnerSeen.current.size > 120) danmakuWinnerSeen.current = new Set(Array.from(danmakuWinnerSeen.current).slice(-60));
    for (const { bet, result } of winners) {
      const name = bet.mine ? t('chipYou') : bettorName(bet.user?.name || bet.user?.username, t);
      const amount = fmtCurrency(Number(bet.amount || 0) * result.odds, bet.currency || 'BRL');
      const pick = pickLabel(room.game, bet.tab, bet.rawPick ?? bet.pick, t);
      pushDanmakuItem(formatTicker(t('tickerWin'), { name, amount, pick }), 'win');
    }
  }, [danmakuMode, distributionQuery.data?.roundNumber, distributionQuery.data?.round_number, recentBets, room?.code, room?.game, room?.id, roundNumber, statusQuery.data?.latest_settled, t]);

  useEffect(() => {
    if (!token || guestMode || !statusQuery.data?.latest_settled || pendingBets.length === 0) return;
    const latestSettledRound = Number(statusQuery.data.latest_settled.round_number || 0);
    const gate = settlementRetry.current;
    const roundsToSettle = Array.from(new Set(pendingBets
      .filter((bet) => bet.round > 0 && bet.round <= latestSettledRound)
      .map((bet) => bet.round)))
      .filter((round) => gate.start(round, Date.now()));
    for (const round of roundsToSettle) {
      setWalletRefreshCount((count) => count + 1);
      settleMyRound(token, round)
        .then(async (summary) => {
          // Paint the authoritative credited balance before showing the settlement card.
          await refreshSettlementWallet();
          if (useAuthStore.getState().token !== token) return;
          if (summary.settlementStatus === 'settled' && summary.bets?.length) {
            setSettledSummaries((current) => ({ ...current, [round]: summary }));
            dispatchOverlay({ type: 'settlement', settlement: { round, netByCurrency: netByCurrency(summary.bets.map(normalizeBet)) }, now: Date.now() });
          }
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['my-bets'] }),
            queryClient.invalidateQueries({ queryKey: ['bet-history'] }),
            queryClient.invalidateQueries({ queryKey: ['room-distribution'] }),
            queryClient.invalidateQueries({ queryKey: ['wallet'] }),
          ]);
        })
        .catch(() => undefined)
        .finally(() => {
          gate.finish(round, Date.now());
          setWalletRefreshCount((count) => count - 1);
        });
    }
  }, [clockTick, guestMode, pendingBetSignature, pendingBets, queryClient, statusQuery.data?.latest_settled, token]);

  function leaveRoom() {
    // Order matters. `setActiveRoomId(null)` while the route is still 'room'
    // re-syncs the URL for the CURRENT route, and buildHash('room', null) is
    // '#/hall' — so doing it first replaced the room's own history entry with
    // '#/hall' and then setRoute pushed a second '#/hall' on top. Two identical
    // adjacent entries: the first system-back press appeared to do nothing, and
    // the room entry it overwrote was gone for good. Leaving first, then
    // clearing the id (which no longer touches the URL, the route is 'lobby'
    // by then) leaves the room entry intact behind a single '#/hall'. setRoute
    // clears activeRoomId on its own for any route that is not 'room', so the
    // separate call is not just misordered, it is redundant.
    setRoute('lobby');
  }

  function submitChat(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = chatDraft.trim();
    if (!text || guestMode || sendChatMutation.isPending) return;
    sendChatMutation.mutate(text);
  }

  async function startVoice() {
    if (guestMode || voiceRecording || voiceSending) return;
    if (!(navigator.mediaDevices?.getUserMedia) || !window.MediaRecorder) {
      showToast(t('voiceUnsupported'), 'info');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = preferredVoiceMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      voiceChunksRef.current = [];
      voiceMimeTypeRef.current = recorder.mimeType || mimeType || 'audio/webm';
      recorder.ondataavailable = (event) => {
        if (event.data?.size) voiceChunksRef.current.push(event.data);
      };
      recorder.start();
      voiceStreamRef.current = stream;
      voiceRecorderRef.current = recorder;
      setVoiceRecording(true);
    } catch {
      cleanupVoiceStream();
      setVoiceRecording(false);
      showToast(t('voicePermissionRequired'), 'warn');
    }
  }

  async function stopVoice(send: boolean) {
    const recorder = voiceRecorderRef.current;
    const duration = Math.max(0, voiceSecondsRef.current || voiceSeconds);
    setVoiceRecording(false);
    setVoiceSeconds(0);
    voiceSecondsRef.current = 0;
    if (!recorder) {
      cleanupVoiceStream();
      return;
    }
    const chunks = await new Promise<Blob[]>((resolve) => {
      recorder.onstop = () => resolve([...voiceChunksRef.current]);
      try {
        if (recorder.state !== 'inactive') recorder.stop();
        else resolve([...voiceChunksRef.current]);
      } catch {
        resolve([...voiceChunksRef.current]);
      }
    });
    cleanupVoiceStream();
    if (!send) return;
    if (duration < 1 || chunks.length === 0) {
      showToast(t('voiceTooShort'), 'warn');
      return;
    }
    if (!room?.code) return;
    const blob = new Blob(chunks, { type: voiceMimeTypeRef.current || 'audio/webm' });
    setVoiceSending(true);
    try {
      const response = await sendVoiceMessage(room.code, token, blob, Math.min(duration, 30));
      if (response.message) {
        const code = response.message.room_id || room.code;
        queryClient.setQueryData<ChatMessagesResponse>(['room-chat', code], (previous) => mergeRoomChat(code, previous, [response.message!]));
      }
      showToast(t('voiceSent'), 'success');
    } catch (error) {
      showToast(voiceErrorMessage(error, t), 'warn');
    } finally {
      setVoiceSending(false);
    }
  }

  function cleanupVoiceStream() {
    try {
      voiceStreamRef.current?.getTracks().forEach((track) => track.stop());
    } catch {
      // Ignore cleanup errors from browsers that already closed the track.
    }
    voiceStreamRef.current = null;
    voiceRecorderRef.current = null;
    voiceChunksRef.current = [];
  }

  function toggleDanmakuMode() {
    setDanmakuMode((current) => {
      const next: DanmakuMode = current === 'on' ? 'min' : current === 'min' ? 'off' : 'on';
      persistDanmakuMode(next);
      if (next === 'off') setDanmakuItems([]);
      showToast(next === 'on' ? t('feedOn') : next === 'min' ? t('feedMin') : t('feedOff'), next === 'off' ? 'warn' : 'info');
      return next;
    });
  }

  if (!room) {
    return (
      <section className="room-screen">
        <button className="back-button" type="button" onClick={leaveRoom}>
          <ArrowLeft size={18} aria-hidden="true" />
          {t('backToLobby')}
        </button>
        <div className="state-panel">
          <RefreshCw size={20} aria-hidden="true" />
          <div>
            <strong>{t('loadingRoom')}</strong>
            <p>{t('loadingRoomsBody')}</p>
          </div>
        </div>
      </section>
    );
  }

  const poolPct = poolCap ? Math.min(100, (poolBrl / poolCap) * 100) : 0;
  const poolBets = fmtInt(distributionQuery.data?.totalBets || distributionQuery.data?.total_bets || recentBets.length);
  const gameLetter = room.game === 'sicbo' ? 'S' : room.game === 'roulette' ? 'R' : '#';

  return (
    <section className="room-screen">
      <header className="bg-card border-b border-border1 px-3 pt-2 pb-2 flex items-center gap-2 shrink-0">
        <button className="text-ink-2 inline-flex items-center gap-1" type="button" onClick={leaveRoom} aria-label={t('backToLobby')}>
          <ArrowLeft size={20} aria-hidden="true" />
          <span className="brand-dot" aria-hidden="true" />
        </button>
        <div className={`glogo glogo-game tier-${String(room.tier || 'bronze').toLowerCase()}`} style={{ width: 36, height: 36, borderRadius: 8 }}>
          <div className="w-full h-full" style={{ padding: '5px' }}><GameIcon game={room.game} /></div>
        </div>
        <button className="flex-1 min-w-0 text-left" type="button" onClick={() => { if ((roomsQuery.data?.rooms || []).some((other) => other.id !== room.id)) setSwitchOpen(true); }}>
          <div className="font-semibold text-sm text-ink-1 truncate flex items-center gap-1.5">
            <span className="truncate">{t(gameNameKey(room.game))} · {roomDisplayName(room, roomsQuery.data?.rooms || [], t)}</span>
            <ChevronDown size={13} className="text-brand-300 shrink-0" aria-hidden="true" />
          </div>
          <div className="text-[10px] text-ink-3 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />
              <span className="text-[9px] uppercase tracking-wider text-emerald-400">{t('liveShort')}</span>
            </span>
            <span data-online-count>·</span>
            <span data-online-count>{fmtInt(roomOnlineCount(room))}/{fmtInt(room.capacity)} {t('online')}</span>
          </div>
        </button>
        <button className={`relative text-${danmakuMode === 'off' ? 'ink-3' : danmakuMode === 'min' ? 'amber-400' : 'brand-300'}`} type="button" onClick={toggleDanmakuMode} aria-label={t('feedOn')} data-danmaku-toggle-react="true">
          <MessageCircle size={18} aria-hidden="true" />
          {danmakuMode === 'off' ? <span className="danmaku-slash" aria-hidden="true" /> : null}
          {danmakuMode === 'min' ? <span className="danmaku-min-badge" aria-hidden="true">−</span> : null}
        </button>
        <button className="text-ink-2" type="button" onClick={() => setRoute('rules')} aria-label={t('rulesHeader')}>
          <BookOpen size={18} aria-hidden="true" />
        </button>
      </header>

      <div className="room-status-stack">
        {banner ? (
          <RoundResultBanner
            banner={banner}
            onOpen={() => { setPinnedRound(banner.round); setBanner(null); }}
            onDismiss={() => setBanner(null)}
          />
        ) : null}
        <button className="room-round-strip" type="button" onClick={() => openTable('dist')} data-room-bets-trigger-react="true">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-ink-2">
                {/* "pote" used to sit here, orphaned from the amount it labels
                    over on the right — the strip read "Rodada 217/288 pote ·
                    0 apostas" with the money somewhere else entirely. */}
                {t('currentRound')} <span className="font-mono font-semibold text-ink-1">{dailySeq(currentRound) ? `${Number(dailySeq(currentRound))}/${ROUNDS_PER_DAY}` : `#${roundNumber || '---'}`}</span> · <span className="text-ink-3">{poolBets} {t('bets')}</span>
              </span>
              <span className="font-mono font-semibold text-gold-400"><span className="text-ink-3 font-sans font-normal">{t('roomHeaderPool')} </span>{fmtAmount(poolBrl)} / {fmtAmount(poolCap)}</span>
            </div>
            <div className={`pool-bar mt-1${poolPct > 85 ? ' near-full' : ''}`}><div className="pool-bar-fill" style={{ width: `${poolPct}%` }} /></div>
          </div>
          <ChevronRight size={14} className="text-ink-3 shrink-0" aria-hidden="true" />
        </button>
        <RoomActivityDrum
          token={token}
          guestMode={guestMode}
          userId={user?.id}
          onRaffle={(view, resultId) => {
            if (guestMode) {
              setGuestAuthOpen(true);
              return;
            }
            // Route into the dedicated Sorteios venue; a settled draw still
            // deep-links straight to its result and returns to Sorteios.
            if (view === 'result') {
              openWalletTicketsFlow('result', resultId, { route: 'sorteios' });
            } else {
              setRoute('sorteios');
            }
          }}
          onOpenRewards={() => setRoute('rewards')}
        />
      </div>

      {/* Floating widget stack — draggable vertically (legacy parity) so it never
          blocks what the user wants to watch; position persists per device */}
      <div
        className="room-float-stack"
        style={{ top: widgetTop }}
        onPointerDown={onWidgetPointerDown}
        onPointerMove={onWidgetPointerMove}
        onPointerUp={onWidgetPointerUp}
        onPointerCancel={onWidgetPointerUp}
        onClickCapture={onWidgetClickCapture}
        data-room-float-stack="true"
      >
      <div className={`room-drag-pill${widgetDragging ? ' dragging' : ''}${widgetHintPulse ? ' drag-hint-pulse' : ''}`} aria-hidden="true">
        <GripHorizontal size={9} />
        <span>{t('drag')}</span>
      </div>
      {drawServiceStalled ? (
        <div className="room-draw-stalled" role="status" data-draw-stalled-react="true">
          <span className="rds-dot" aria-hidden="true" />
          <span className="rds-text">{t('drawServiceStalled')}</span>
        </div>
      ) : (
        <button
          className={`room-phase-widget ${drawingRound ? 'waiting' : ''} ${phase.tone === 'urgent' || phase.tone === 'locked' ? 'urgent' : ''}`}
          type="button"
          onClick={() => setHistoryOpen(true)}
          data-draw-waiting-react={drawingRound ? 'true' : undefined}
        >
          <div className="text-left">
            <div className="rpw-kicker">{phase.label}</div>
            <div className="rpw-time" style={{ color: ROUND_TONE_COLOR[phase.tone] }}>{phase.value}</div>
          </div>
          <div className="rpw-sep" />
          {drawingRound ? (
            <div className="rpw-dots" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={`block-dot${
                    i < waitingConfirmed
                      ? ' confirmed'
                      : i === waitingConfirmed && waitingSub !== 'lockbuffer' && waitingSub !== 'computing'
                        ? ' pending'
                        : ''
                  }`}
                />
              ))}
            </div>
          ) : (
            <div className="rpw-round">#{dailySeq(currentRound) || roundNumber || '--'}</div>
          )}
        </button>
      )}

      {!roundAcceptsBets && currentRoundBets.some((bet) => bet.result === 'pending') ? (
        <button className="room-pending-bet-badge pending-bet-pulse" type="button" onClick={() => setMyBetsOpen(true)} data-pending-bet-badge-react="true">
          <span className="pix-spinner pending-spinner" aria-hidden="true" />
          <span>{t(gameNameKey(room.game))} · {t('pendingBetBadge').replace('%n%', String(dailySeq(currentRound) || roundNumber || '---'))}</span>
        </button>
      ) : null}

      {lastFive.length > 0 ? (
        <button className="room-last5-widget" type="button" onClick={() => openTable('trend')} data-last5-widget-react="true">
          <span className="last5-kicker">{t('last')}</span>
          <span className="last5-values">
            {lastFive.map((round) => {
              const games = round.games || {};
              if (room.game === 'sicbo') {
                const diceValues = (games.sicbo || []) as number[];
                const sum = diceValues.reduce((acc, v) => acc + Number(v || 0), 0);
                const triple = diceValues.length === 3 && diceValues.every((v) => Number(v) === Number(diceValues[0]));
                return <b key={round.round_number} className={triple ? 'text-violet-400' : sum >= 11 ? 'text-amber-400' : 'text-cyan-400'}>{sum || '-'}</b>;
              }
              if (room.game === 'roulette') {
                const value = Number(games.roulette ?? -1);
                const tone = value === 0 ? 'green' : [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36].includes(value) ? 'red' : 'black';
                return <b key={round.round_number} className={`roulette-pill-react pill-${tone}`}>{value >= 0 ? value : '-'}</b>;
              }
              const digit = Number(games.lastdigit ?? -1);
              return <b key={round.round_number} className={digit % 2 === 1 ? 'text-rose-400' : 'text-sky-400'}>{digit >= 0 ? digit : '-'}</b>;
            })}
          </span>
          <ChevronRight size={12} className="text-brand-300" aria-hidden="true" />
        </button>
      ) : null}
      </div>

      {danmakuMode !== 'off' ? <DanmakuLayer items={danmakuItems} minimized={danmakuMode === 'min'} /> : null}
      {reveal && overlay.stage === 'reveal' ? <RoomRevealOverlay reveal={reveal} compact={myBetsOpen} onDismiss={dismissReveal} /> : null}

      {voidNotice ? (
        <div
          className="void-notice-backdrop"
          role="dialog"
          aria-modal="true"
          data-void-notice-react="true"
          onClick={() => setVoidNotice(null)}
        >
          <div className="void-notice-card" onClick={(event) => event.stopPropagation()}>
            <div className="vn-icon" aria-hidden="true">↩</div>
            <div className="vn-title">{t('voidTitle')}</div>
            <div className="vn-round">#{voidNotice.round}</div>
            <p className="vn-body">
              {voidNotice.reason === 'clock_drift'
                ? t('voidReasonSafety')
                : voidNotice.reason === 'case_c_no_block' ||
                    voidNotice.reason === 'case_b_insufficient_blocks' ||
                    voidNotice.reason === 'rpc_quorum_failed'
                  ? t('voidReasonChain')
                  : t('voidReasonGeneric')}
            </p>
            <div className="vn-refund">
              <span>{t('voidRefunded')}</span>
              <b>{fmtCurrency(voidNotice.amount, voidNotice.currency)}</b>
            </div>
            <button className="vn-btn" type="button" onClick={() => setVoidNotice(null)}>
              {t('voidOk')}
            </button>
          </div>
        </div>
      ) : null}

      <div className={`room-chat-scroll no-scrollbar ${guestMode ? 'chat-blur' : ''}`} ref={chatScrollRef}>
        {chatMessages.map((message) => <ChatMessageRow key={message.id} message={message} />)}
        {chatQuery.isLoading ? <p className="text-center text-[12px] text-ink-3 py-4">{t('loading')}</p> : null}
        {!chatQuery.isLoading && chatMessages.length === 0 ? <p className="text-center text-[12px] text-ink-3 py-4">{t('chatEmpty')}</p> : null}
      </div>

      {voiceRecording ? (
        <div className="voice-recording-overlay-react" data-voice-recording-react="true">
          <Mic size={20} aria-hidden="true" />
          <div>
            <strong>{t('voiceRecording')}</strong>
            <span>{formatVoiceDuration(voiceSeconds)} · {t('voiceSlideCancel')}</span>
          </div>
          <button type="button" onClick={() => stopVoice(false)}>{t('cancel')}</button>
          <button type="button" onClick={() => stopVoice(true)}>{t('send')}</button>
        </div>
      ) : null}

      {!guestMode ? (
        <button
          className={`bet-fab btn-bet ${fabDrag ? 'bet-fab-dragging' : ''} ${!roundAcceptsBets ? 'is-locked' : ''}`}
          type="button"
          style={fabStyle}
          onPointerDown={onFabPointerDown}
          onPointerMove={onFabPointerMove}
          onPointerUp={onFabPointerUp}
          aria-label={t('betCta')}
         
        >
          <Sparkles size={22} aria-hidden="true" />
          <span className="bet-fab-label">{t('betCta')}</span>
        </button>
      ) : null}

      {guestMode ? (
        <div className="room-guest-strip">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-ink-3 block">{t('guest')}</span>
            <strong className="text-ink-1 text-sm">{t('guestReadOnly')}</strong>
          </div>
          <button className="btn-gold-primary px-4 py-2 rounded-xl text-sm" type="button" onClick={() => { clearGuest(); setRoute('auth'); }}>{t('login')}</button>
        </div>
      ) : (
        <form className="room-chat-input" onSubmit={submitChat}>
          <button type="button" onClick={() => setPlusOpen(true)} aria-label={t('sheetMyBets')} data-plus-menu-trigger-react="true">
            <Plus size={16} aria-hidden="true" />
          </button>
          <input
            disabled={sendChatMutation.isPending || voiceSending}
            maxLength={240}
            placeholder={t('chatPlaceholder')}
            value={chatDraft}
            onChange={(event) => setChatDraft(event.target.value)}
          />
          {!chatDraft.trim() ? (
            <button type="button" disabled={sendChatMutation.isPending || voiceSending} onPointerDown={startVoice} aria-label={t('voiceRecording')} data-voice-record-trigger-react="true">
              <Mic size={16} aria-hidden="true" />
            </button>
          ) : null}
          {chatDraft.trim() ? (
            <button type="submit" className="send" disabled={sendChatMutation.isPending} aria-label={t('send')}>
              <Send size={16} aria-hidden="true" />
            </button>
          ) : null}
        </form>
      )}

      {betOpen ? (
        <BetSheet
          key={`${user?.id ?? 'guest'}:${room.code}:${fiatCurrency}`}
          room={room}
          roundNumber={roundNumber}
          displayRound={dailySeq(currentRound)}
          roundAcceptsBets={roundAcceptsBets}
          phase={phase}
          currentRoundBets={currentRoundBets}
          previousSettlementPending={walletRefreshCount > 0 || pendingBets.some((bet) => bet.round > 0 && bet.round < roundNumber)}
          focusTarget={betTarget}
          onClose={() => { setBetOpen(false); setBetTarget(null); }}
          onOpenAnalytics={() => {
            setBetOpen(false);
            setBetTarget(null);
            openTable('trend');
          }}
          onOpenBasket={() => {
            setBetOpen(false);
            setBasketOpen(true);
          }}
          onPlaced={(nextUser) => {
            if (nextUser) setUser(nextUser);
            queryClient.invalidateQueries({ queryKey: ['my-bets'] });
            queryClient.invalidateQueries({ queryKey: ['room-distribution'] });
            queryClient.invalidateQueries({ queryKey: ['me'] });
          }}
        />
      ) : null}

      {myBetsOpen ? (
        <BetHistorySheet
          token={token}
          roomContext={room?.code ? { code: room.code, label: room.label } : null}
          onClose={() => setMyBetsOpen(false)}
        />
      ) : null}

      {basketOpen ? (
        <BasketSheet
          room={room}
          roundNumber={roundNumber}
          displayRound={dailySeq(currentRound)}
          phase={phase}
          bets={currentRoundBets}
          onPlaceAnother={() => {
            setBasketOpen(false);
            openBetSheet();
          }}
          onClose={() => setBasketOpen(false)}
        />
      ) : null}

      {tableOpen ? (
        <div className="sheet-backdrop room-table-backdrop" role="presentation" onMouseDown={() => setTableOpen(false)}>
          <section className="my-bets-sheet room-table-sheet" role="dialog" aria-modal="true" aria-labelledby="room-table-title" onMouseDown={(event) => event.stopPropagation()} data-room-table-sheet-react="true">
            <div className="sheet-grabber" aria-hidden="true" />
            <header className="sheet-header">
              <div>
                {/* `#225`, not `225/288`. The denominator was noise here: nobody
                    reads this sheet to find out how much of the day is left, and
                    a slash invited it to be read as a score. Zero-padded so the
                    day opens at #001 and the heading never changes width. */}
                <h2 id="room-table-title">{t('readTable')}{dailySeq(currentRound) ? ` · #${dailySeq(currentRound)}` : ''}</h2>
                <p style={{ color: ROUND_TONE_COLOR[phase.tone] }}>{phase.label} {phase.value}</p>
              </div>
              <button className="icon-button" type="button" onClick={() => setTableOpen(false)} aria-label={t('close')}>
                <X size={18} aria-hidden="true" />
              </button>
            </header>
            <nav className="room-table-seg" role="tablist" aria-label={t('readTable')}>
              {([['trend', t('tableTrend')], ['dist', t('tableDist')], ['ppl', t('tablePeople')]] as const).map(([key, label]) => (
                <button
                  key={key}
                  id={`room-table-tab-${key}`}
                  type="button"
                  role="tab"
                  aria-controls="room-table-panel"
                  aria-selected={tableTab === key}
                  tabIndex={tableTab === key ? 0 : -1}
                  className={tableTab === key ? 'active' : ''}
                  onClick={() => setTableTab(key)}
                  onKeyDown={(event) => handleTablistKey(event, ['trend', 'dist', 'ppl'] as const, key, setTableTab, (next) => `room-table-tab-${next}`)}
                >
                  {label}
                </button>
              ))}
            </nav>
            <div
              id="room-table-panel"
              className={`room-table-body room-table-body-${tableTab}`}
              role="tabpanel"
              aria-labelledby={`room-table-tab-${tableTab}`}
            >
              {tableTab === 'trend' ? (
                <AnalyticsSheet
                  embedded
                  game={room.game}
                  rounds={analyticsQuery.data || []}
                  loading={analyticsQuery.isLoading}
                  error={analyticsQuery.isError}
                  onRetry={() => analyticsQuery.refetch()}
                  onClose={() => {}}
                  onBet={() => {}}
                  canBet={false}
                  onQuickBet={(target) => { setTableOpen(false); openBetSheet(target); }}
                  quickBetLocked={!roundAcceptsBets}
                />
              ) : null}
              {tableTab === 'dist' ? (
                <RoomBetsSheet
                  embedded
                  room={room}
                  roundNumber={roundNumber}
                  dailySeq={dailySeq(currentRound)}
                  phase={phase}
                  distribution={roomBetsDistributionQuery.data || distributionQuery.data}
                  prevDistribution={prevDistributionQuery.data}
                  loading={(roomBetsDistributionQuery.isLoading || roomBetsDistributionQuery.isFetching) && !roomBetsDistributionQuery.data && !distributionQuery.data}
                  error={roomBetsDistributionQuery.isError && !distributionQuery.data}
                  guestMode={guestMode}
                  onRefresh={() => { distributionQuery.refetch(); roomBetsDistributionQuery.refetch(); }}
                  onOpenProfile={(profileId) => { setTableOpen(false); openProfileFromRoom(profileId); }}
                  roundAcceptsBets={roundAcceptsBets}
                  onOpenTrend={() => setTableTab('trend')}
                  onPickTarget={setTablePick}
                  selectedPick={tablePick}
                  recentRounds={analyticsQuery.data || []}
                  onClose={() => setTableOpen(false)}
                />
              ) : null}
              {tableTab === 'ppl' ? (
                <RoomPeopleView
                  heat={heat}
                  game={room.game}
                  guestMode={guestMode}
                  onOpenProfile={(profileId) => { setTableOpen(false); openProfileFromRoom(profileId); }}
                />
              ) : null}
            </div>
            {/* One bar, one job. The caption beside the button was a different
                sentence per tab — a tagline, a disclaimer, a lock notice — none
                of which the button needed, and the lock notice only repeated the
                disabled label. The action now owns the bar full width, where a
                thumb can reach it. */}
            {/* The bar used to throw away everything you had just read: it
                closed the panel and opened a blank slip, leaving you to rebuild
                the pick from memory. It now carries the market you tapped. */}
            <div className="room-table-betbar">
              {tablePick ? (
                <button className="room-table-bet-clear" type="button" onClick={() => setTablePick(null)} aria-label={t('clear')}>
                  <X size={16} aria-hidden="true" />
                </button>
              ) : null}
              <button
                className={`room-table-bet-btn${tablePick ? ' armed' : ''}`}
                type="button"
                disabled={!roundAcceptsBets}
                onClick={() => { setTableOpen(false); openBetSheet(tablePick); }}
              >
                {!roundAcceptsBets
                  ? t('betsLocked')
                  : tablePick
                    ? t('betOnPick').replace('%pick%', pickLabel(room.game, tablePick.tab, tablePick.pick, t))
                    : t('betCta')}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {historyOpen ? (
        <HistoryDrawerSheet
          game={room.game}
          rounds={historyQuery.data || []}
          loading={historyQuery.isLoading}
          error={historyQuery.isError}
          onRetry={() => historyQuery.refetch()}
          onOpenDraw={(round) => { setHistoryOpen(false); setSelectedDraw(round); }}
          onOpenArchive={() => {
            setHistoryOpen(false);
            setRoute('archive');
          }}
          onClose={() => setHistoryOpen(false)}
        />
      ) : null}

      {selectedDraw ? <DrawDetailSheet round={selectedDraw} onClose={() => setSelectedDraw(null)} /> : null}


      {plusOpen ? (
        <div className="sheet-backdrop" role="presentation" onMouseDown={() => setPlusOpen(false)}>
          <section className="plus-sheet-react" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()} data-plus-sheet-react="true">
            <div className="px-4 pt-3 pb-2 flex justify-center"><div className="w-10 h-1 bg-border1 rounded-full" /></div>
            <div className="px-4 pb-6 grid grid-cols-3 gap-3">
              <button className="plus-tile tap" type="button" onClick={() => { setPlusOpen(false); openTable('dist'); }}>
                <UsersRound size={26} className="text-cyan-300" aria-hidden="true" />
                <span>{t('sheetRoomBets')}</span>
              </button>
              <button className="plus-tile tap" type="button" onClick={() => { setPlusOpen(false); setRoute('leaderboard'); }}>
                <Crown size={26} className="text-gold-400" aria-hidden="true" />
                <span>{t('sheetTop')}</span>
              </button>
              <button className="plus-tile tap" type="button" onClick={() => { setPlusOpen(false); setHistoryOpen(true); }}>
                <History size={26} className="text-violet-300" aria-hidden="true" />
                <span>{t('sheetHistory')}</span>
              </button>
              <button className="plus-tile tap" type="button" onClick={() => { setPlusOpen(false); setMyBetsOpen(true); }}>
                <Ticket size={26} className="text-emerald-300" aria-hidden="true" />
                <span>{t('sheetMyBets')}</span>
              </button>
              <button className="plus-tile tap" type="button" onClick={() => { setPlusOpen(false); openTable('trend'); }}>
                <BarChart3 size={26} className="text-rose-300" aria-hidden="true" />
                <span>{t('analyticsTitle')}</span>
              </button>
              <button className="plus-tile tap" type="button" onClick={() => { setPlusOpen(false); setRoute('rules'); }}>
                <ShieldCheck size={26} className="text-gold-400" aria-hidden="true" />
                <span>{t('sheetVerify')}</span>
              </button>
            </div>
          </section>
        </div>
      ) : null}
      {switchOpen ? (
        <SwitchRoomSheet
          currentRoom={room}
          rooms={roomsQuery.data?.rooms || []}
          vipLevel={userVipLevel(user)}
          guestMode={guestMode}
          onClose={() => setSwitchOpen(false)}
          onSwitch={(nextRoom) => {
            setActiveRoomId(nextRoom.id);
            setSwitchOpen(false);
            setBetOpen(false);
            setMyBetsOpen(false);
            queryClient.invalidateQueries({ queryKey: ['room-distribution'] });
            queryClient.invalidateQueries({ queryKey: ['my-bets'] });
            queryClient.invalidateQueries({ queryKey: ['room-chat'] });
          }}
        />
      ) : null}

      {room && (overlay.stage === 'settling' || overlay.stage === 'settled') && !myBetsOpen ? (
        <RoundResultCard
          stage={overlay.stage}
          roundLabel={roundLabelFor(overlay.round)}
          roomGame={room.game}
          draw={overlay.stage === 'settled'
            ? (settledSummaries[overlay.round]?.draw ?? (overlayDraw?.round === overlay.round ? overlayDraw.draw : null))
            : (overlayDraw?.round === overlay.round ? overlayDraw.draw : null)}
          bets={overlay.stage === 'settled'
            ? (settledSummaries[overlay.round]?.bets || []).map(normalizeBet)
            : pendingBets.filter((bet) => bet.round === overlay.round)}
          fiatCurrency={fiatCurrency}
          autoClose
          onClose={dismissReveal}
          onHistory={() => { dismissReveal(); setHistoryOpen(true); }}
        />
      ) : null}

      {room && pinnedRound != null && settledSummaries[pinnedRound] ? (
        <RoundResultCard
          stage="settled"
          roundLabel={roundLabelFor(pinnedRound)}
          roomGame={room.game}
          draw={settledSummaries[pinnedRound].draw ?? null}
          bets={(settledSummaries[pinnedRound].bets || []).map(normalizeBet)}
          fiatCurrency={fiatCurrency}
          autoClose={false}
          onClose={() => setPinnedRound(null)}
          onHistory={() => { setPinnedRound(null); setHistoryOpen(true); }}
        />
      ) : null}

      {guestAuthOpen ? (
        <GuestAuthSheet
          backdropClassName="sheet-backdrop nested-sheet-backdrop"
          onSignup={() => {
            clearGuest();
            setGuestAuthOpen(false);
            setRoute('auth.register');
          }}
          onLogin={() => {
            clearGuest();
            setGuestAuthOpen(false);
            setRoute('auth.login');
          }}
          onClose={() => setGuestAuthOpen(false)}
        />
      ) : null}
    </section>
  );
}

type RoomDrumItem = ActivityDrumItem & {
  eventStatus?: string;
  eventId?: string;
  drawAt?: number;
  closeAt?: number;
  entries?: number;
  minEntries?: number;
  entryLimit?: number;
  ticketBalance?: number;
  myEntries?: number;
  prize?: number;
};

function RoomActivityDrum({
  token,
  guestMode,
  userId,
  onRaffle,
  onOpenRewards,
}: {
  token: string;
  guestMode: boolean;
  userId?: number | string;
  onRaffle: (view: 'register' | 'participants' | 'result', resultId?: string | null) => void;
  onOpenRewards: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [pauseUntil, setPauseUntil] = useState(0);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const [clockOffsetSec, setClockOffsetSec] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  const viewed = useRef(new Set<string>());
  const dragStartY = useRef<number | null>(null);
  const suppressClick = useRef(false);
  const drumQuery = useQuery({
    queryKey: ['activity-drum', token],
    queryFn: () => fetchActivityDrum(token),
    enabled: !!token && !guestMode,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const raffleQuery = useQuery({
    queryKey: ['raffle', token],
    queryFn: () => fetchRaffleData(token),
    enabled: !!token && !guestMode,
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: false,
  });
  const sourceItems = useMemo(
    () => guestMode ? [guestDrumItem()] : roomDrumItems(drumQuery.data?.items || [], raffleQuery.data, userId),
    [drumQuery.data?.items, guestMode, raffleQuery.data, userId],
  );
  const items = useMemo(() => {
    if (sourceItems.length) return sourceItems;
    if (drumQuery.isError && raffleQuery.isError) return [];
    if (drumQuery.isLoading || raffleQuery.isLoading) return [];
    return [trustDrumItem()];
  }, [drumQuery.isError, drumQuery.isLoading, raffleQuery.isError, raffleQuery.isLoading, sourceItems]);
  const prismItems = useMemo(() => {
    if (items.length <= 1) return items;
    if (items.length === 2) return [items[0], items[1], trustDrumItem()];
    return items.slice(0, 3);
  }, [items]);
  const serverEpoch = serverEpochSeconds(drumQuery.data?.server_time) || serverEpochSeconds(raffleQuery.data?.server_time);

  useEffect(() => {
    if (!serverEpoch) return;
    setClockOffsetSec(serverEpoch - Math.floor(Date.now() / 1000));
  }, [serverEpoch]);

  useEffect(() => {
    const tick = () => setNowSec(Math.floor(Date.now() / 1000) + clockOffsetSec);
    tick();
    const timer = window.setInterval(tick, 1_000);
    return () => window.clearInterval(timer);
  }, [clockOffsetSec]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const sync = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  useEffect(() => {
    if (prismItems.length <= 1 || reducedMotion || !pageVisible) return;
    const timer = window.setInterval(() => {
      if (Date.now() < pauseUntil) return;
      setIndex((current) => (current + 1) % prismItems.length);
    }, 16_000);
    return () => window.clearInterval(timer);
  }, [pageVisible, pauseUntil, prismItems.length, reducedMotion]);

  useEffect(() => {
    if (index >= prismItems.length) setIndex(0);
  }, [index, prismItems.length]);

  const active = prismItems[index] || prismItems[0];
  useEffect(() => {
    if (!token || !active || !uuidLike(active.id) || viewed.current.has(active.id)) return;
    viewed.current.add(active.id);
    updateActivityDrumState(token, active.id, 'view').catch(() => undefined);
  }, [active, token]);

  if (!active && (drumQuery.isLoading || raffleQuery.isLoading)) {
    return (
      <div className="room-activity-drum is-loading" aria-label={t('loading')}>
        <div className="room-drum-skeleton" aria-hidden="true"><i /><span><b /><b /></span><i /></div>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="room-activity-drum is-error">
        <button
          className="room-drum-retry"
          type="button"
          onClick={() => {
            void drumQuery.refetch();
            void raffleQuery.refetch();
          }}
        >
          <span>{t('roomDrumServiceUnavailable')}</span>
          <strong>{t('roomDrumRetry')} <RefreshCw size={13} aria-hidden="true" /></strong>
        </button>
      </div>
    );
  }

  function pauseRotation() {
    setPauseUntil(Date.now() + 30_000);
  }

  function activate(item: RoomDrumItem, view: ReturnType<typeof drumView>) {
    pauseRotation();
    if (item.type === 'guest_public_raffle') {
      onRaffle('register');
      return;
    }
    if (item.type === 'raffle_trust') {
      onRaffle('participants');
      return;
    }
    if (uuidLike(item.id)) {
      updateActivityDrumState(token, item.id, 'click')
        .then(() => queryClient.invalidateQueries({ queryKey: ['activity-drum', token] }))
        .catch(() => undefined);
    }
    if (item.type === 'daily_raffle' || item.layout === 'raffle') {
      if (view.target === 'register' && !item.ticketBalance) onOpenRewards();
      else onRaffle(view.target, item.eventId);
    } else {
      onOpenRewards();
    }
  }

  return (
    <div
      className="room-activity-drum"
      onPointerDown={(event) => {
        dragStartY.current = event.clientY;
        pauseRotation();
      }}
      onPointerUp={(event) => {
        if (dragStartY.current == null || prismItems.length <= 1) return;
        const delta = event.clientY - dragStartY.current;
        dragStartY.current = null;
        if (Math.abs(delta) < 24) return;
        suppressClick.current = true;
        setIndex((current) => (current + (delta < 0 ? 1 : prismItems.length - 1)) % prismItems.length);
        window.setTimeout(() => { suppressClick.current = false; }, 0);
      }}
      onPointerCancel={() => { dragStartY.current = null; }}
      onClickCapture={(event) => {
        if (!suppressClick.current) return;
        event.preventDefault();
        event.stopPropagation();
      }}
      onFocusCapture={pauseRotation}
      onMouseEnter={pauseRotation}
      data-room-activity-drum-react="true"
    >
      <div className="room-drum-viewport">
        <div
          className={`room-drum-prism ${prismItems.length > 1 && !reducedMotion ? 'is-rotating' : ''}`}
          style={{ transform: prismItems.length > 1 && !reducedMotion ? `rotateX(${-index * 120}deg)` : undefined }}
        >
          {prismItems.map((item, faceIndex) => {
            const view = drumView(item, nowSec, t, language);
            const isCurrent = reducedMotion ? faceIndex === 0 : faceIndex === index;
            return (
              <button
                key={`${item.id}:${faceIndex}`}
                type="button"
                className={`room-drum-face room-drum-face-${faceIndex} state-${view.state}`}
                onClick={() => activate(item, view)}
                tabIndex={isCurrent ? 0 : -1}
                aria-hidden={!isCurrent}
                aria-label={[view.title, view.secondary, view.metricLabel, view.cta].filter(Boolean).join('. ')}
              >
                <span className="room-drum-copy">
                  <span className="room-drum-title">
                    <strong>{view.title}</strong>
                    {view.pool ? <b>{view.pool}</b> : null}
                  </span>
                  <span className="room-drum-secondary">{view.secondary}</span>
                </span>
                <span className="room-drum-action">
                  {view.metric ? <time className={`tone-${view.metricTone}`} dateTime={view.dateTime || undefined}>{view.metric}</time> : null}
                  <span className={`tone-${view.tone}`}><span className="room-drum-cta-label">{view.cta}</span><ChevronRight size={13} aria-hidden="true" /></span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function roomDrumItems(definitions: ActivityDrumItem[], raffle?: RaffleData, userId?: number | string): RoomDrumItem[] {
  const normalized = definitions.map((item) => normalizeRoomDrumItem(item));
  const hasRaffle = normalized.some((item) => item.layout === 'raffle' || item.type === 'daily_raffle');
  if (!hasRaffle && raffle?.event) normalized.unshift(raffleDrumItem(raffle, userId));
  return normalized
    .filter((item) => item.layout === 'raffle' || item.title || item.title_main || item.title_i18n || item.title_main_i18n)
    .sort((a, b) => Number(a.tier || 1) - Number(b.tier || 1) || Number(b.priority || 0) - Number(a.priority || 0));
}

function guestDrumItem(): RoomDrumItem {
  return {
    id: 'guest-public-raffle',
    type: 'guest_public_raffle',
    tier: 1,
    priority: 100,
  };
}

function trustDrumItem(): RoomDrumItem {
  return {
    id: 'raffle-trust',
    type: 'raffle_trust',
    tier: 2,
    priority: 1,
  };
}

function normalizeRoomDrumItem(item: ActivityDrumItem): RoomDrumItem {
  const params = item.secondary_params || {};
  return {
    ...item,
    eventStatus: String(item.status || ''),
    eventId: String(item.event_id || item.eventId || item.raffle_event_id || item.raffleEventId || ''),
    drawAt: Number(item.draw_at || item.expires_at || 0),
    closeAt: Number(item.register_close_at || item.draw_at || item.expires_at || 0),
    entries: Number(item.entry_count ?? params.n ?? 0),
    minEntries: Number(item.min_entries_to_run ?? params.min ?? 50),
    entryLimit: Number(item.entry_limit ?? params.max ?? params.m ?? 0),
    ticketBalance: Number(item.my_ticket_balance || 0),
    myEntries: Number(item.my_entry_count || 0),
    prize: Number(item.my_prize_brl || 0),
  };
}

function raffleDrumItem(data: RaffleData, userId?: number | string): RoomDrumItem {
  const event = data.event!;
  const bracket = event.prize_brackets?.find((row) => Number(event.total_entries || 0) >= Number(row.min_n || 0)) || event.prize_brackets?.[0];
  const entries = (data.entries || []).filter((entry) => String(entry.user_id || '') === String(userId || '')).length;
  return {
    id: `raffle:${String(event.event_id || event.id || 'current')}`,
    type: 'daily_raffle',
    layout: 'raffle',
    tier: 1,
    priority: 100,
    color_theme: 'gold',
    eventStatus: String(event.status || 'registering'),
    eventId: String(event.event_id || event.id || ''),
    drawAt: Number(event.scheduled_at || 0),
    closeAt: Math.max(0, Number(event.scheduled_at || 0) - 10),
    entries: Number(event.total_entries || 0),
    minEntries: Number(event.min_entries_to_run || 50),
    entryLimit: Number(event.max_total_entries || bracket?.max_n || 0),
    ticketBalance: (data.tickets || []).filter((ticket) => ticket.status === 'active').length,
    myEntries: entries,
    prize: Number((data.recent_events || []).find((row) => String(row.event_id || row.id) === String(event.event_id || event.id))?.my_prize || 0),
    pool_label: bracket?.pool ? fmtMoney(bracket.pool) : '',
  };
}

function drumView(
  item: RoomDrumItem,
  nowSec: number,
  t: ReturnType<typeof useI18nStore.getState>['t'],
  language: string,
) {
  if (item.type === 'guest_public_raffle') {
    return {
      title: t('roomDrumGuestTitle'),
      secondary: t('roomDrumGuestSecondary'),
      metric: '',
      metricLabel: '',
      dateTime: '',
      cta: t('roomDrumLogin'),
      target: 'register' as const,
      pool: '',
      state: 'guest',
      tone: 'signal',
      metricTone: 'signal',
    };
  }

  if (item.type === 'raffle_trust') {
    return {
      title: t('roomDrumTrustTitle'),
      secondary: t('roomDrumTrustSecondary'),
      metric: '',
      metricLabel: '',
      dateTime: '',
      cta: t('roomDrumSeeDetails'),
      target: 'participants' as const,
      pool: '',
      state: 'trust',
      tone: 'signal',
      metricTone: 'signal',
    };
  }

  const status = String(item.eventStatus || item.status || 'registering').toLowerCase();
  const raffle = item.layout === 'raffle' || item.type === 'daily_raffle';
  const drawTime = item.drawAt
    ? new Intl.DateTimeFormat(language, { hour: '2-digit', minute: '2-digit', hour12: false }).format(item.drawAt * 1000)
    : '';
  const targetTime = Number(status === 'registering' ? item.closeAt : ['locked', 'full'].includes(status) ? item.drawAt : 0);
  const countdown = targetTime && targetTime > nowSec ? formatDrumCountdown(targetTime - nowSec) : '';

  if (!raffle) {
    const title = activityDrumText(t, item.title_main || item.title, item.title_main_i18n || item.title_i18n, item.title_main_params || item.title_params);
    const secondary = activityDrumText(t, item.secondary, item.secondary_i18n, item.secondary_params);
    const cta = activityDrumText(t, item.cta_label, item.cta_label_i18n) || t('actRaffleSeeCta');
    return {
      title,
      secondary,
      metric: countdown,
      metricLabel: countdown ? interpolateDrumMessage(t('roomDrumClosesIn'), { time: countdown }) : '',
      dateTime: targetTime ? new Date(targetTime * 1000).toISOString() : '',
      cta,
      target: 'register' as const,
      pool: String(item.pool_label || ''),
      state: 'activity',
      tone: 'signal',
      metricTone: 'signal',
    };
  }

  const entries = Number(item.entries || 0);
  const minEntries = Number(item.minEntries || 50);
  const entryLimit = Number(item.entryLimit || 100);
  const progress = entries < minEntries
    ? interpolateDrumMessage(t('roomDrumNeedsEntries'), { n: entries, min: minEntries, max: entryLimit })
    : interpolateDrumMessage(t('roomDrumConfirmed'), { n: entries, max: entryLimit });
  const isLocked = ['locked', 'full'].includes(status);
  const isSettling = ['settling', 'drawing'].includes(status);
  const isSettled = ['settled', 'paid'].includes(status);
  const isVoided = ['voided', 'cancelled', 'canceled'].includes(status);
  const title = isLocked ? t('roomDrumRegistrationClosed')
    : isSettling ? t('roomDrumSettling')
      : isSettled && item.prize ? interpolateDrumMessage(t('roomDrumWon'), { value: fmtAmount(item.prize) })
        : isSettled && item.myEntries ? t('roomDrumLost')
          : isVoided ? interpolateDrumMessage(t('roomDrumVoided'), { min: minEntries })
            : t('roomDrumDailyTitle');
  const secondary = isVoided
    ? interpolateDrumMessage(t('roomDrumRefunded'), { n: item.myEntries || 0 })
    : isSettled ? (item.myEntries ? interpolateDrumMessage(t('roomDrumMyEntries'), { n: item.myEntries, progress }) : progress)
      : isSettling ? (item.myEntries ? interpolateDrumMessage(t('roomDrumMyEntries'), { n: item.myEntries, progress }) : progress)
        : item.myEntries ? interpolateDrumMessage(t('roomDrumMyEntries'), { n: item.myEntries, progress })
          : item.ticketBalance ? interpolateDrumMessage(t('roomDrumTicketsAvailable'), { n: item.ticketBalance, progress })
            : progress;
  const target = raffleJourneyTargetForStatus(status);
  const cta = isSettled ? t('roomDrumViewResult')
    : isVoided ? t('roomDrumSeeDetails')
      : isSettling ? t('roomDrumViewDraw')
        : isLocked ? (item.myEntries ? t('roomDrumMyNumbers') : t('roomDrumSeeDetails'))
          : item.myEntries && item.ticketBalance ? t('raffleAddTicketsCta')
            : item.myEntries ? t('roomDrumMyNumbers')
              : item.ticketBalance ? t('drumJoin') : t('roomDrumEarnTickets');
  const metric = status === 'registering' ? countdown : isLocked && drawTime ? drawTime : '';
  const metricLabel = status === 'registering' && countdown
    ? interpolateDrumMessage(t('roomDrumClosesIn'), { time: countdown })
    : isLocked && drawTime ? interpolateDrumMessage(t('roomDrumDrawAt'), { time: drawTime }) : '';
  const urgent = status === 'registering' && Boolean(targetTime) && targetTime - nowSec <= 30;
  return {
    title,
    secondary,
    metric,
    metricLabel,
    dateTime: targetTime ? new Date(targetTime * 1000).toISOString() : '',
    cta,
    target,
    pool: String(item.pool_label || ''),
    state: isVoided ? 'voided' : isSettled ? (item.prize ? 'won' : 'settled') : isSettling ? 'settling' : isLocked ? 'locked' : 'registering',
    tone: isVoided ? 'danger' : isSettled && item.prize ? 'win' : status === 'registering' && item.ticketBalance && !item.myEntries ? 'money' : 'signal',
    metricTone: urgent ? 'danger' : 'signal',
  };
}

function interpolateDrumMessage(template: string, params: Record<string, string | number>) {
  return Object.entries(params).reduce(
    (value, [name, replacement]) => value.split(`%${name}%`).join(String(replacement)),
    template,
  );
}

function serverEpochSeconds(value?: number | { unix?: number }) {
  const raw = typeof value === 'number' ? value : Number(value?.unix || 0);
  if (!raw) return 0;
  return raw > 1_000_000_000_000 ? Math.floor(raw / 1000) : Math.floor(raw);
}

function activityDrumText(
  t: ReturnType<typeof useI18nStore.getState>['t'],
  literal?: string,
  key?: string,
  params?: Record<string, string | number>,
) {
  let value = String(literal || '');
  if (!value && key) {
    const knownKey = ({
      actRaffleTitleMain: 'actRaffleTitleMain',
      actRaffleSecondaryEntries: 'actRaffleSecondaryEntries',
      actRaffleSeeCta: 'actRaffleSeeCta',
      actRaffleResultCta: 'actRaffleResultCta',
    } as const)[key];
    value = knownKey ? t(knownKey) : '';
  }
  Object.entries(params || {}).forEach(([name, replacement]) => {
    value = value.split(`%${name}%`).join(String(replacement));
  });
  return value;
}

// Units, not `mm:ss` — this number sits inches from the draw's literal clock
// time ("Sorteio de hoje … 07:00"), and "10:24" was read as a time of day.
// Same shape as formatRaffleCountdown; expressed on a raw duration.
function formatDrumCountdown(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const pad = (value: number) => String(value).padStart(2, '0');
  const days = Math.floor(safe / 86_400);
  const hours = Math.floor((safe % 86_400) / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  if (days > 0) return `${days}d ${pad(hours)}h`;
  if (hours > 0) return `${hours}h ${pad(minutes)}m`;
  if (minutes > 0) return `${minutes}m ${pad(secs)}s`;
  return `${secs}s`;
}

function uuidLike(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function RoomRevealOverlay({ reveal, compact = false, onDismiss }: { reveal: RoomRevealState; compact?: boolean; onDismiss: () => void }) {
  const t = useI18nStore((state) => state.t);
  const lastdigit = reveal.draw.lastdigit;
  const dice = reveal.draw.sicbo || [];
  const diceSum = dice.reduce((sum, value) => sum + Number(value || 0), 0);
  const triple = dice.length === 3 && dice.every((value) => value === dice[0]);
  const roulette = reveal.draw.roulette;
  const rouletteTone = roulette == null ? 'green' : rouletteColor(Number(roulette));
  // Same draw settles all three games — footer the other two results
  const others: Array<{ game: GameCode; value: string; color?: string }> = [];
  if (reveal.game !== 'lastdigit' && lastdigit !== undefined) others.push({ game: 'lastdigit', value: String(lastdigit) });
  if (reveal.game !== 'sicbo' && dice.length === 3) others.push({ game: 'sicbo', value: `${dice.join('+')}=${diceSum}` });
  if (reveal.game !== 'roulette' && roulette !== undefined) {
    const tone = rouletteColor(Number(roulette));
    others.push({ game: 'roulette', value: String(roulette), color: tone === 'red' ? '#f87171' : tone === 'green' ? '#34d399' : '#cbd5e1' });
  }

  // With the bet record open the player is already watching the one row they
  // care about, and that row now carries the draw itself — so a full-screen
  // reveal lands on top of the exact thing it is announcing. Downgrade to a
  // banner and let the card flip in place.
  if (compact) {
    const headline =
      reveal.game === 'sicbo' ? (dice.length === 3 ? `${dice.join('+')}=${diceSum}` : '')
        : reveal.game === 'roulette' ? (roulette == null ? '' : String(roulette))
          : lastdigit === undefined ? '' : String(lastdigit);
    return (
      <div className={`room-reveal-banner-react ${reveal.exiting ? 'exiting' : ''}`} role="status" data-room-reveal-compact="true">
        <GameIcon game={reveal.game} className="room-reveal-game-ico" />
        <b>{t('summaryTitle').replace('%round%', String(reveal.round || '---'))}</b>
        <span className={`room-reveal-banner-value-react tone-${reveal.game === 'roulette' ? rouletteTone : 'default'}`}>{headline}</span>
        {triple ? <em>{t('sicboTripleN').replace('%n%', String(dice[0]))}</em> : null}
        <button type="button" onClick={onDismiss} aria-label={t('close')}>
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className={`room-reveal-stage-react ${reveal.exiting ? 'exiting' : ''}`} role="presentation" onMouseDown={onDismiss} data-room-reveal-react="true">
      <section className={`room-reveal-card-react game-${reveal.game}`} role="dialog" aria-modal="true" aria-label={t(gameNameKey(reveal.game))} onMouseDown={(event) => event.stopPropagation()}>
        <button className="room-reveal-close-react" type="button" onClick={onDismiss} aria-label={t('close')}>
          <X size={18} aria-hidden="true" />
        </button>
        <span className="room-reveal-game-react">
          <GameIcon game={reveal.game} className="room-reveal-game-ico" />
          {t(gameNameKey(reveal.game))}
        </span>
        <span className="room-reveal-round-react">{t('summaryTitle').replace('%round%', String(reveal.round || '---'))}</span>

        {reveal.game === 'lastdigit' ? (
          <div className="room-reveal-lastdigit-react">
            <div className="room-reveal-slot-window-react">
              <div className="room-reveal-slot-strip-react" style={{ '--slot-end': `${reveal.slotEnd}px` } as CSSProperties}>
                {Array.from({ length: 60 }, (_, index) => <span key={index}>{index % 10}</span>)}
                <span>{lastdigit ?? '-'}</span>
              </div>
            </div>
            <div className="room-reveal-tags-react">
              {/* Semantic colors match the rest of the app: big=amber small=cyan odd=rose even=sky */}
              <span className={Number(lastdigit) >= 5 ? 'amber' : 'cyan'}>{lastdigit == null ? '-' : Number(lastdigit) >= 5 ? t('big') : t('small')}</span>
              <span className={Number(lastdigit) % 2 === 0 ? 'sky' : 'rose'}>{lastdigit == null ? '-' : Number(lastdigit) % 2 === 0 ? t('even') : t('odd')}</span>
            </div>
          </div>
        ) : null}

        {reveal.game === 'sicbo' ? (
          <div className="room-reveal-sicbo-react">
            <div className="room-reveal-dice-react room-reveal-dice-pips">
              {(dice.length ? dice : [1, 1, 1]).map((value, index) => <DiceFaceSvg key={`${value}:${index}`} n={value} size={44} />)}
            </div>
            <div className="room-reveal-tags-react">
              <span className={diceSum >= 11 ? 'amber' : 'cyan'}>{t('sicboSumLabel')} {diceSum || '-'}</span>
              {!triple && diceSum ? <span className={diceSum >= 11 ? 'amber' : 'cyan'}>{diceSum >= 11 ? t('big') : t('small')}</span> : null}
              {triple ? <span className="gold">{t('triple')}!</span> : null}
            </div>
          </div>
        ) : null}

        {reveal.game === 'roulette' ? (
          <div className="room-reveal-roulette-react">
            {/* Pointer lives on the static wrapper so it doesn't spin with the disk */}
            <div className="room-reveal-wheelwrap-react">
              <div className="room-reveal-wheel-react" style={{ '--wheel-end': `${reveal.wheelEnd}deg` } as CSSProperties}>
                <i />
              </div>
            </div>
            <div className={`room-reveal-pocket-react tone-${rouletteTone}`}>{roulette ?? '-'}</div>
            <div className="room-reveal-tags-react">
              <span className={rouletteTone}>{rouletteTone === 'red' ? t('red') : rouletteTone === 'black' ? t('black') : t('green')}</span>
              {roulette ? <span className={Number(roulette) % 2 === 0 ? 'sky' : 'amber'}>{Number(roulette) % 2 === 0 ? t('even') : t('odd')}</span> : null}
              {roulette ? <span>{Number(roulette) >= 19 ? '19-36' : '1-18'}</span> : null}
              {roulette ? <span>{rouletteDozenLabel(Number(roulette))}</span> : null}
            </div>
          </div>
        ) : null}

        {others.length ? (
          <div className="room-reveal-others-react">
            {others.map((item) => (
              <span key={item.game}>
                <GameIcon game={item.game} className="room-reveal-other-ico" />
                <b style={item.color ? { color: item.color } : undefined}>{item.value}</b>
              </span>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function SwitchRoomSheet({
  currentRoom,
  rooms,
  vipLevel,
  guestMode,
  onClose,
  onSwitch,
}: {
  currentRoom: Room;
  rooms: Room[];
  vipLevel: number;
  guestMode: boolean;
  onClose: () => void;
  onSwitch: (room: Room) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const showToast = useAppStore((state) => state.showToast);
  const switchUser = useAuthStore((state) => state.user);
  const groups = useMemo(() => groupSwitchableRooms(currentRoom, rooms), [currentRoom, rooms]);
  const empty = groups.every((group) => group.rooms.length === 0);

  // Legacy keeps locked rows tappable and explains via toast (room-entry-feature.js).
  function handleSwitchTap(nextRoom: Room, gate: ReturnType<typeof switchRoomGate>) {
    if (!gate.ok) {
      if (gate.reason === 'soon') showToast(t('introSoon'), 'info');
      else if (gate.reason === 'signin') showToast(t('roomDirectorySignInPopup'), 'warn', { ms: 3600 });
      else if (gate.reason === 'full') showToast(`${t('introFull')} (${roomOnlineCount(nextRoom)}/${nextRoom.capacity})`, 'warn');
      else if (gate.reason === 'vip') showToast(t('introVipShort').replace('%vip%', String(gate.minVip)).replace('%my%', String(vipLevel)).replace('%exp%', String(Math.max(0, Number(((switchUser?.vip_thresholds || {}) as Record<string, number>)[String(gate.minVip)] ?? 0) - Number(switchUser?.total_effective_bet_brl ?? switchUser?.totalEffectiveBet ?? 0)))), 'warn');
      return;
    }
    onSwitch(nextRoom);
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet switch-room-sheet-react" role="dialog" aria-modal="true" aria-label={t('switchRoomTitle')} onMouseDown={(event) => event.stopPropagation()} data-switch-room-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('switchRoomTitle')}</h2>
            <p>{t('switchRoomDesc')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="switch-room-list">
          {empty ? <p className="empty-note">{t('switchRoomEmpty')}</p> : null}
          {groups.map((group) => (
            <section className="switch-room-group" key={group.game}>
              <header className="switch-room-heading">
                <div>
                  <span>{t(gameNameKey(group.game))}</span>
                  {group.isCurrent ? <b>{t('switchRoomCurrent')}</b> : null}
                </div>
                <small>{group.rooms.length} {t(group.rooms.length === 1 ? 'roomSingular' : 'roomsPlural')}</small>
              </header>
              <div className="switch-room-options">
                {group.rooms.map((nextRoom) => {
                  const gate = switchRoomGate(nextRoom, vipLevel, guestMode, t);
                  const minVip = gate.minVip;
                  const rakebackPct = Number(nextRoom.rakebackPct || 0) * 100;
                  const accessLabel = gate.reason === 'soon'
                    ? t('roomComingSoon')
                    : gate.reason === 'full'
                      ? t('roomFull')
                      : minVip > 0
                        ? t('roomDirectoryVipThreshold').replace('%vip%', String(minVip))
                        : t('roomDirectoryNoVipThreshold');
                  return (
                    <button
                      key={nextRoom.id}
                      type="button"
                      onClick={() => handleSwitchTap(nextRoom, gate)}
                      className={`switch-room-option tap${gate.reason === 'vip' || gate.reason === 'full' ? ' access-locked' : ''}${gate.reason === 'soon' ? ' is-muted' : ''}`}
                    >
                      <span className="switch-room-option-copy">
                        <span className="switch-room-option-title">
                          <strong>{roomDisplayName(nextRoom, rooms, t)}</strong>
                          {rakebackPct > 0 ? <b>{t('roomDirectoryBackBadge').replace('%pct%', fmtPercent(rakebackPct))}</b> : null}
                        </span>
                        <span className="switch-room-option-meta">
                          <span data-online-count className="switch-room-option-online">
                            <i aria-hidden="true" />
                            {roomOnlineCount(nextRoom)}/{nextRoom.capacity} {t('online')}
                          </span>
                          <span aria-hidden="true">·</span>
                          <span>{t('roomDirectoryBetsThisRound').replace('%n%', fmtInt(nextRoom.thisRoundBets || 0))}</span>
                        </span>
                        <span className="switch-room-option-foot">
                          <span className="switch-room-option-gate">{accessLabel}</span>
                          <span className="switch-room-option-limit">{fmtMoney(nextRoom.chipValuesBrl?.[0] || 0)}–{fmtMoney(nextRoom.perRoundMaxBrl)}</span>
                        </span>
                      </span>
                      {gate.ok ? (
                        <span className="hn-room-cta">{t('roomDirectoryPlay')}</span>
                      ) : gate.reason === 'vip' ? (
                        <span className="hn-room-lock"><Lock size={12} aria-hidden="true" />{t('roomDirectoryVipShort').replace('%vip%', String(minVip))}</span>
                      ) : gate.reason === 'signin' ? (
                        <span className="hn-room-lock"><Lock size={12} aria-hidden="true" />{t('login')}</span>
                      ) : gate.reason === 'full' ? (
                        <span className="hn-room-lock">{t('roomFull')}</span>
                      ) : (
                        <span className="hn-room-lock">{t('roomComingSoon')}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}

function DanmakuLayer({ items, minimized }: { items: DanmakuItem[]; minimized: boolean }) {
  return (
    <div className={`danmaku-layer-react ${minimized ? 'minimized' : ''}`} aria-hidden="true" data-danmaku-layer-react="true">
      {items.map((item) => (
        <span className={`danmaku-item-react row-${item.row} kind-${item.kind}`} key={item.id}>
          {item.text}
        </span>
      ))}
    </div>
  );
}

function RoomBetsSheet({
  room,
  roundNumber,
  dailySeq,
  phase,
  distribution,
  prevDistribution,
  loading,
  error,
  guestMode,
  onRefresh,
  onOpenProfile,
  roundAcceptsBets,
  onOpenTrend,
  onPickTarget,
  selectedPick,
  recentRounds,
  onClose,
  embedded = false,
}: {
  room: Room;
  roundNumber: number;
  dailySeq?: string;
  phase: RoundPhase;
  distribution?: RoomDistribution;
  prevDistribution?: RoomDistribution;
  loading: boolean;
  error: boolean;
  guestMode: boolean;
  onRefresh: () => void;
  onOpenProfile: (profileId: string) => void;
  roundAcceptsBets: boolean;
  /** Switches the outer panel to the results/trend view. */
  onOpenTrend?: () => void;
  /** Arms the panel's bet bar with the market the player just tapped. */
  onPickTarget?: (target: BetTarget | null) => void;
  /** The armed pick, so the tapped cell can show as selected. */
  selectedPick?: BetTarget | null;
  /** Settled rounds, newest first — the quiet-round history strip. */
  recentRounds?: AnalyticsRound[];
  onClose: () => void;
  embedded?: boolean;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const fiatCurrency = useCountryStore((state) => state.country.fiatCurrency);
  const [betFilter, setBetFilter] = useState<string>('all');
  // The feed answers "who is betting right now" in arrival order, which is the
  // wrong order for the two questions players actually asked of it: what is the
  // biggest bet on the table, and who has the most on this round.
  const [betSort, setBetSort] = useState<'recent' | 'amount' | 'bettor'>('recent');
  // Consensus bars can weight by money (how much) or by heads (how many people).
  // Default money — that's what "where the money is going" asks; heads is one tap.
  const [weightBy, setWeightBy] = useState<'amount' | 'people'>('amount');
  const allBets = distribution?.bets || distribution?.all_bets || [];
  const hasCompleteBets = Boolean(distribution?.betsComplete || distribution?.bets_complete || allBets.length);
  const recent = hasCompleteBets ? allBets : (distribution?.recentBets || distribution?.recent_bets || []);
  // Currency chips are jargon to most players — only offer them when the list
  // actually mixes currencies (today's rooms are BRL-only, so they stay hidden).
  const currencyMix = new Set(recent.map((bet) => String(bet.currency || fiatCurrency).toUpperCase())).size > 1;
  const effectiveFilter = !currencyMix && (betFilter === fiatCurrency || betFilter === 'USDT') ? 'all' : betFilter;
  // Guests have no "mine"; currency chips only exist on a real currency mix.
  // When both fall away the bar is a lone "All" that filters nothing.
  const betFilterOptions = [
    'all' as const,
    ...(guestMode ? [] : ['mine' as const]),
    ...(currencyMix ? [fiatCurrency, 'USDT'] : []),
  ];
  const filteredRecent = recent.filter((bet) => {
    if (effectiveFilter === 'mine') return !!bet.mine;
    if (effectiveFilter === fiatCurrency || effectiveFilter === 'USDT') return String(bet.currency || fiatCurrency).toUpperCase() === effectiveFilter;
    return true;
  });
  // Sorting never mutates the source: `recent` is the query's array.
  const sortedRecent = betSort === 'amount'
    ? [...filteredRecent].sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
    : filteredRecent;
  // "Top bettors" is a different question from "biggest bet": one whale beats a
  // regular who placed eight bets on the biggest-bet list, and loses to them
  // here. Keyed by profile id so a person betting five markets is one row.
  const topBettors = useMemo(() => {
    const byUser = new Map<string, { bet: RoomDistributionBet; total: number; count: number }>();
    filteredRecent.forEach((bet) => {
      const key = roomBetProfileId(bet) || String(bet.user?.username || bet.ts || Math.random());
      const seen = byUser.get(key);
      const amount = Number(bet.amount || 0);
      if (seen) {
        seen.total += amount;
        seen.count += 1;
        // Keep the biggest single bet as the row's representative pick.
        if (amount > Number(seen.bet.amount || 0)) seen.bet = bet;
      } else {
        byUser.set(key, { bet, total: amount, count: 1 });
      }
    });
    return [...byUser.values()].sort((a, b) => b.total - a.total);
  }, [filteredRecent]);

  const filteredStakeLines = currencyLines([
    { currency: fiatCurrency, amount: filteredRecent.filter((bet) => String(bet.currency || fiatCurrency).toUpperCase() === fiatCurrency).reduce((sum, bet) => sum + Number(bet.amount || 0), 0) },
    { currency: 'USDT', amount: filteredRecent.filter((bet) => String(bet.currency || fiatCurrency).toUpperCase() === 'USDT').reduce((sum, bet) => sum + Number(bet.amount || 0), 0) },
  ]);
  const brlSummary = distribution?.summary?.[fiatCurrency];
  const brlBreakdown = distribution?.breakdown?.[fiatCurrency];
  const poolAmount = Number(brlSummary?.poolAmount || distribution?.pools?.[fiatCurrency]?.amount || 0);
  const poolCap = Number(brlSummary?.poolCap || distribution?.pools?.[fiatCurrency]?.cap || room.poolCapBrl || 0);
  const totalBets = Number(distribution?.totalBets || distribution?.total_bets || brlSummary?.betCount || recent.length || 0);
  // Crowd bars are recomputed from the round's own bets so an exact-number bet
  // counts toward the big/small and odd/even sides it actually covers. Falls
  // back to the backend aggregates whenever the bet list is truncated.
  const consensus = hasCompleteBets ? deriveConsensus(room.game, recent) : null;
  const barPairs = pairBreakdownBars(applyConsensus(brlBreakdown?.bars || [], consensus));
  // Share-of-pool denominator: every bar reads against the whole round, so
  // "100%" can only ever mean "all of the money", never "all of this pair".
  const barMoneyTotal = consensus ? consensus.totalAmount : poolAmount;
  const barPeopleTotal = consensus
    ? consensus.allPeople.size
    : Number(distribution?.pools?.[fiatCurrency]?.count || 0) || totalBets;
  const gridItems = brlBreakdown?.grid || [];

  // ── Decision-support derivations (all degrade gracefully when empty) ──
  // "vs last round" pool + heads, to judge if this round's signal is unusual.
  const prevPoolAmount = Number(prevDistribution?.summary?.[fiatCurrency]?.poolAmount ?? prevDistribution?.pools?.[fiatCurrency]?.amount ?? 0);
  const prevPoolBettors = Number(prevDistribution?.pools?.[fiatCurrency]?.count ?? prevDistribution?.totalBets ?? prevDistribution?.total_bets ?? 0);
  // My own picks this round — highlight my side/number vs the crowd.
  const myPickKeys = new Set(
    recent.filter((bet) => bet.mine).map((bet) => String(bet.pick ?? bet.rawPick ?? '').toLowerCase()).filter(Boolean),
  );
  const isQuietRound = recent.length === 0 && totalBets === 0 && poolAmount === 0;
  // Roulette does not get the paired duo bars, for two reasons that both make
  // them show numbers that are simply wrong:
  //
  // 1. `pairBreakdownBars` chunks the bar list two at a time. That is right for
  //    last digit (big/small, odd/even) and sicbo (big/small), but roulette
  //    sends twelve bars, so the tail pairs up as "3rd 12 vs column 1" and
  //    "column 2 vs column 3" — two markets that are not opposite sides of
  //    anything, drawn as a head-to-head bar.
  // 2. `betMarketKeys` classifies no roulette pick into a dozen or a column, so
  //    `applyConsensus` overwrites those bars with 0 and erases real money the
  //    backend had already totalled correctly.
  //
  // The board below covers the same markets from the untouched backend bars —
  // its own even-money, dozen and column rows — so this is not a loss of
  // information. It removes a duplicate that was also lying.
  const showDuoBars = room.game !== 'roulette';

  const body = (
    <>
        {/* One line, not three tiles. The old grid gave a third of the golden
            zone to "round total bet cap" — an ops constraint that answers none
            of the questions this panel exists for, wrapped to two lines, and
            sat 700× larger than the pool beside it. Pool / bets / people are
            what a bettor reads; the cap survives as a muted token. */}
        <div className="room-bets-statusline">
          <strong>{fmtMoney(poolAmount)}</strong>
          <span>{t(totalBets === 1 ? 'roomBetsFilteredCountOne' : 'roomBetsFilteredCount').replace('%n%', fmtInt(totalBets))}</span>
          <span>{fmtInt(barPeopleTotal)} {t(barPeopleTotal === 1 ? 'roomBetsPersonOne' : 'roomBetsPeople')}</span>
          {/* A live round compared against a *finished* one is a moving target
              — at t=0 it always read "▼ 100% vs last" in alarm red, which is
              just the round resetting. A neutral reference says as much. */}
          {prevPoolAmount > 0 ? <em>{t('roomBetsPrevRef').replace('%pool%', fmtMoney(prevPoolAmount))}</em> : null}
          {poolCap > 0 ? <i title={t('roomBetsPoolCap')}>{t('roomBetsCapShort').replace('%cap%', fmtMoney(poolCap))}</i> : null}
        </div>

        <div className="room-bets-scroll">
          {loading ? <p className="empty-note">{t('loading')}</p> : null}
          {error ? (
            <div className="sheet-state-react">
              <strong>{t('roomLoadError')}</strong>
              <button type="button" onClick={onRefresh}>{t('retry')}</button>
            </div>
          ) : null}

          {!loading && !error ? (
            <>
              <section className="room-bets-section">
                <div className="room-bets-section-head">
                  <span>{t('roomBetsBreakdown')}</span>
                  {/* The toggle only re-weights the duo bars, which roulette no
                      longer renders — a control that changes nothing is worse
                      than no control. */}
                  {!isQuietRound && showDuoBars ? (
                    <div className="weight-toggle" role="tablist" aria-label={t('roomBetsWeightBy')}>
                      <button type="button" role="tab" aria-selected={weightBy === 'amount'} className={weightBy === 'amount' ? 'active' : ''} onClick={() => setWeightBy('amount')}>{t('roomBetsWeightMoney')}</button>
                      <button type="button" role="tab" aria-selected={weightBy === 'people'} className={weightBy === 'people' ? 'active' : ''} onClick={() => setWeightBy('people')}>{t('roomBetsWeightPeople')}</button>
                    </div>
                  ) : null}
                </div>
                {isQuietRound ? (
                  // ⑦ Quiet round — a friendly nudge + last-round reference beats a
                  // wall of "R$ 0 · 0 people".
                  <div className="pool-quiet">
                    <p className="pool-quiet-title">{t('roomBetsQuietTitle')}</p>
                    <p className="pool-quiet-sub">{t('roomBetsQuietSub')}</p>
                    {prevPoolAmount > 0 ? (
                      <p className="pool-quiet-prev">{t('roomBetsQuietPrev').replace('%pool%', fmtMoney(prevPoolAmount)).replace('%n%', fmtInt(prevPoolBettors))}</p>
                    ) : null}
                    {/* The copy used to send players to a "Trend" tab that does
                        not exist by that name. Now it is the actual button. */}
                    {onOpenTrend ? (
                      <button type="button" className="pool-quiet-cta" onClick={onOpenTrend}>{t('roomBetsQuietTrendCta')}</button>
                    ) : null}
                  </div>
                ) : null}
                {/* An empty round is exactly when history is worth the most, and
                    exactly when this panel used to show nothing but white space
                    — the last rounds were one tab away and never borrowed. */}
                {isQuietRound ? (
                  <QuietRoundHistory game={room.game} rounds={recentRounds || []} onPick={onPickTarget} selected={selectedPick} />
                ) : (
                  <>
                    {showDuoBars
                      ? barPairs.map((pair, index) => <PoolBarPair key={`${pair[0]?.key || 'a'}:${pair[1]?.key || 'b'}:${index}`} game={room.game} pair={pair} weightBy={weightBy} myKeys={myPickKeys} moneyTotal={barMoneyTotal} selected={selectedPick} onPick={onPickTarget} />)
                      : null}
                    {/* No "smart money" card. It named the round's biggest stake
                        and a 3-win streak as signal, which this game cannot
                        have: the draw is public and verifiably random, so no
                        stake size and no streak predicts the next digit. The
                        facts it showed are already on this screen — the grid
                        gives money per number, the list gives every bet — so
                        once it stopped being a copy target it was a duplicate
                        under a label that promised an edge. */}
                    {room.game === 'roulette'
                      ? <RouletteHeatBoard items={gridItems} bars={brlBreakdown?.bars || []} myKeys={myPickKeys} />
                      : <PoolGrid game={room.game} items={gridItems} myKeys={myPickKeys} selected={selectedPick} onPick={onPickTarget} />}
                  </>
                )}
              </section>

              <section className="room-bets-section">
                <div className="room-bets-section-head">
                  <span>{hasCompleteBets ? t('roomBetsAll') : t('roomBetsRecent')}</span>
                  {/* The same count used to appear three times — the status
                      line, this header, and the filter summary below. The
                      header keeps it only while a filter is hiding rows, where
                      "3 of 7" is the one thing the status line cannot say. */}
                  {recent.length > 0 && filteredRecent.length !== recent.length ? (
                    <b>{t('roomBetsFilteredOf').replace('%n%', fmtInt(filteredRecent.length)).replace('%total%', fmtInt(recent.length))}</b>
                  ) : null}
                  {recent.length > 0 && filteredRecent.length === recent.length && !hasCompleteBets ? <b>{t('roomBetsRecentLimited')}</b> : null}
                </div>
                {/* Three readings of the same feed, not three filters: arrival
                    order, biggest single bet, and who has the most on the round.
                    Only worth showing once there is enough to reorder. */}
                {recent.length > 1 ? (
                  <div className="room-bets-sort" role="tablist" aria-label={t('roomBetsSortBy')}>
                    {(['recent', 'amount', 'bettor'] as const).map((key) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={betSort === key}
                        className={betSort === key ? 'active' : ''}
                        onClick={() => setBetSort(key)}
                      >
                        {t(key === 'recent' ? 'roomBetsSortRecent' : key === 'amount' ? 'roomBetsSortAmount' : 'roomBetsSortBettor')}
                      </button>
                    ))}
                  </div>
                ) : null}
                {/* Filters over an empty list are dead weight; guests can't have
                    "mine"; currency chips only appear on a real currency mix. */}
                {recent.length > 0 ? (
                  <>
                    {/* A filter bar whose only option is "All" filters nothing —
                        it is a control that cannot change anything on screen. */}
                    {betFilterOptions.length > 1 ? (
                    <div className="room-bets-filter-bar-react" role="group" aria-label={t('roomBetsFilter')}>
                      {betFilterOptions.map((item) => (
                        <button
                          className={effectiveFilter === item ? 'active' : ''}
                          type="button"
                          onClick={() => setBetFilter(item)}
                          key={item}
                        >
                          {item === 'all' ? t('roomBetsFilterAll') : item === 'mine' ? t('roomBetsFilterMine') : item}
                        </button>
                      ))}
                    </div>
                    ) : null}
                    {/* Only worth a row once a filter narrows the list — for
                        "All" it just restates the status line at the top. */}
                    {effectiveFilter !== 'all' ? (
                      <div className="room-bets-filter-summary-react">
                        <span>{filteredRecent.length === 1 ? t('roomBetsFilteredCountOne') : t('roomBetsFilteredCount').replace('%n%', fmtInt(filteredRecent.length))}</span>
                        <b>{filteredStakeLines.join(' · ') || fmtMoney(0)}</b>
                      </div>
                    ) : null}
                  </>
                ) : null}
                {guestMode || distribution?.visibility?.individualBetsBlurred ? <p className="room-bets-privacy-note">{t('roomBetsHiddenGuest')}</p> : null}
                {recent.length === 0 ? <p className="empty-note">{t('roomBetsNoBets')}</p> : null}
                {recent.length > 0 && filteredRecent.length === 0 ? <p className="empty-note">{t('roomBetsFilterEmpty')}</p> : null}
                <div className={guestMode || distribution?.visibility?.individualBetsBlurred ? 'room-bets-blurred-list' : ''}>
                  {betSort === 'bettor'
                    ? topBettors.map((entry, index) => (
                      <RecentBetRow
                        key={`${roomBetProfileId(entry.bet) || index}`}
                        bet={entry.bet}
                        onOpenProfile={onOpenProfile}
                        rank={index + 1}
                        totalStaked={entry.total}
                        betCount={entry.count}
                      />
                    ))
                    : sortedRecent.map((bet, index) => <RecentBetRow key={`${bet.ts || bet.placed_at || index}:${bet.pick}:${bet.amount}`} bet={bet} onOpenProfile={onOpenProfile} />)}
                </div>
              </section>
            </>
          ) : null}
        </div>
    </>
  );
  if (embedded) return body;
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="my-bets-sheet room-bets-sheet-react" role="dialog" aria-modal="true" aria-label={t('roomBetsTitle')} onMouseDown={(event) => event.stopPropagation()} data-room-bets-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('roomBetsTitle')}{dailySeq ? ` · ${Number(dailySeq)}/${ROUNDS_PER_DAY}` : (roundNumber ? ` #${roundNumber}` : '')}</h2>
            <p>{t('roomBetsSubtitle').replace('%n%', fmtInt(totalBets)).replace('%time%', phase.value || formatClock(0))}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        {body}
      </section>
    </div>
  );
}


function PoolBarPair({ game, pair, weightBy = 'amount', myKeys, moneyTotal = 0, selected, onPick }: {
  game: GameCode;
  pair: Array<{ key?: string; count?: number; bettors?: number; amount?: number }>;
  weightBy?: 'amount' | 'people';
  myKeys?: Set<string>;
  /** The round's money, for the "how much of the round is on this pair" line. */
  moneyTotal?: number;
  selected?: BetTarget | null;
  onPick?: (target: BetTarget | null) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const a = pair[0];
  const b = pair[1];
  const amtA = Number(a?.amount || 0);
  const amtB = Number(b?.amount || 0);
  // "How many people" — distinct bettors from the backend; fall back to the
  // bet count on older responses that don't send bettors yet.
  const peopleA = Number(a?.bettors ?? a?.count ?? 0);
  const peopleB = Number(b?.bettors ?? b?.count ?? 0);
  // The split weights by money or by heads depending on the toggle.
  const wA = weightBy === 'people' ? peopleA : amtA;
  const wB = weightBy === 'people' ? peopleB : amtB;
  // Share of *this pair*, so the two sides always add to 100. Measuring each
  // side against the whole round instead left a hole the reader had to explain
  // to themselves: "Big 42% · Small 53%" under a two-sided bar reads as broken
  // arithmetic, and a footnote about the other market answers a question nobody
  // asked here. This bar has one job — Big or Small, where is the crowd — and
  // the pair is the only honest denominator for it.
  //
  // What that denominator drops is "is this market where the round even is",
  // which matters: sicbo's combos and pairs sit outside big/small entirely, so
  // a real round held R$ 350 of R$ 1.040 here and the old bar spent two thirds
  // of its width on hatching. That question now gets its own line below.
  const pairTotal = wA + wB;
  const pctA = pairTotal > 0 ? Math.round((wA / pairTotal) * 100) : 0;
  // Derived, not rounded independently: 47/53 must never render as 47/54.
  const pctB = pairTotal > 0 ? 100 - pctA : 0;
  const hasData = pairTotal > 0;
  // Money on the round that this pair does not cover. Stated as the pair's own
  // weight ("R$ 831 of R$ 881") rather than as a leftover percentage — the
  // reader can size it at a glance without subtracting anything.
  const pairMoney = amtA + amtB;
  const showWeight = hasData && moneyTotal > pairMoney;
  const toneA = poolTone(a?.key);
  const toneB = poolTone(b?.key);
  // ⑥ Which side did I back? — highlight it so I read "me vs the crowd".
  const mineA = !!myKeys?.has(String(a?.key ?? '').toLowerCase());
  const mineB = !!myKeys?.has(String(b?.key ?? '').toLowerCase());
  // Reading the split and backing a side were two different screens: you had
  // to memorise the number, close the panel and rebuild the pick from scratch.
  // A side is now a button that arms the bet bar underneath.
  const targetA = poolBetTarget(game, a?.key);
  const targetB = poolBetTarget(game, b?.key);
  const isSel = (target: BetTarget | null) => !!target && selected?.tab === target.tab && selected?.pick === target.pick;
  const side = (
    entry: { key?: string } | undefined,
    target: BetTarget | null,
    tone: string,
    mine: boolean,
    pct: number,
    right: boolean,
  ) => {
    const label = <b>{right ? (mine ? <i className="you-dot" title={t('chipYou')}>•</i> : null) : null}{sideLabel(game, entry?.key, t)}{right ? null : (mine ? <i className="you-dot" title={t('chipYou')}>•</i> : null)}</b>;
    const value = <em>{hasData ? `${pct}%` : t('roomBetsNoSideYet')}</em>;
    const cls = `pool-duo-side${right ? ' right' : ''} tone-${tone}${mine ? ' mine' : ''}${isSel(target) ? ' picked' : ''}`;
    if (!target || !onPick) return <span className={cls}>{label}{value}</span>;
    return (
      <button type="button" className={cls} aria-pressed={isSel(target)} onClick={() => onPick(isSel(target) ? null : target)}>
        {label}{value}
      </button>
    );
  };
  return (
    <article className={`pool-duo${hasData ? '' : ' empty'}`}>
      <div className="pool-duo-top">
        {side(a, targetA, toneA, mineA, pctA, false)}
        {side(b, targetB, toneB, mineB, pctB, true)}
      </div>
      <div className="pool-duo-bar">
        <i className={`pool-duo-fill tone-${toneA}`} style={{ width: `${hasData ? pctA : 50}%` }} />
        <i className={`pool-duo-fill right tone-${toneB}`} style={{ width: `${hasData ? pctB : 50}%` }} />
        <i className="pool-duo-mid" style={{ left: `${hasData ? pctA : 50}%` }} />
      </div>
      <div className="pool-duo-foot">
        <span className={`tone-${toneA}`}>{fmtMoney(amtA)} · <b>{fmtInt(peopleA)}</b> {t(peopleA === 1 ? 'roomBetsPersonOne' : 'roomBetsPeople')}</span>
        <span className={`tone-${toneB}`}><b>{fmtInt(peopleB)}</b> {t(peopleB === 1 ? 'roomBetsPersonOne' : 'roomBetsPeople')} · {fmtMoney(amtB)}</span>
      </div>
      {/* How much of the round is on this market at all. Only when something is
          outside it — "R$ 881 of R$ 881" would be a line saying nothing. */}
      {showWeight ? (
        <p className="pool-duo-rest">
          {t('roomBetsPairWeight').replace('%pair%', fmtMoney(pairMoney)).replace('%total%', fmtMoney(moneyTotal))}
        </p>
      ) : null}
    </article>
  );
}

const QUIET_HISTORY_WINDOW = 20;

/**
 * What a settled round paid out on each even-money market, or null when the
 * game has no result for it yet. Roulette's zero pays neither side.
 */
/**
 * The backend hands guest viewers the literal display name "Player" for every
 * bettor. It is data, not a missing value, so the `|| fallback` never fired and
 * an English word rode into the ticker, the drum and the distribution list.
 * Translate it at render time only — `shared/game/consensus.ts` still keys the
 * de-duplication off the raw sentinel.
 */
function bettorName(raw: string | undefined | null, t: (key: MessageKey) => string): string {
  const name = String(raw || '').trim();
  return !name || name.toLowerCase() === 'player' ? t('anonPlayerName') : name;
}

function roundSides(game: GameCode, round: AnalyticsRound): { bigSmall: 'big' | 'small' | null; oddEven: 'odd' | 'even' | null } {
  if (game === 'lastdigit') {
    const digit = round.lastdigit;
    if (digit == null || !Number.isFinite(digit)) return { bigSmall: null, oddEven: null };
    return { bigSmall: digit >= 5 ? 'big' : 'small', oddEven: digit % 2 === 1 ? 'odd' : 'even' };
  }
  if (game === 'sicbo') {
    const sum = round.sicboSum;
    if (sum == null || !Number.isFinite(sum)) return { bigSmall: null, oddEven: null };
    // A triple pays neither side, so it counts toward neither tally.
    if (round.sicboTriple != null) return { bigSmall: null, oddEven: null };
    return { bigSmall: sum >= 11 ? 'big' : 'small', oddEven: sum % 2 === 1 ? 'odd' : 'even' };
  }
  const num = round.roulette;
  if (num == null || !Number.isFinite(num) || num === 0) return { bigSmall: null, oddEven: null };
  return { bigSmall: num >= 19 ? 'big' : 'small', oddEven: num % 2 === 1 ? 'odd' : 'even' };
}

/**
 * The last rounds, borrowed into an empty Distribution.
 *
 * A quiet round used to leave two thirds of the panel blank — precisely when a
 * player has no crowd to read and history is the only thing left to go on. The
 * data is already in memory for the Results tab; this spends it here, and every
 * tile arms the bet bar like the live ones do.
 */
/**
 * The name of one side of a duo bar (Alto/Baixo, Ímpar/Par).
 *
 * `pickLabel` needs `t` or it returns the English authoring label, and for
 * roulette there is no bet option whose pick is big/small at all, so it fell
 * through to the raw key. Both paths put English on screen: "Big / Small" in
 * the money split and "big 10 / 10 small" in the last-20 bars.
 */
function sideLabel(game: GameCode, key: string | undefined, t: (k: MessageKey) => string): string {
  if (key === 'big' || key === 'small' || key === 'odd' || key === 'even') return t(key);
  return pickLabel(game, undefined, key, t);
}

function QuietRoundHistory({ game, rounds, onPick, selected }: {
  game: GameCode;
  rounds: AnalyticsRound[];
  onPick?: (target: BetTarget | null) => void;
  selected?: BetTarget | null;
}) {
  const t = useI18nStore((state) => state.t);
  const window = rounds.slice(0, QUIET_HISTORY_WINDOW);
  const tally = { big: 0, small: 0, odd: 0, even: 0 };
  window.forEach((round) => {
    const { bigSmall, oddEven } = roundSides(game, round);
    if (bigSmall) tally[bigSmall] += 1;
    if (oddEven) tally[oddEven] += 1;
  });
  // Rounds since each digit last landed — the "overdue number" read that
  // players come to the Results tab for.
  const missing = game === 'lastdigit'
    ? Array.from({ length: 10 }, (_, digit) => {
      const index = rounds.findIndex((round) => round.lastdigit === digit);
      return { digit, gap: index < 0 ? rounds.length : index };
    }).sort((a, b) => b.gap - a.gap).slice(0, 3)
    : [];
  if (window.length === 0) return null;

  const pair = (a: 'big' | 'odd', b: 'small' | 'even') => {
    const total = tally[a] + tally[b];
    if (total === 0) return null;
    const pct = Math.round((tally[a] / total) * 100);
    return (
      <div className="quiet-hist-row" key={a}>
        {/* t(a) directly, not pickLabel: called without `t` it returns the English
            authoring fallback, and roulette has no option whose pick is
            big/small at all, so the bars read "big 10 / 10 small · Odd 12 / 8
            Even" in every locale. These four keys are the same Alto/Baixo/
            Ímpar/Par the map and the road view use. */}
        <span className={`tone-${poolTone(a)}`}>{t(a)} <b>{tally[a]}</b></span>
        <i className="quiet-hist-bar"><em className={`tone-${poolTone(a)}`} style={{ width: `${pct}%` }} /></i>
        <span className={`tone-${poolTone(b)}`}><b>{tally[b]}</b> {t(b)}</span>
      </div>
    );
  };

  return (
    <section className="quiet-hist">
      <p className="quiet-hist-title">{t('roomBetsHistWindow').replace('%n%', fmtInt(window.length))}</p>
      {pair('big', 'small')}
      {pair('odd', 'even')}
      {missing.length > 0 && missing[0].gap > 0 ? (
        <>
          <p className="quiet-hist-title">{t('roomBetsHistOverdue')}</p>
          <div className="quiet-hist-chips">
            {missing.map(({ digit, gap }) => {
              const target = poolBetTarget(game, String(digit));
              const picked = !!target && selected?.tab === target.tab && selected?.pick === target.pick;
              const body = <><strong>{digit}</strong><em>{t('roomBetsHistGap').replace('%n%', fmtInt(gap))}</em></>;
              if (!target || !onPick) return <span className="quiet-hist-chip" key={digit}>{body}</span>;
              return (
                <button type="button" className={`quiet-hist-chip${picked ? ' picked' : ''}`} key={digit} aria-pressed={picked} onClick={() => onPick(picked ? null : target)}>
                  {body}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  );
}

function PoolGrid({ game, items, myKeys, selected, onPick }: {
  game: GameCode;
  items: Array<{ key?: string | number; count?: number; bettors?: number; amount?: number }>;
  myKeys?: Set<string>;
  selected?: BetTarget | null;
  onPick?: (target: BetTarget | null) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const title = game === 'roulette' ? t('roomBetsGridRoulette') : game === 'sicbo' ? t('roomBetsGridSicbo') : t('roomBetsGridLastdigit');
  const activeCount = items.filter((item) => Number(item.count || 0) > 0).length;
  // Small grids (0–9, dice) read at a glance, so show them open. Roulette's 37
  // cells are a wall of "R$0" — keep those one tap behind the toggle.
  const [open, setOpen] = useState(game !== 'roulette');
  // Nine cells of "R$ 0 · 0 people" around one real number is noise pretending
  // to be data. Untouched numbers collapse to a bare digit strip until the
  // round is busy enough that the full grid carries information again.
  const [showAllCells, setShowAllCells] = useState(false);
  if (items.length === 0) return null;
  const maxAmount = items.reduce((max, item) => Math.max(max, Number(item.amount || 0)), 0);
  const totalAmount = items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const quietGrid = !showAllCells && activeCount > 0 && activeCount <= Math.max(2, Math.floor(items.length / 3));
  const shownItems = quietGrid ? items.filter((item) => Number(item.amount || 0) > 0) : items;
  const restItems = quietGrid ? items.filter((item) => !(Number(item.amount || 0) > 0)) : [];
  return (
    <article className={`pool-grid-card pool-heat-card game-${game} ${open ? 'open' : ''}`}>
      <button type="button" className="pool-grid-toggle" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span>{title}</span>
        <b>{open ? t('roomBetsHideNumbers') : `${t('roomBetsShowNumbers')} · ${fmtInt(activeCount)}`}</b>
      </button>
      {open && maxAmount === 0 ? (
        // A wall of "R$ 0 · 0 people" ×10 tells a new player nothing — one
        // line does, and the grid appears with the first real number bet.
        <p className="pool-grid-empty-note">{t('roomBetsGridEmpty')}</p>
      ) : null}
      {open && maxAmount > 0 ? (
        <>
          <div className={`pool-heat-grid${quietGrid ? ' quiet' : ''}`}>
            {shownItems.map((item) => {
              const index = items.indexOf(item);
              const key = item.key ?? index;
              const label = game === 'sicbo' ? String(index + 1) : String(key);
              const amount = Number(item.amount || 0);
              const people = Number(item.bettors ?? item.count ?? 0);
              const fill = maxAmount > 0 ? Math.round((amount / maxAmount) * 100) : 0;
              // A crown on "R$ 2 · 1 person" devalues the crown. It marks a
              // genuine lead: the top number, held by more than one bettor or
              // carrying a real share of the round.
              const isLead = amount > 0 && amount === maxAmount
                && (people > 1 || (totalAmount > 0 && amount / totalAmount >= 0.3));
              const mine = !!myKeys?.has(label.toLowerCase());
              const target = poolBetTarget(game, label);
              const picked = !!target && selected?.tab === target.tab && selected?.pick === target.pick;
              const cls = `pool-heat-cell ${amount === 0 ? 'empty' : isLead ? 'lead' : amount < maxAmount * 0.28 ? 'cold' : ''}${mine ? ' mine' : ''}${picked ? ' picked' : ''}`;
              const inner = (
                <>
                  <i className="pool-heat-fill" style={{ height: `${fill}%` }} aria-hidden="true" />
                  {isLead ? <span className="pool-heat-crown" aria-hidden="true">👑</span> : null}
                  <strong>{label}</strong>
                  <em>{fmtMoney(amount)}</em>
                  <span>{fmtInt(people)} {t(people === 1 ? 'roomBetsPersonOne' : 'roomBetsPeople')}</span>
                </>
              );
              // A crowned cell you cannot tap is a dead end: the whole point of
              // spotting the hot number is backing it. Tapping arms the bet bar.
              if (!target || !onPick) return <div className={cls} key={`${key}:${index}`}>{inner}</div>;
              return (
                <button type="button" className={cls} key={`${key}:${index}`} aria-pressed={picked} onClick={() => onPick(picked ? null : target)}>
                  {inner}
                </button>
              );
            })}
          </div>
          {/* The untouched numbers, as a bare strip. Still tappable — backing a
              number nobody has taken is a real move — but it no longer spends
              nine full cells saying "R$ 0 · 0 people". */}
          {restItems.length > 0 ? (
            <div className="pool-heat-rest">
              <button type="button" className="pool-heat-rest-toggle" onClick={() => setShowAllCells(true)}>
                {t('roomBetsUntouched').replace('%n%', fmtInt(restItems.length))}
              </button>
              <div className="pool-heat-rest-chips">
                {restItems.map((item) => {
                  const index = items.indexOf(item);
                  const label = game === 'sicbo' ? String(index + 1) : String(item.key ?? index);
                  const target = poolBetTarget(game, label);
                  const picked = !!target && selected?.tab === target.tab && selected?.pick === target.pick;
                  if (!target || !onPick) return <span className="pool-heat-chip" key={label}>{label}</span>;
                  return (
                    <button type="button" className={`pool-heat-chip${picked ? ' picked' : ''}`} key={label} aria-pressed={picked} onClick={() => onPick(picked ? null : target)}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {/* Payouts are fixed, not parimutuel — so the multiplier is a fact the
              grid can state once instead of leaving players to guess. */}
          {gridOddsLabel(game) ? <p className="pool-grid-odds">{gridOddsLabel(game)} {t('ifHit')}</p> : null}
        </>
      ) : null}
    </article>
  );
}

// Casino-style roulette distribution board — mirrors the bet page felt so a
// player reads "where everyone bet, how much, how many" exactly where they'd
// place chips. Numbers carry a red/black/green cell + heat (by money) + a
// R$/people chip; the outside rows (columns · dozens · even-money) fill from
// the breakdown bars. Dozens/columns show '—' until the backend aggregates them.
function RouletteHeatBoard({ items, bars, myKeys }: {
  items: Array<{ key?: string | number; count?: number; bettors?: number; amount?: number }>;
  bars: Array<{ key?: string; count?: number; bettors?: number; amount?: number }>;
  myKeys?: Set<string>;
}) {
  const t = useI18nStore((state) => state.t);
  const byNum = new Map<number, { amount: number; people: number }>();
  items.forEach((it, i) => byNum.set(Number(it.key ?? i), { amount: Number(it.amount || 0), people: Number(it.bettors ?? it.count ?? 0) }));
  const maxAmount = items.reduce((m, it) => Math.max(m, Number(it.amount || 0)), 0);
  const barBy = new Map<string, { amount: number; people: number }>();
  bars.forEach((b) => { if (b.key) barBy.set(String(b.key).toLowerCase(), { amount: Number(b.amount || 0), people: Number(b.bettors ?? b.count ?? 0) }); });

  const cell = (n: number) => {
    const d = byNum.get(n) || { amount: 0, people: 0 };
    const color = n === 0 ? 'green' : ROULETTE_RED.has(n) ? 'red' : 'black';
    const fill = maxAmount > 0 ? Math.round((d.amount / maxAmount) * 100) : 0;
    const mine = !!myKeys?.has(String(n).toLowerCase());
    const isLead = d.amount > 0 && d.amount === maxAmount;
    return (
      <div className={`rhb-cell rhb-${color}${d.amount > 0 ? ' has' : ''}${mine ? ' mine' : ''}${isLead ? ' lead' : ''}`} key={n}>
        <i className="rhb-fill" style={{ height: `${fill}%` }} aria-hidden="true" />
        {isLead ? <span className="rhb-crown" aria-hidden="true">👑</span> : null}
        <b className="rhb-n">{n}</b>
        {d.amount > 0 ? <em className="rhb-chip">{fmtMoney(d.amount)} · {fmtInt(d.people)}</em> : null}
      </div>
    );
  };

  const out = (key: string, label: string, tone: string) => {
    const d = barBy.get(key) || { amount: 0, people: 0 };
    const mine = !!myKeys?.has(key);
    return (
      <div className={`rhb-out tone-${tone}${d.amount > 0 ? ' has' : ''}${mine ? ' mine' : ''}`} key={key}>
        <span className="rhb-out-name">{label}</span>
        <span className="rhb-out-val">{d.amount > 0 ? `${fmtMoney(d.amount)} · ${fmtInt(d.people)}` : '—'}</span>
      </div>
    );
  };

  return (
    <div className="roulette-heat-board">
      <div className="rhb-zero">{cell(0)}</div>
      <div className="rhb-grid">
        {Array.from({ length: 12 }, (_, ri) => (
          <div className="rhb-row" key={ri}>{cell(ri * 3 + 1)}{cell(ri * 3 + 2)}{cell(ri * 3 + 3)}</div>
        ))}
      </div>
      <div className="rhb-cols">{out('col1', '2:1', 'cyan')}{out('col2', '2:1', 'cyan')}{out('col3', '2:1', 'cyan')}</div>
      <div className="rhb-dozens">{out('dz1', t('roulDozen_dz1'), 'violet')}{out('dz2', t('roulDozen_dz2'), 'violet')}{out('dz3', t('roulDozen_dz3'), 'violet')}</div>
      <div className="rhb-evenmoney">
        {out('lo', '1–18', 'slate')}{out('even', t('even').toUpperCase(), 'slate')}{out('red', t('red').toUpperCase(), 'red')}{out('black', t('black').toUpperCase(), 'black')}{out('odd', t('odd').toUpperCase(), 'slate')}{out('hi', '19–36', 'slate')}
      </div>
    </div>
  );
}

function HistoryDrawerSheet({
  game,
  rounds,
  loading,
  error,
  onRetry,
  onOpenDraw,
  onOpenArchive,
  onClose,
}: {
  game: GameCode;
  rounds: ArchiveRound[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpenDraw: (round: ArchiveRound) => void;
  onOpenArchive: () => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const groups = useMemo(() => groupArchiveRoundsByDay(rounds), [rounds]);
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  function startDrag(event: PointerEvent<HTMLElement>) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    setDragStartY(event.clientY);
    setDragOffset(0);
    // Do NOT capture on press: the header holds the ✕ button, and capturing
    // here retargets the click to the header so ✕ never fires. Capture only
    // once a real downward drag begins (moveDrag).
  }

  function moveDrag(event: PointerEvent<HTMLElement>) {
    if (dragStartY == null) return;
    const dy = Math.min(150, Math.max(0, event.clientY - dragStartY));
    if (dy > 4) {
      try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* non-capturable */ }
    }
    setDragOffset(dy);
  }

  function endDrag() {
    if (dragOffset > 70) onClose();
    setDragStartY(null);
    setDragOffset(0);
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={`profile-sheet history-drawer-sheet-react ${dragStartY != null ? 'dragging' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('recentDrawsTitle')}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{ '--history-drag-y': `${dragOffset}px` } as CSSProperties}
        data-history-drawer-react="true"
      >
        <button className="sheet-grabber history-drag-grabber-react" type="button" aria-label={t('close')} onPointerDown={startDrag} onClick={onClose} />
        <header className="sheet-header" onPointerDown={startDrag}>
          <div>
            <h2>{t('recentDrawsTitle')}</h2>
            <p>{t('recentDrawsHint')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="history-drawer-list">
          {loading ? <p className="empty-note">{t('loading')}</p> : null}
          {error ? (
            <div className="sheet-state-react">
              <strong>{t('roomLoadError')}</strong>
              <button type="button" onClick={onRetry}>{t('retry')}</button>
            </div>
          ) : null}
          {!loading && !error && groups.length === 0 ? <p className="empty-note">{t('archiveEmpty')}</p> : null}
          {groups.map((group) => (
            <section className="history-day-group" key={group.key}>
              <header>
                <strong>{group.key === 'today' ? t('today') : t('yesterday')}</strong>
                <span>{t('drawsCount').replace('%n%', String(group.rounds.length))}</span>
              </header>
              <div className="history-round-grid">
                {group.rounds.map((round) => (
                  <button type="button" onClick={() => onOpenDraw(round)} key={`${round.utc_date || group.key}:${round.round_number}`}>
                    <small>#{dailySeq(round) || round.round_number}</small>
                    <HistoryRoundValue round={round} game={game} />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
        <p className="history-archive-note-react">{t('olderInArchive')}</p>
        <button className="ghost-action history-archive-link" type="button" onClick={onOpenArchive}>
          <History size={15} aria-hidden="true" />
          {t('openArchiveFull')}
        </button>
      </section>
    </div>
  );
}

function HistoryRoundValue({ round, game }: { round: ArchiveRound; game: GameCode }) {
  const t = useI18nStore((state) => state.t);
  const games = round.games || {};
  if (game === 'sicbo') {
    const dice = (games.sicbo || []).map((item) => Number(item || 0));
    const sum = dice.reduce((total, item) => total + item, 0);
    const seen = new Map<number, number>();
    for (const face of dice) seen.set(face, (seen.get(face) || 0) + 1);
    const triple = dice.length === 3 && seen.size === 1;
    // Big/small in the colours the bet cards use, so the history reinforces the
    // association the player formed while betting. A triple voids both markets,
    // so it overrides them rather than sitting alongside.
    const tone = triple ? 'triple' : sum >= 11 ? 'big' : 'small';
    return (
      <>
        <strong className={`draw-sum tone-${tone}`}>{sum || '-'}</strong>
        <span className="draw-dice">
          {dice.length
            ? dice.map((face, index) => (
              // Doubles land in ~42% of rounds, so they get the quietest
              // treatment that still reads: the repeated faces brighten, the
              // odd one out stays muted. A badge on two tiles in five would be
              // noise, not signal.
              <span key={index} className={(seen.get(face) || 0) > 1 ? 'is-repeat' : undefined}>
                {face}{index < dice.length - 1 ? <i aria-hidden="true">-</i> : null}
              </span>
            ))
            : '-'}
        </span>
      </>
    );
  }
  if (game === 'roulette') {
    const value = Number(games.roulette ?? -1);
    const color = value >= 0 ? rouletteColor(value) : 'green';
    return (
      <>
        <strong className={`roulette-value tone-${color}`}>{value >= 0 ? value : '-'}</strong>
        <span>{value === 0 ? t('green') : value > 0 ? (value % 2 === 0 ? t('even') : t('odd')) : '-'}</span>
      </>
    );
  }
  const digit = Number(games.lastdigit ?? -1);
  return (
    <>
      {/* Same big/small tones as the bet cards and the Sic Bo strip. These were
          Tailwind utilities too, and lost to `.history-round-grid strong` in
          exactly the same way — every digit rendered white. */}
      <strong className={`draw-sum tone-${digit >= 5 ? 'big' : 'small'}`}>{digit >= 0 ? digit : '-'}</strong>
      <span>{digit >= 0 ? (digit % 2 === 0 ? t('even') : t('odd')) : '-'}</span>
    </>
  );
}

function DrawDetailSheet({ round, onClose }: { round: ArchiveRound; onClose: () => void }) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);

  return (
    <div className="sheet-backdrop nested-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet draw-detail-sheet-react" role="dialog" aria-modal="true" aria-label={t('archiveDetailTitle')} onMouseDown={(event) => event.stopPropagation()} data-draw-detail-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>#{dailySeq(round) || round.round_number}</h2>
            <p>{t('roundGlobalNumber').replace('%n%', String(round.round_number))} · {formatArchiveTime(round.cutoff_ts || round.ends_at || round.starts_at || 0)}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <DrawDetailContent round={round} />
      </section>
    </div>
  );
}

function GamePreview({ game }: { game: GameCode }) {
  if (game === 'roulette') {
    return (
      <div className="roulette-preview">
        {Array.from({ length: 37 }, (_, n) => (
          <span key={n} className={n === 0 ? 'green' : n % 2 ? 'red' : 'black'}>{n}</span>
        ))}
      </div>
    );
  }
  if (game === 'sicbo') {
    return (
      <div className="dice-preview">
        {[1, 2, 3, 4, 5, 6].map((n) => <span key={n}>{n}</span>)}
      </div>
    );
  }
  return (
    <div className="digit-preview">
      {Array.from({ length: 10 }, (_, n) => <span key={n}>{n}</span>)}
    </div>
  );
}

function groupSwitchableRooms(currentRoom: Room, rooms: Room[]) {
  const currentId = currentRoom.id;
  const grouped = new Map<GameCode, Room[]>();
  for (const item of rooms) {
    if (item.id === currentId || item.code === currentRoom.code) continue;
    const list = grouped.get(item.game) || [];
    list.push(item);
    grouped.set(item.game, list);
  }
  const order = [
    currentRoom.game,
    ...(['lastdigit', 'sicbo', 'roulette'] as GameCode[]).filter((game) => game !== currentRoom.game),
  ];
  return order
    .filter((game) => grouped.has(game))
    .map((game) => ({ game, isCurrent: game === currentRoom.game, rooms: grouped.get(game) || [] }));
}

const SWITCH_TIER_MIN_VIP: Record<string, number> = { bronze: 0, silver: 3, gold: 5, diamond: 7, black: 9 };

function switchRoomGate(room: Room, vipLevel: number, guestMode: boolean, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const configuredMinVip = Number(room.minVipLevel);
  const fallbackMinVip = SWITCH_TIER_MIN_VIP[String(room.tier || '').toLowerCase()] ?? 0;
  const minVip = Number.isFinite(configuredMinVip) && configuredMinVip >= 0 ? configuredMinVip : fallbackMinVip;
  if (!room.enabled) return { ok: false, reason: 'soon', label: t('roomComingSoon'), minVip };
  if (room.online >= room.capacity) return { ok: false, reason: 'full', label: t('roomFull'), minVip };
  // Same rule as the hall list (roomDirectoryAccess): a guest may enter
  // bronze, every other tier asks them to log in first.
  if (guestMode && String(room.tier || 'bronze').toLowerCase() !== 'bronze') return { ok: false, reason: 'signin', label: t('roomDirectorySignInRequired'), minVip };
  if (!guestMode && vipLevel < minVip) return { ok: false, reason: 'vip', label: t('accessLocked').replace('%vip%', String(minVip)), minVip };
  return { ok: true, reason: 'ok', label: 'OK', minVip };
}

function heatTier(room: Room) {
  const occupancy = room.capacity ? room.online / room.capacity : 0;
  if (room.thisRoundBets >= 20 || occupancy >= 0.75) return 'hot';
  if (room.thisRoundBets >= 8 || occupancy >= 0.4) return 'warm';
  return 'calm';
}

function maintenanceBlocksGame(level?: string) {
  return level === 'game_only' || level === 'full';
}

function heatLabel(room: Room) {
  const t = useI18nStore.getState().t;
  const tier = heatTier(room);
  if (tier === 'hot') return t('hot');
  if (tier === 'warm') return t('active');
  return t('calm');
}

// Room heat rail — the in-room discovery surface. Instead of a Copy button on
// every one of 50 bets, curate the round into a few 🔥 hot hands / 🐋 whales and
// let the user follow (catch the luck) or just watch (the spectacle) from here.
// The 人 (Jogadores) tab of the read-the-table panel: 🔥 hot hands to follow,
// 🐋 whales to watch, 🧊 cold hands to fade. Consolidates the old inline heat
// rail and adds the cold lane (bet against a losing streak).
/**
 * Who is in the room, by lane. Read-only on purpose.
 *
 * The lanes used to end in "Follow" (copy their bet) and "Fade" (bet against
 * them). Both sold the same false premise — that a streak or a stake predicts a
 * public, verifiably random draw — and they turned a roster into a pair of
 * one-tap gambling shortcuts aimed at a stranger. The row already opens the
 * profile, which is the honest thing to do with "here is who is playing".
 */
function RoomPeopleView({ heat, game, guestMode, onOpenProfile }: {
  heat: { hot: HeatEntry[]; whales: HeatEntry[]; cold: HeatEntry[] };
  game: GameCode;
  guestMode: boolean;
  onOpenProfile: (profileId: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  if (heat.hot.length === 0 && heat.whales.length === 0 && heat.cold.length === 0) {
    // Teach the feature instead of leaving a void: what each lane will show
    // once bets come in, so first-time visitors know why this tab exists.
    return (
      <div className="people-empty">
        <p className="people-empty-title">{t('tablePeopleEmpty')}</p>
        <div className="people-empty-row"><span aria-hidden="true">🔥</span><span><strong>{t('peopleHot')}</strong><em>{t('tablePeopleEmptyHot')}</em></span></div>
        <div className="people-empty-row"><span aria-hidden="true">🐋</span><span><strong>{t('peopleWhale')}</strong><em>{t('tablePeopleEmptyWhale')}</em></span></div>
        <div className="people-empty-row"><span aria-hidden="true">🧊</span><span><strong>{t('peopleCold')}</strong><em>{t('tablePeopleEmptyCold')}</em></span></div>
        <p className="people-empty-note">{t('tablePeopleEmptyNote')}</p>
      </div>
    );
  }
  const rowName = (bet: RoomDistributionBet) => {
    const raw = bettorName(bet.user?.username || bet.user?.name, t);
    return guestMode ? `${String(raw)[0] || 'u'}•••` : String(raw);
  };
  const row = (entry: HeatEntry, kind: 'hot' | 'whale' | 'cold') => {
    const bet = entry.bet;
    const name = rowName(bet);
    const initial = (bet.user?.initial || name[0] || 'U').toUpperCase();
    const pick = pickLabel(bet.game || game, bet.tab, bet.rawPick ?? bet.pick, t) || '--';
    const em = kind === 'hot' ? '🔥' : kind === 'whale' ? '🐋' : '🧊';
    const badge = kind === 'hot'
      ? (entry.streak ? t('heatStreak').replace('%n%', String(entry.streak)) : `${Math.round((entry.windowWinRate || 0) * 100)}%`)
      : kind === 'cold'
        ? (entry.lossStreak ? t('coldStreak').replace('%n%', String(entry.lossStreak)) : `${Math.round((entry.windowLossRate || 0) * 100)}%`)
        : t('heatMultiple').replace('%n%', String(Math.max(2, Math.round(entry.stakeMultiple || 0))));
    const profileId = roomBetProfileId(bet);
    return (
      <div className={`people-row ${kind}`} key={`${kind}:${profileId}:${bet.tab}:${bet.pick}`}>
        <span className="people-em" aria-hidden="true">{em}</span>
        <button className="people-who" type="button" onClick={() => profileId && onOpenProfile(profileId)}>
          <span className="mini-avatar">{initial}</span>
          <span className="people-id">
            <strong>@{name}</strong>
            <span className="people-sub">{badge} · {pick} · {fmtCurrency(Number(bet.amount || 0), bet.currency || 'BRL')}</span>
          </span>
        </button>
      </div>
    );
  };
  return (
    <div className="room-people-view">
      {heat.hot.length ? <div className="people-lane"><span className="people-lane-lb hot">🔥 {t('peopleHot')}</span>{heat.hot.map((entry) => row(entry, 'hot'))}</div> : null}
      {heat.whales.length ? <div className="people-lane"><span className="people-lane-lb whale">🐋 {t('peopleWhale')}</span>{heat.whales.map((entry) => row(entry, 'whale'))}</div> : null}
      {heat.cold.length ? <div className="people-lane"><span className="people-lane-lb cold">🧊 {t('peopleCold')}</span>{heat.cold.map((entry) => row(entry, 'cold'))}</div> : null}
    </div>
  );
}

function RecentBetRow({ bet, onOpenProfile, rank, totalStaked, betCount }: {
  bet: RoomDistributionBet;
  onOpenProfile: (profileId: string) => void;
  /** Top-bettors mode: the person's place, their round total, and how many bets
      it took. The row then reports the person, not the single bet. */
  rank?: number;
  totalStaked?: number;
  betCount?: number;
}) {
  const t = useI18nStore((state) => state.t);
  // PRD §4.3.4: guests see pool aggregates, but individual bets must be
  // anonymized (no handle/avatar/profile link). Server should enforce too.
  const guestMode = useAuthStore.getState().guestMode;
  const rawName = bet.user?.username || bet.user?.name || 'user';
  const name = guestMode ? `${String(rawName)[0] || 'u'}•••` : rawName;
  const vipLevel = Number(bet.user?.vipLevel ?? bet.user?.level ?? 1) || 1;
  const flag = String(bet.user?.flag || '').trim();
  const profileId = roomBetProfileId(bet);
  const following = useFollowingIds();
  const isFollowed = !guestMode && !!profileId && !bet.mine && following.has(profileId);
  // The pick was grey body text under the name, so a row read as two lines of
  // label with no shape: "@ananunes VIP4 / 8". It is the one thing the row
  // exists to say, so it now carries the market's own colour — the same tones
  // the pool bars use, so Small reads teal in both places.
  const pickKey = String(bet.rawPick ?? bet.pick ?? '').toLowerCase();
  const pickText = pickLabel(bet.game || 'lastdigit', bet.tab, bet.rawPick ?? bet.pick, t) || '--';
  const aggregated = betCount != null;
  const content = (
    <>
      <div>
        <strong>
          {rank ? <i className="recent-bet-rank">{rank}</i> : null}
          <span>@{name}</span>
          {flag ? <em>{flag}</em> : null}
          <i>VIP{vipLevel}</i>
          {isFollowed ? <i className="followed">{t('roomFeedFollowingBadge')}</i> : null}
          {bet.mine ? <i className="you">{useI18nStore.getState().t('chipYou')}</i> : null}
        </strong>
        {/* No "· BRL" suffix: the amount on the right already carries the
            currency symbol — repeating the code on every row is noise */}
        {aggregated
          ? <small className="recent-bet-agg">{t(betCount === 1 ? 'roomBetsBetOne' : 'roomBetsBetN').replace('%n%', fmtInt(betCount || 0))}</small>
          : <small className={`recent-bet-pick tone-${poolTone(pickKey)}`}>{pickText}</small>}
      </div>
      <b>{fmtCurrency(aggregated ? Number(totalStaked || 0) : Number(bet.amount || 0), bet.currency || 'BRL')}</b>
    </>
  );
  // No copy button here. Every row used to carry one, so ten identical CTAs
  // competed at equal weight and the action stopped meaning "follow this
  // player" — it just meant "bet again". Worse, the draw is verifiably random,
  // so a stranger's pick carries no signal to follow in the first place.
  // The backend sends avatarDefaultId on every bet (the leaderboard already
  // renders it); this row was the one place still showing a bare initial in a
  // grey square, which made a feed of ten real people look like a table dump.
  const avatarId = Number(bet.user?.avatarDefaultId ?? bet.user?.avatar_default_id ?? 0);
  const avatarSrc = guestMode ? '' : (bet.user?.avatarThumbUrl || bet.user?.avatar_url
    || (avatarId >= 1 && avatarId <= 100 ? defaultAvatarUrl(avatarId) : ''));
  const avatar = (
    <span className="mini-avatar">
      {avatarSrc
        ? <img src={avatarSrc} alt="" loading="lazy" />
        : (bet.user?.initial || name[0] || 'U').toUpperCase()}
    </span>
  );

  if (!profileId || guestMode) {
    return (
      <div className="recent-bet-wrap">
        <div className={`recent-bet ${bet.mine ? 'mine' : ''}`}>{avatar}{content}</div>
      </div>
    );
  }
  // Only the avatar opens a profile. The whole row used to be one button, so a
  // scroll that started on a row fired it — and that destination closes the
  // panel and leaves the room, a heavy price for wanting a peek at someone.
  return (
    <div className={`recent-bet-wrap ${isFollowed ? 'is-followed' : ''}`}>
      <div className={`recent-bet ${bet.mine ? 'mine' : ''}`}>
        <button
          className="recent-bet-profile-button tap"
          type="button"
          onClick={() => onOpenProfile(profileId)}
          data-room-bet-profile-trigger-react="true"
          aria-label={`@${name}`}
        >
          {avatar}
        </button>
        {content}
      </div>
    </div>
  );
}

// Who the viewer follows, as profile ids. Shared cache key, so every row in the
// feed reads the same single request. Following someone has to change what the
// room looks like, or the follow button is a button that does nothing.
function useFollowingIds() {
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const query = useQuery({
    queryKey: ['social-following', token || 'guest'],
    queryFn: () => fetchSocialFollowing(token || ''),
    enabled: !!token && !guestMode,
    staleTime: 60_000,
    retry: false,
  });
  return useMemo(() => {
    const ids = new Set<string>();
    for (const row of query.data?.items || []) {
      const id = String(row.user?.id ?? '').trim();
      if (id) ids.add(id);
    }
    return ids;
  }, [query.data]);
}

function roomBetProfileId(bet: RoomDistributionBet) {
  const user = bet.user || {};
  const raw = user.id ?? user.user_id ?? user.userId ?? user.player_id ?? user.playerId ?? '';
  const value = String(raw).trim();
  if (value) return value;
  return String(user.username || '').trim();
}

type UiChatMessage = {
  id: string;
  nickname: string;
  initial: string;
  avatarUrl: string;
  flag: string;
  vipLevel: number;
  isAdmin: boolean;
  text: string;
  type: string;
  ts: number;
  mine: boolean;
  isWin: boolean;
  voiceUrl: string;
  voiceMimeType: string;
  duration: number;
};

function normalizeChatMessage(message: ChatMessage, user: PlayerUser | null): UiChatMessage {
  const playerId = user?.id == null ? '' : `u_${user.id}`;
  const mine = !!message.player_id && message.player_id === playerId;
  const rawUser = message.user || {};
  const currentUserFlag = String((user as (PlayerUser & { flag?: string }) | null)?.flag || '');
  const nickname = mine ? userName(user) : (message.nickname || rawUser.username || rawUser.name || 'Visitante');
  const initial = String((mine ? user?.initial : rawUser.initial) || nickname[0] || 'U').toUpperCase();
  // Own avatar comes from the auth user (always available); other users' avatar
  // depends on the backend adding avatar fields to the chat payload (todo) — until
  // then rawUser has none and this resolves to '' → initial fallback.
  const avatarUrl = mine ? resolveAvatarUrl(user) : resolveAvatarUrl(rawUser);
  return {
    id: String(message.id || `${message.player_id || 'anon'}:${message.ts || Date.now()}:${message.text || ''}`),
    nickname,
    initial,
    avatarUrl,
    flag: String((mine ? currentUserFlag : rawUser.flag) || ''),
    vipLevel: Number((mine ? userVipLevel(user) : (rawUser.vipLevel ?? rawUser.vip_level ?? rawUser.level)) || 1),
    isAdmin: !!(rawUser.isAdmin || rawUser.is_admin),
    text: message.type === 'voice' ? '[voice]' : (message.text || ''),
    type: message.type || 'text',
    ts: Number(message.ts || 0),
    mine,
    isWin: !!(message as ChatMessage & { isWin?: boolean; is_win?: boolean }).isWin || !!(message as ChatMessage & { is_win?: boolean }).is_win,
    voiceUrl: message.voice_url || '',
    voiceMimeType: message.mime_type || message.mimeType || '',
    duration: Number(message.duration_sec || 0),
  };
}

function ChatMessageRow({ message }: { message: UiChatMessage }) {
  const token = useAuthStore((state) => state.token);
  const showToast = useAppStore((state) => state.showToast);
  const t = useI18nStore((state) => state.t);
  const [playing, setPlaying] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef('');

  useEffect(() => () => {
    stopVoicePlayback(audioRef, objectUrlRef, setPlaying);
  }, []);

  async function playVoice() {
    if (!message.voiceUrl) return;
    if (message.voiceMimeType && !canPlayVoiceMimeType(message.voiceMimeType)) {
      showToast(t('voiceFormatUnsupported'), 'warn');
      return;
    }
    if (playing) {
      stopVoicePlayback(audioRef, objectUrlRef, setPlaying);
      return;
    }
    stopVoicePlayback(audioRef, objectUrlRef, setPlaying);
    try {
      const response = await fetch(message.voiceUrl, {
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error('VOICE_UNAVAILABLE');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      objectUrlRef.current = url;
      audioRef.current = audio;
      audio.onended = () => stopVoicePlayback(audioRef, objectUrlRef, setPlaying);
      audio.onerror = () => stopVoicePlayback(audioRef, objectUrlRef, setPlaying);
      setPlaying(true);
      await audio.play();
    } catch {
      stopVoicePlayback(audioRef, objectUrlRef, setPlaying);
      showToast(t('voiceUnavailable'), 'warn');
    }
  }

  if (message.type === 'system') {
    return <div className="flex justify-center"><div className="bubble bubble-system">{message.text}</div></div>;
  }
  if (message.type === 'bet-broadcast' || message.type === 'broadcast') {
    return <div className="flex justify-center"><div className={`bubble bubble-broadcast${message.isWin ? ' win' : ''}`}>{message.text}</div></div>;
  }

  return (
    <div className={`chat-row ${message.mine ? 'mine' : ''}`} data-voice-message-react={message.type === 'voice' ? 'true' : undefined}>
      {message.avatarUrl && !avatarError ? (
        <img className="chat-avatar chat-avatar-img" src={message.avatarUrl} alt="" onError={() => setAvatarError(true)} />
      ) : (
        <span className="chat-avatar">{message.initial}</span>
      )}
      <div className="chat-col">
        <div className="chat-meta">
          <VipLevelBadge level={message.vipLevel} />
          <span className="uname">{message.nickname}</span>
          {message.flag ? <span className="text-[10px]">{message.flag}</span> : null}
          {message.isAdmin ? <span className="chip bg-gold-500/20 text-gold-400 border-gold-500/30">{t('chatTeamBadge')}</span> : null}
        </div>
        {message.type === 'voice' ? (
          <button className={`bubble voice-bubble ${message.mine ? 'bubble-me' : 'bubble-them'}`} type="button" onClick={playVoice}>
            {playing ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
            <span className="waveform" aria-hidden="true">
              {[6, 10, 14, 8, 12, 10, 14, 6].map((h, i) => <i key={i} style={{ height: h }} />)}
            </span>
            <em className="font-mono text-xs">{formatVoiceDuration(message.duration)}</em>
          </button>
        ) : (
          <div className={`bubble ${message.type === 'admin' || message.isAdmin ? 'bubble-admin' : message.mine ? 'bubble-me' : 'bubble-them'}`}>{message.text}</div>
        )}
        <span className="chat-time">{formatChatTime(message.ts)}</span>
      </div>
    </div>
  );
}

function formatChatTime(ts: number) {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatVoiceDuration(seconds: number) {
  const safeSeconds = Math.max(0, Math.round(seconds || 0));
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function chatErrorMessage(error: Error, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const message = error.message || '';
  if (message.includes('CHAT_MUTED')) return t('chatMuted');
  if (message.includes('MESSAGE_BLOCKED')) return t('messageBlocked');
  if (message.includes('MESSAGE_TOO_LONG')) return t('messageTooLong');
  if (message.includes('UNAUTHORIZED')) return t('enterToChat');
  return t('chatSendError');
}

function voiceErrorMessage(error: unknown, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const message = error instanceof Error ? error.message : '';
  if (message.includes('CHAT_MUTED')) return t('chatMuted');
  if (message.includes('VOICE_TOO_BIG')) return t('voiceTooBig');
  if (message.includes('INVALID_VOICE_TYPE')) return t('voiceFormatUnsupported');
  if (message.includes('INVALID_VOICE_DURATION')) return t('voiceTooShort');
  if (message.includes('UNAUTHORIZED')) return t('enterToChat');
  return t('voiceUnavailable');
}

function preferredVoiceMimeType() {
  const candidates = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
  return candidates.find((type) => {
    try {
      return window.MediaRecorder?.isTypeSupported(type) && canPlayVoiceMimeType(type);
    } catch {
      return false;
    }
  }) || '';
}

function canPlayVoiceMimeType(type: string) {
  if (!type) return true;
  try {
    const audio = document.createElement('audio');
    return !!audio.canPlayType(type.split(';', 1)[0] || type);
  } catch {
    return true;
  }
}

function stopVoicePlayback(
  audioRef: { current: HTMLAudioElement | null },
  objectUrlRef: { current: string },
  setPlaying: (value: boolean) => void,
) {
  try {
    audioRef.current?.pause();
  } catch {
    // Browser audio cleanup can throw when the element is already detached.
  }
  if (objectUrlRef.current) {
    try {
      URL.revokeObjectURL(objectUrlRef.current);
    } catch {
      // Ignore revoked object URL races.
    }
  }
  audioRef.current = null;
  objectUrlRef.current = '';
  setPlaying(false);
}

type NormalizedBet = {
  id: string;
  round: number;
  roomCode: string;
  game: string;
  tab: string;
  pick: string;
  amount: number;
  currency: string;
  result: 'pending' | 'win' | 'lose' | 'cancelled';
  payout: number;
  time: number;
};

function normalizeBet(raw: BetItem): NormalizedBet {
  const resultRaw = String(raw.result || raw.status || 'pending');
  const result = resultRaw === 'won' ? 'win' : resultRaw === 'lost' ? 'lose' : resultRaw;
  return {
    id: String(raw.id || `${raw.round_number || raw.round}:${raw.tab}:${raw.pick}:${raw.placed_at || raw.time}`),
    round: Number(raw.round_number || raw.round || 0),
    roomCode: raw.room_code || raw.room_id || '',
    game: raw.game || 'lastdigit',
    tab: raw.tab || '',
    pick: String(raw.pick ?? ''),
    amount: Number(raw.amount || 0),
    currency: raw.currency || 'BRL',
    result: result === 'win' || result === 'lose' || result === 'cancelled' ? result : 'pending',
    payout: Number(raw.payout || 0),
    time: Number(raw.placed_at || raw.time || 0),
  };
}

/** Local date + time of a bet, e.g. "06/08, 17:04". Formats in the viewer's own
 *  timezone; the locale only fixes day-before-month ordering. */
function betDateTime(tsSec: number): string {
  if (!tsSec) return '';
  return new Date(Number(tsSec) * 1000).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function MyBetRow({ bet, showRound = false }: { bet: NormalizedBet; showRound?: boolean }) {
  const t = useI18nStore((state) => state.t);
  // Grouped by round, the card header already carries the round and the time, so
  // repeating them on every bet inside it says nothing — all bets in a round sit
  // in the same five-minute window. Only the flat list, which has no header to
  // group under, needs each row to identify itself. The bet id is gone from both:
  // it is the global bets sequence, the same figure already dropped from the
  // wallet statement because it leaks platform throughput and identifies nothing
  // a player can act on.
  const meta = showRound
    ? [`#${roundSeqFromRound({ starts_at: bet.time }) || bet.round}`, betDateTime(bet.time)].filter(Boolean).join(' · ')
    : '';
  return (
    <div className={`my-bet-row result-${bet.result} my-bet-row-legacy`}>
      <span className={`my-bet-result-icon ${bet.result}`} aria-hidden="true">
        {bet.result === 'win' ? <Check size={14} /> : bet.result === 'lose' ? <X size={14} /> : <History size={14} />}
      </span>
      <div>
        <strong>{t(gameNameKey(bet.game as GameCode))} <em className="my-bet-pick-chip">{pickLabel(bet.game, bet.tab, bet.pick, t)}</em></strong>
        {meta ? <span>{meta}</span> : null}
      </div>
      <div>
        <strong>{fmtCurrency(bet.amount, bet.currency)}</strong>
        <span className={bet.result === 'win' ? 'text-emerald-400' : bet.result === 'lose' ? 'text-rose-400' : 'text-gold-400'}>{bet.result === 'pending' ? '—' : betResultText(bet)}</span>
      </div>
    </div>
  );
}

function BasketSheet({
  room,
  roundNumber,
  displayRound,
  phase,
  bets,
  onPlaceAnother,
  onClose,
}: {
  room: Room;
  roundNumber: number;
  displayRound?: string;
  phase: RoundPhase;
  bets: NormalizedBet[];
  onPlaceAnother: () => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const fiatCurrency = useCountryStore((state) => state.country.fiatCurrency);
  const stakeLines = currencyLines([
    { currency: fiatCurrency, amount: sumBets(bets.filter((bet) => bet.currency === fiatCurrency), 'amount') },
    { currency: 'USDT', amount: sumBets(bets.filter((bet) => bet.currency === 'USDT'), 'amount') },
  ]);
  // Per currency, and per DRAW: the round lands on one outcome, so the basket's
  // ceiling is the best single draw for the whole set — not the sum of every
  // row's own ceiling, which no draw can pay when the picks exclude each other.
  const estimatedMaxWinLines = currencyLines([
    { currency: fiatCurrency, amount: batchWinRange(bets.filter((bet) => bet.currency === fiatCurrency), room.game).max },
    { currency: 'USDT', amount: batchWinRange(bets.filter((bet) => bet.currency === 'USDT'), room.game).max },
  ]);

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="my-bets-sheet basket-sheet-react" role="dialog" aria-modal="true" aria-label={t('basketTitle').replace('%round%', String(displayRound || roundNumber || '---'))} onMouseDown={(event) => event.stopPropagation()} data-basket-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('basketTitle').replace('%round%', String(displayRound || roundNumber || '---'))}</h2>
            <p>{room.label} · {t(gameNameKey(room.game))} · {t('basketBetsCount').replace('%n%', String(bets.length))} · <span style={{ color: ROUND_TONE_COLOR[phase.tone] }}>{phase.label} {phase.value}</span></p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="basket-list-react">
          {bets.length === 0 ? <p className="empty-note basket-empty-react">{t('basketEmpty')}</p> : null}
          {bets.map((bet, index) => (
            <article className="basket-row-react" key={bet.id}>
              <span>#{index + 1}</span>
              <div>
                <strong>{pickLabel(bet.game, bet.tab, bet.pick, t)}</strong>
                <small>{fmtCurrency(bet.amount, bet.currency)} · {t('basketMaxWin')} {fmtCurrency(estimateMaxWin(bet, room.game), bet.currency)}</small>
              </div>
              <span title={t('betLockedTip')}><Lock size={16} aria-hidden="true" /></span>
            </article>
          ))}
        </div>

        {bets.length > 0 ? (
          <footer className="basket-footer-react">
            <div>
              <span>{t('basketStake')}</span>
              <strong>{stakeLines.join(' · ') || '-'}</strong>
            </div>
            <div>
              <span>{t('basketMaxWin')}</span>
              <strong>{estimatedMaxWinLines.length ? estimatedMaxWinLines.join(' · ') : '-'}</strong>
            </div>
            <button className="primary-button" type="button" onClick={onPlaceAnother}>
              {t('placeAnother')}
            </button>
          </footer>
        ) : null}
      </section>
    </div>
  );
}

// Signed = NET, same as the bet-history sheet and the settlement card. A won
// row used to print its gross payout with a `+`, so a R$ 2 bet at ×2 read
// "+R$ 4" here and "Resultado +R$ 2" on the settlement card for the same round.
function betResultText(bet: NormalizedBet) {
  const net = betNetAmount(bet);
  if (net === null) return 'pending';
  if (bet.result === 'cancelled') return 'cancelled';
  return signedNet(net, (abs) => fmtCurrency(abs, bet.currency));
}

function sumBets(bets: NormalizedBet[], key: 'amount' | 'payout') {
  return bets.reduce((sum, bet) => sum + Number(bet[key] || 0), 0);
}



function currencyLines(items: Array<{ currency: string; amount: number }>) {
  return items
    .filter((item) => Number(item.amount || 0) > 0)
    .map((item) => fmtCurrency(item.amount, item.currency));
}

function estimateMaxWin(bet: NormalizedBet, fallbackGame: GameCode) {
  const game = (bet.game === 'lastdigit' || bet.game === 'sicbo' || bet.game === 'roulette') ? bet.game : fallbackGame;
  // The max-win multiplier is authoritative per tab/pick (legacy _pickMaxOdds) —
  // reading it off the sublabel string missed tabs whose odds live in the `odds`
  // field (Single, Combo, Big/Small…), which is why a Sicbo Single showed ×2
  // instead of its ×6 top payout (3 matching dice).
  return Number(bet.amount || 0) * defaultOddsForBet(game, bet.tab, String(bet.pick));
}

/**
 * The one digit that pays the lower multiplier, when a batch has exactly one
 * bet that has such a digit. Only Last-Digit odd/even and big/small do: the
 * boundary digits are 0 (Even, Small) and 9 (Odd, Big). Mixed batches get no
 * digit — there is no single answer — and fall back to the generic note.
 */
function edgeDigitFor(game: GameCode, bets: Array<{ tab: string; pick: string }>): string | undefined {
  if (game !== 'lastdigit' || bets.length !== 1) return undefined;
  const [bet] = bets;
  if (bet.tab !== 'odd-even' && bet.tab !== 'big-small') return undefined;
  return bet.pick === 'odd' || bet.pick === 'big' ? '9' : '0';
}

type AnalyticsTab = 'trend' | 'cold' | 'road' | 'fair';
type RoadKind = 'bigsmall' | 'oddeven' | 'redblack' | 'hilo';

type AnalyticsRound = {
  round: number;
  cutoffTs: number;
  lastdigit: number | null;
  dice: number[];
  sicboSum: number | null;
  sicboTriple: number | null;
  roulette: number | null;
  rouletteColor: 'red' | 'black' | 'green' | null;
};

async function fetchAnalyticsRounds() {
  const responses = await Promise.all([analyticsDate(0), analyticsDate(-1)].map((date) => fetchArchiveRounds(date, 288)));
  return normalizeAnalyticsRounds(responses.flatMap((response) => response.rounds || []));
}

/**
 * How far back the Missing tab is allowed to look.
 *
 * "Not once in the last N rounds" is only a real number if N is big enough to
 * contain the gap. Two days (576 rounds) is not: a specific Sic Bo triple comes
 * up once in 216 rounds on average, and its gap runs past 576 often enough that
 * every unfound tile printed the same window size — three tiles reading exactly
 * "288" is a cap wearing a number's clothes, not a measurement.
 *
 * Ten days is ~2880 rounds, which contains a 1-in-216 gap 99.99% of the time.
 * Days are fetched oldest-last and the walk stops as soon as every tracked
 * outcome has been seen, so the common case still costs one or two requests.
 */
const MISSING_MAX_DAYS = 10;

async function fetchMissingHistory(game: GameCode, seed: AnalyticsRound[]): Promise<{ rounds: AnalyticsRound[]; complete: boolean }> {
  const day = async (offset: number) => {
    const response = await fetchArchiveRounds(analyticsDate(offset), ROUNDS_PER_DAY);
    return normalizeAnalyticsRounds(response.rounds || []).filter((round) => hasAnalyticsValue(round, game));
  };
  // The panel already holds today + yesterday, which covers every market except
  // the six specific triples, so the usual case adds no requests at all.
  const rounds = seed.length ? [...seed] : (await Promise.all([day(0), day(-1)])).flat();
  for (let offset = -2; offset > -MISSING_MAX_DAYS; offset -= 1) {
    if (allColdOutcomesSeen(game, rounds)) return { rounds, complete: true };
    rounds.push(...await day(offset));
  }
  return { rounds, complete: allColdOutcomesSeen(game, rounds) };
}

/**
 * Every outcome the Missing tab shows has appeared at least once, so every
 * count on screen is a real gap rather than the size of what we loaded.
 * Non-Sic Bo reads the same bucket list the grid renders; Sic Bo's sections are
 * hand-built, so its predicates are repeated here to match them.
 */
function allColdOutcomesSeen(game: GameCode, rounds: AnalyticsRound[]): boolean {
  const has = (predicate: (round: AnalyticsRound) => boolean) => rounds.some(predicate);
  if (game !== 'sicbo') return analyticsBuckets(game).every((bucket) => has(bucket.matches));
  const faces = [1, 2, 3, 4, 5, 6];
  const sums = Array.from({ length: 14 }, (_, index) => index + 4);
  return faces.every((n) => has((round) => round.dice.includes(n)))
    && faces.every((n) => has((round) => round.dice.filter((value) => value === n).length >= 2))
    && faces.every((n) => has((round) => round.sicboTriple === n))
    && sums.every((sum) => has((round) => round.sicboSum === sum));
}

async function fetchRoomHistoryRounds() {
  const responses = await Promise.all([analyticsDate(0), analyticsDate(-1)].map((date) => fetchArchiveRounds(date, 288)));
  return responses
    .flatMap((response) => response.rounds || [])
    .filter((round) => round.games && round.status !== 'VOID')
    .sort((a, b) => Number(b.cutoff_ts || b.ends_at || b.starts_at || 0) - Number(a.cutoff_ts || a.ends_at || a.starts_at || 0));
}

function AnalyticsSheet({
  game,
  rounds,
  loading,
  error,
  onRetry,
  onClose,
  onBet,
  canBet,
  embedded = false,
  onQuickBet,
  quickBetLocked = false,
}: {
  game: GameCode;
  rounds: AnalyticsRound[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onClose: () => void;
  onBet: () => void;
  canBet: boolean;
  embedded?: boolean;
  /** Deep-link out of a Missing tile into that tile's own market. */
  onQuickBet?: (target: BetTarget) => void;
  /** Round no longer accepts bets: tiles stay readable but stop being buttons. */
  quickBetLocked?: boolean;
}) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const tabId = useId();
  const analyticsBodyRef = useRef<HTMLDivElement | null>(null);
  const [tab, setTab] = useState<AnalyticsTab>('trend');
  const [roadKind, setRoadKind] = useState<RoadKind>(() => game === 'roulette' ? 'redblack' : 'bigsmall');
  const loadedRounds = useMemo(() => rounds.filter((round) => hasAnalyticsValue(round, game)), [game, rounds]);
  const gameRounds = useMemo(() => loadedRounds.slice(0, ROUNDS_PER_DAY), [loadedRounds]);
  // Missing counts a gap, so it needs however much history the gap spans — the
  // other tabs only ever read the recent window. It starts from what is already
  // loaded and walks further back only if some outcome is still unaccounted for.
  const missingQuery = useQuery({
    queryKey: ['room-missing-history', game, loadedRounds.length > 0],
    queryFn: () => fetchMissingHistory(game, loadedRounds),
    enabled: tab === 'cold',
    staleTime: 60_000,
  });
  const sample = gameRounds.slice(0, 144);
  const tabs: Array<{ key: AnalyticsTab; label: string }> = [
    { key: 'trend', label: t('analyticsTabTrend') },
    { key: 'cold', label: t('analyticsTabCold') },
    { key: 'road', label: t('analyticsTabRoad') },
    { key: 'fair', label: t('analyticsTabFair') },
  ];

  useEffect(() => {
    setRoadKind(game === 'roulette' ? 'redblack' : 'bigsmall');
  }, [game]);

  useEffect(() => {
    analyticsBodyRef.current?.scrollTo({ top: 0 });
  }, [game, tab]);

  const inner = (
    <>
        <nav className="analytics-tabs-react" role="tablist" aria-label={t('analyticsTitle')}>
          {tabs.map((item) => (
            <button
              key={item.key}
              id={`${tabId}-${item.key}-tab`}
              type="button"
              role="tab"
              aria-controls={`${tabId}-panel`}
              aria-selected={tab === item.key}
              tabIndex={tab === item.key ? 0 : -1}
              className={tab === item.key ? 'active' : ''}
              onClick={() => setTab(item.key)}
              onKeyDown={(event) => handleTablistKey(event, tabs.map(({ key }) => key), item.key, setTab, (next) => `${tabId}-${next}-tab`)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div
          id={`${tabId}-panel`}
          className="analytics-body-react"
          ref={analyticsBodyRef}
          role="tabpanel"
          aria-labelledby={`${tabId}-${tab}-tab`}
          aria-busy={loading || undefined}
        >
          {loading ? <AnalyticsLoading game={game} tab={tab} /> : null}
          {!loading && error ? <AnalyticsState icon={BarChart3} title={t('analyticsLoadError')} actionLabel={t('retry')} onAction={onRetry} /> : null}
          {!loading && !error && sample.length === 0 ? <AnalyticsState icon={BarChart3} title={t('analyticsEmpty')} /> : null}
          {!loading && !error && sample.length > 0 ? (
            <>
            {tab === 'trend' ? <AnalyticsTrend game={game} rounds={sample} /> : null}
            {tab === 'cold' ? (
              <AnalyticsCold
                game={game}
                rounds={missingQuery.data?.rounds?.length ? missingQuery.data.rounds : gameRounds}
                bounded={missingQuery.data ? !missingQuery.data.complete : true}
                onQuickBet={onQuickBet}
                quickBetLocked={quickBetLocked}
              />
            ) : null}
            {tab === 'road' ? <AnalyticsRoad game={game} rounds={sample} roadKind={roadKind} onRoadKind={setRoadKind} onQuickBet={onQuickBet} quickBetLocked={quickBetLocked} /> : null}
            {tab === 'fair' ? <AnalyticsFairness game={game} rounds={gameRounds} /> : null}
            </>
          ) : null}
          {/* Inside the scroller, directly under the chart it qualifies. It used
              to be dropped whenever the panel was embedded and smuggled into the
              bet bar instead — a responsible-gaming line has no business sitting
              beside the button that places the bet, and out here it also sat
              outside the body's padding, flush against both screen edges. */}
          {!loading && !error ? <footer className="analytics-footer-react">{t('analyticsDisclaimer')}</footer> : null}
        </div>
    </>
  );
  if (embedded) return <div className="analytics-embedded-react">{inner}</div>;
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="analytics-sheet-react" role="dialog" aria-modal="true" aria-label={t('analyticsTitle')} onMouseDown={(event) => event.stopPropagation()} data-analytics-react="room">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('analyticsTitle')}</h2>
            <p>{t(gameNameKey(game))} · {sample.length} {t('analyticsRounds')}</p>
          </div>
          <div className="sheet-header-actions">
            {canBet ? <button className="ghost-action" type="button" onClick={onBet}>{t('analyticsToBet')}</button> : null}
            <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </header>
        {inner}
      </section>
    </div>
  );
}

function AnalyticsState({ icon: Icon, title, actionLabel, onAction }: { icon: typeof BarChart3; title: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="analytics-state-react">
      <Icon size={26} aria-hidden="true" />
      <strong>{title}</strong>
      {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}</button> : null}
    </div>
  );
}

function AnalyticsLoading({ game, tab }: { game: GameCode; tab: AnalyticsTab }) {
  const t = useI18nStore((state) => state.t);
  if (tab === 'trend') return <AnalyticsTrendLoading game={game} />;
  const count = tab === 'cold' ? 15 : tab === 'road' ? 28 : 6;
  return (
    <div className={`analytics-loading-react analytics-loading-${tab}`} role="status" aria-label={t('loading')}>
      <div className="analytics-loading-toolbar" aria-hidden="true">
        <i />
        <i />
      </div>
      <div className="analytics-loading-grid" aria-hidden="true">
        {Array.from({ length: count }, (_, index) => <i key={index} />)}
      </div>
    </div>
  );
}

function AnalyticsTrendLoading({ game }: { game: GameCode }) {
  const t = useI18nStore((state) => state.t);
  const axis = trendAxis(game);
  const chartH = trendChartHeight();
  const L = { padT: 18, padB: 22, chartH, gutterW: 28 };
  const totalH = L.padT + L.chartH + L.padB;
  const totalW = 360;
  const midY = L.padT + ((axis.max - axis.threshold) / (axis.max - axis.min || 1)) * L.chartH;
  const yRatios = [0.68, 0.45, 0.58, 0.28, 0.36, 0.72, 0.61, 0.42, 0.52, 0.24, 0.31];
  const pointX = (index: number) => 12 + index * ((totalW - 24) / (yRatios.length - 1));
  const pointY = (ratio: number) => L.padT + ratio * L.chartH;
  return (
    <section className="analytics-panel-react analytics-trend-loading" role="status" aria-label={t('loading')}>
      <div className="trend-toolbar-react" aria-hidden="true">
        <div className="trend-legend-react">
          {trendLegendItems(game, t).map((item) => (
            <span key={item.cls}><i className={`legend-dot ${item.cls}`} />{item.label}</span>
          ))}
          <span className="trend-legend-dash"><i className="legend-dash" />{t('analyticsThreshold')}</span>
        </div>
        {/* Mirrors the loaded toolbar exactly — the direction caption is gone
            there, so it goes here too or the layout jumps when data lands. */}
      </div>
      <div className="trend-frame trend-loading-frame" aria-hidden="true">
        <div className="trend-yaxis">
          <svg width={L.gutterW} height={totalH} viewBox={`0 0 ${L.gutterW} ${totalH}`}>
            {axis.ticks.map((tick) => {
              const y = L.padT + ((axis.max - tick) / (axis.max - axis.min || 1)) * L.chartH;
              return <text key={tick} x={L.gutterW - 5} y={y + 3} textAnchor="end" className="trend-axis">{tick}</text>;
            })}
            <line x1={L.gutterW - 14} y1={midY} x2={L.gutterW} y2={midY} className="trend-threshold" strokeDasharray="3 3" />
          </svg>
        </div>
        <div className="trend-loading-plot">
          <svg width="100%" height={totalH} viewBox={`0 0 ${totalW} ${totalH}`}>
            <rect x={0} y={L.padT} width={totalW} height={midY - L.padT} className="trend-zone-big" />
            <rect x={0} y={midY} width={totalW} height={L.padT + L.chartH - midY} className="trend-zone-small" />
            <line x1={0} y1={midY} x2={totalW} y2={midY} className="trend-threshold" strokeDasharray="5 4" />
            {[0.25, 0.5, 0.75].map((ratio) => (
              <line key={ratio} x1={ratio * totalW} y1={L.padT} x2={ratio * totalW} y2={L.padT + L.chartH} className="trend-hour-grid" />
            ))}
            <g className="trend-loading-ink">
              <polyline points={yRatios.map((ratio, index) => `${pointX(index)},${pointY(ratio)}`).join(' ')} className="trend-loading-line" />
              {yRatios.map((ratio, index) => <circle key={index} cx={pointX(index)} cy={pointY(ratio)} r={6} className="trend-loading-dot" />)}
            </g>
          </svg>
        </div>
      </div>
    </section>
  );
}

// Mouse users cannot swipe an overflow area — give horizontal scrollers
// drag-to-scroll (touch keeps native swipe; taps under 5px still click).
function useMouseDragScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let startX = 0;
    let startLeft = 0;
    let active = false;
    let moved = false;
    const down = (event: globalThis.PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      active = true;
      moved = false;
      startX = event.clientX;
      startLeft = el.scrollLeft;
    };
    const move = (event: globalThis.PointerEvent) => {
      if (!active) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 5) moved = true;
      if (moved) el.scrollLeft = startLeft - dx;
    };
    const up = () => {
      active = false;
      window.setTimeout(() => { moved = false; }, 0);
    };
    const click = (event: globalThis.MouseEvent) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    el.addEventListener('click', click, true);
    return () => {
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      el.removeEventListener('click', click, true);
    };
  }, []);
  return ref;
}

// Legacy-parity trend chart: sticky Y-axis gutter, horizontally scrollable plot
// (auto-scrolled to newest), hour gridlines, #seq labels, gold streak rings (≥4 same side).
function AnalyticsTrend({ game, rounds }: { game: GameCode; rounds: AnalyticsRound[] }) {
  const t = useI18nStore((state) => state.t);
  // Scroll to newest ONCE per open/game — doing it in a ref callback re-ran on
  // every parent re-render (countdown ticks each second) and yanked the user
  // back to the right edge, making the chart feel unscrollable.
  const trendScrollRef = useMouseDragScroll<HTMLDivElement>();
  useEffect(() => {
    if (trendScrollRef.current) trendScrollRef.current.scrollLeft = trendScrollRef.current.scrollWidth;
  }, [game, trendScrollRef]);
  const axis = trendAxis(game);
  const chartH = trendChartHeight();
  const L = { padL: 4, padR: 8, padT: 18, padB: 22, chartH, gutterW: 28 };
  const STEP = game === 'lastdigit' ? 15 : 16;
  const DOT = game === 'lastdigit' ? 12 : 14;
  const points = [...rounds].reverse()
    .map((round) => {
      const value = trendValue(round, game);
      if (value == null) return null;
      let side: string;
      let streakSide: string | null;
      if (game === 'sicbo') {
        side = round.sicboTriple ? 'triple' : value >= 11 ? 'big' : 'small';
        streakSide = value >= 11 ? 'big' : 'small';
      } else if (game === 'roulette') {
        side = round.rouletteColor || 'green';
        streakSide = value === 0 ? null : value >= 19 ? 'hi' : 'lo';
      } else {
        side = value >= 5 ? 'big' : 'small';
        streakSide = side;
      }
      return { value, side, streakSide, cutoffTs: round.cutoffTs };
    })
    .filter((point): point is { value: number; side: string; streakSide: string | null; cutoffTs: number } => point != null);

  const totalW = L.padL + points.length * STEP + L.padR;
  const totalH = L.padT + L.chartH + L.padB;
  const yFor = (value: number) => L.padT + ((axis.max - value) / (axis.max - axis.min || 1)) * L.chartH;
  const xFor = (index: number) => L.padL + (index + 0.5) * STEP;
  const midY = yFor(axis.threshold);

  // Gold ring on runs of ≥4 same side — "a streak is forming" signal (legacy PM rule)
  const STREAK_MIN = 4;
  const streakSet = new Set(game === 'roulette' ? ['hi', 'lo'] : ['big', 'small']);
  const isStreak = new Array(points.length).fill(false);
  let runStart = 0;
  let runSide: string | null = points[0]?.streakSide ?? null;
  for (let i = 1; i <= points.length; i++) {
    const cur = i < points.length ? points[i].streakSide : undefined;
    if (cur !== runSide || i === points.length) {
      if (runSide != null && streakSet.has(runSide) && i - runStart >= STREAK_MIN) {
        for (let j = runStart; j < i; j++) isStreak[j] = true;
      }
      runStart = i;
      runSide = cur ?? null;
    }
  }

  const seqLabel = (cutoffTs: number) => `#${dailySeq({ cutoff_ts: cutoffTs })}`;
  const legend = trendLegendItems(game, t);

  return (
    <section className="analytics-panel-react">
      <div className="trend-toolbar-react">
        <div className="trend-legend-react">
          {legend.map((item) => (
            <span key={item.cls}><i className={`legend-dot ${item.cls}`} />{item.label}</span>
          ))}
          <span className="trend-legend-dash"><i className="legend-dash" />{t('analyticsThreshold')}</span>
        </div>
        {/* No "older → newer" caption: the hour gridlines and the #seq label under
            the newest point already say which way time runs, and the caption was
            squeezing the legend until "Big / small" truncated. Direction stays in
            the chart's own aria-label for anyone who cannot see either cue. */}
      </div>
      <div className="trend-frame">
        <div className="trend-yaxis">
          <svg width={L.gutterW} height={totalH} viewBox={`0 0 ${L.gutterW} ${totalH}`} aria-hidden="true">
            {axis.ticks.map((tick) => (
              <text key={tick} x={L.gutterW - 5} y={yFor(tick) + 3} textAnchor="end" className="trend-axis">{tick}</text>
            ))}
            <line x1={L.gutterW - 14} y1={midY} x2={L.gutterW} y2={midY} className="trend-threshold" strokeDasharray="3 3" />
          </svg>
        </div>
        <div
          className="trend-scroll"
          ref={trendScrollRef}
          role="region"
          tabIndex={0}
          aria-label={`${t('analyticsTrendScrollLabel')} ${t('analyticsDirectionLabel')}`}
          onKeyDown={(event) => {
            const scrollBy = Math.max(90, Math.round(event.currentTarget.clientWidth * 0.75));
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              event.currentTarget.scrollBy({ left: -scrollBy });
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              event.currentTarget.scrollBy({ left: scrollBy });
            } else if (event.key === 'Home') {
              event.preventDefault();
              event.currentTarget.scrollTo({ left: 0 });
            } else if (event.key === 'End') {
              event.preventDefault();
              event.currentTarget.scrollTo({ left: event.currentTarget.scrollWidth });
            }
          }}
        >
          <svg width={totalW} height={totalH} viewBox={`0 0 ${totalW} ${totalH}`} role="img" aria-label={t('analyticsTrendChartLabel').replace('%n%', String(points.length))}>
            <rect x={0} y={L.padT} width={totalW} height={midY - L.padT} className="trend-zone-big" />
            <rect x={0} y={midY} width={totalW} height={L.padT + L.chartH - midY} className="trend-zone-small" />
            <line x1={0} y1={midY} x2={totalW} y2={midY} className="trend-threshold" strokeDasharray="5 4" />
            {points.map((point, index) => {
              // Hour gridlines in the viewer's own clock, like every other time
              // on this screen. This used to shift by a flat three hours, so a
              // phone outside UTC-3 got "08h" marks standing over 19:00.
              // Testing the local minute rather than a modulo also survives the
              // half-hour offsets some markets use.
              if (!point.cutoffTs) return null;
              const at = new Date(point.cutoffTs * 1000);
              if (at.getMinutes() !== 0 || at.getSeconds() !== 0) return null;
              const hour = at.getHours();
              return (
                <g key={`h:${point.cutoffTs}`}>
                  <line x1={xFor(index)} y1={L.padT} x2={xFor(index)} y2={L.padT + L.chartH} className="trend-hour-grid" />
                  <text x={xFor(index)} y={L.padT - 4} textAnchor="middle" className="trend-hour-label">{String(hour).padStart(2, '0')}h</text>
                </g>
              );
            })}
            <polyline points={points.map((point, index) => `${xFor(index)},${yFor(point.value)}`).join(' ')} className="trend-line" />
            {points.map((point, index) => {
              const longLabel = String(point.value).length >= 2;
              const fontSize = Math.max(8, Math.round(DOT * (longLabel ? 0.46 : 0.55)));
              return (
                <g key={`${point.cutoffTs}:${index}`} className={`trend-dot trend-pt-${point.side}${isStreak[index] ? ' trend-dot-streak' : ''}`} transform={`translate(${xFor(index)},${yFor(point.value)})`}>
                  <circle r={DOT / 2} />
                  <text y={fontSize / 3} textAnchor="middle" fontSize={fontSize} className="trend-dot-num">{point.value}</text>
                </g>
              );
            })}
            {points.map((point, index) => {
              if (!(index === 0 || index === points.length - 1 || index % 12 === 0)) return null;
              return (
                <text key={`x:${index}`} x={xFor(index)} y={totalH - 6} textAnchor="middle" className="trend-axis">{seqLabel(point.cutoffTs)}</text>
              );
            })}
          </svg>
        </div>
      </div>
    </section>
  );
}

/**
 * One Missing cell. Every cell in every Missing section counts exactly one
 * market — the predicate it counts on is the same predicate the settlement
 * engine pays on — so the cell doubles as the way into that bet. It only
 * upgrades from a div to a button when the market resolves and the round is
 * still open; a locked round leaves the numbers readable and inert.
 */
function ColdTile({ game, target, onQuickBet, locked, betLabel, className, children }: {
  game: GameCode;
  target: BetTarget;
  onQuickBet?: (target: BetTarget) => void;
  locked?: boolean;
  betLabel: string;
  className: string;
  children: ReactNode;
}) {
  if (!onQuickBet || locked || !findBetOption(game, target)) {
    return <div className={className}>{children}</div>;
  }
  return (
    <button type="button" className={`${className} cold-tile-bet`} aria-label={betLabel} onClick={() => onQuickBet(target)}>
      {children}
    </button>
  );
}

function AnalyticsCold({ game, rounds, bounded = false, onQuickBet, quickBetLocked }: {
  game: GameCode;
  rounds: AnalyticsRound[];
  /** The history ran out before every outcome was found, so an unfound gap is
   *  "at least this many", not "exactly this many". */
  bounded?: boolean;
  onQuickBet?: (target: BetTarget) => void;
  quickBetLocked?: boolean;
}) {
  const t = useI18nStore((state) => state.t);
  const [sort, setSort] = useState<'natural' | 'missing'>('natural');
  const stats = useMemo(() => coldStats(game, rounds, sort), [game, rounds, sort]);
  const tappable = Boolean(onQuickBet) && !quickBetLocked;
  // Sic Bo carries its own section headings, so it skips the panel heading —
  // but it wants the sort just as much as the others: with 32 tiles across four
  // sections, "which of these is coldest" is not a question you answer by eye.
  const sortRow = (
    <div className="analytics-sort-row">
      <span className="analytics-sort-label"><BarChart3 size={12} aria-hidden="true" /> {t('analyticsSortBy')}</span>
      <div className="analytics-segmented-react">
        <button type="button" className={sort === 'natural' ? 'active' : ''} onClick={() => setSort('natural')}>{naturalSortLabel(game, t)}</button>
        <button type="button" className={sort === 'missing' ? 'active' : ''} onClick={() => setSort('missing')}>{t('analyticsSortMissing')}</button>
      </div>
    </div>
  );
  return (
    <section className={`analytics-panel-react analytics-cold-panel game-${game}`}>
      {game === 'sicbo' ? (
        // No panel heading to hang the sort off, so it shares a row with the tap
        // hint rather than claiming a line of its own — this sheet is already
        // fighting the bet dock for vertical space.
        <div className="analytics-cold-sort-solo">
          {tappable ? <p className="analytics-cold-tap-hint">{t('analyticsColdTapHint')}</p> : null}
          {sortRow}
        </div>
      ) : (
        <>
          <div className="analytics-panel-heading analytics-compact-heading">
            <div>
              <strong>{t('analyticsColdHeader')}</strong>
              <p>{coldSubtitle(game, t)}</p>
            </div>
            {sortRow}
          </div>
          {tappable ? <p className="analytics-cold-tap-hint">{t('analyticsColdTapHint')}</p> : null}
        </>
      )}
      {game === 'sicbo' ? (
        <SicboColdSections rounds={rounds} bounded={bounded} sort={sort} t={t} onQuickBet={onQuickBet} locked={quickBetLocked} />
      ) : (
        <div className={`analytics-cold-grid game-${game}`}>
          {stats.map((item) => (
            <ColdTile
              key={item.key}
              game={game}
              target={item.target}
              onQuickBet={onQuickBet}
              locked={quickBetLocked}
              betLabel={t('analyticsColdTapAria').replace('%pick%', item.label)}
              className={`analytics-cold-tile tier-${coldTier(item.missing, item.mean)}`}
            >
              <strong>{item.label}</strong>
              <span>{item.missing < 0 ? `${fmtInt(rounds.length)}${bounded ? '+' : ''}` : fmtInt(item.missing)}</span>
            </ColdTile>
          ))}
        </div>
      )}
    </section>
  );
}

function AnalyticsRoad({ game, rounds, roadKind, onRoadKind, onQuickBet, quickBetLocked }: {
  game: GameCode;
  rounds: AnalyticsRound[];
  roadKind: RoadKind;
  onRoadKind: (kind: RoadKind) => void;
  onQuickBet?: (target: BetTarget) => void;
  quickBetLocked?: boolean;
}) {
  const t = useI18nStore((state) => state.t);
  const tappable = Boolean(onQuickBet) && !quickBetLocked;
  // A side is only a bet button when the round is open and the market resolves.
  // History circles stay inert on purpose: a past result is not a market, and
  // the road is a horizontal drag-scroller — every cell would fight the drag.
  function sideButton(key: string, side: string, label: string, className: string, children: ReactNode, style?: CSSProperties, decorative?: boolean) {
    const target = tappable ? roadBetTarget(game, roadKind, side) : null;
    if (!target || !findBetOption(game, target)) {
      // Inert fallback keeps each call site's own semantics: the dashed arrows
      // are decoration, the legend is the key to the colours and must be read.
      return <span key={key} className={className} style={style} aria-hidden={decorative || undefined}>{children}</span>;
    }
    return (
      <button
        key={key}
        type="button"
        className={`${className} road-bet`}
        style={style}
        aria-label={t('analyticsColdTapAria').replace('%pick%', label)}
        onClick={() => onQuickBet?.(target)}
      >
        {children}
      </button>
    );
  }
  // Same snap-back trap as the trend chart: scroll to newest once, not on every render
  const roadScrollRef = useMouseDragScroll<HTMLDivElement>();
  useEffect(() => {
    if (roadScrollRef.current) roadScrollRef.current.scrollLeft = roadScrollRef.current.scrollWidth;
  }, [game, roadKind, roadScrollRef]);
  const cells = buildRoadCells(game, rounds, roadKind);
  const kinds: Array<{ key: RoadKind; label: string }> = game === 'roulette'
    ? [{ key: 'redblack', label: t('analyticsRoadRedBlack') }, { key: 'hilo', label: t('analyticsRoadHiLo') }]
    : game === 'lastdigit'
      ? [{ key: 'bigsmall', label: t('analyticsRoadBigSmall') }, { key: 'oddeven', label: t('analyticsRoadOddEven') }]
      : [{ key: 'bigsmall', label: t('analyticsRoadBigSmall') }];
  return (
    <section className={`analytics-panel-react analytics-road-panel game-${game}`}>
      <div className="analytics-road-toolbar">
        {kinds.length > 1 ? <div className="analytics-segmented-react" role="group" aria-label={t('analyticsTabRoad')}>
          {kinds.map((item) => (
            <button key={item.key} type="button" className={roadKind === item.key ? 'active' : ''} onClick={() => onRoadKind(item.key)}>
              {item.label}
            </button>
          ))}
        </div> : <strong className="analytics-road-kind">{kinds[0].label}</strong>}
        <div className="analytics-road-legend">
          {roadLegend(game, roadKind, t).map((entry) => sideButton(
            entry.side,
            entry.side,
            entry.label,
            'analytics-road-legend-item',
            <><i className={`legend-ring ring-${entry.tone}`} aria-hidden="true" /> {entry.label}</>,
          ))}
        </div>
      </div>
      <div
        className="analytics-road-scroll"
        ref={roadScrollRef}
        role="region"
        tabIndex={0}
        aria-label={`${t('analyticsTabRoad')}. ${t('analyticsDirectionLabel')}`}
        onKeyDown={(event) => {
          const scrollBy = Math.max(90, Math.round(event.currentTarget.clientWidth * 0.75));
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            event.currentTarget.scrollBy({ left: -scrollBy });
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            event.currentTarget.scrollBy({ left: scrollBy });
          } else if (event.key === 'Home') {
            event.preventDefault();
            event.currentTarget.scrollTo({ left: 0 });
          } else if (event.key === 'End') {
            event.preventDefault();
            event.currentTarget.scrollTo({ left: event.currentTarget.scrollWidth });
          }
        }}
      >
        <div className="analytics-road-grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, roadColumnCount(cells) + 2)}, 30px)` }}>
          {cells.map((cell) => (
            <span
              className={`analytics-road-cell side-${cell.side}`}
              style={{ gridColumn: cell.col + 1, gridRow: cell.row }}
              key={`${cell.round}:${cell.col}:${cell.row}`}
            >
              {cell.value}
            </span>
          ))}
          {cells.length > 0 ? (() => {
            // The two dashed cells are the only two things the next round can do
            // to the road: continue the streak (drops below while the column has
            // room, else trails right along the bottom) or break it (opens a
            // fresh column at the top). The break cell used to be grey, which
            // said "something else" — the one thing a two-sided road already
            // implies. It now wears the side a break would actually land on.
            const last = cells[cells.length - 1];
            const flipSide = oppositeRoadSide(last.side);
            const tailFull = last.row >= ROAD_MAX_ROW;
            const sameCol = tailFull ? last.col + 2 : last.col + 1;
            const sameRow = tailFull ? last.row : last.row + 1;
            return (
              <>
                {sideButton(
                  'hint-same',
                  last.side,
                  roadSideLabel(game, roadKind, last.side, t),
                  `analytics-road-cell road-hint-cell side-${last.side}`,
                  tailFull ? '→' : '↓',
                  { gridColumn: sameCol, gridRow: sameRow },
                  true,
                )}
                {sideButton(
                  'hint-flip',
                  flipSide,
                  roadSideLabel(game, roadKind, flipSide, t),
                  `analytics-road-cell road-hint-cell road-hint-flip side-${flipSide}`,
                  '→',
                  { gridColumn: last.col + 2, gridRow: 1 },
                  true,
                )}
              </>
            );
          })() : null}
        </div>
      </div>
    </section>
  );
}

function AnalyticsFairness({ game, rounds }: { game: GameCode; rounds: AnalyticsRound[] }) {
  // Legacy 6h/12h/24h fairness window selector (72/144/288 rounds).
  const [fairWindow, setFairWindow] = useState(144);
  rounds = rounds.slice(0, fairWindow);
  const t = useI18nStore((state) => state.t);
  const cards = fairnessCards(game, rounds, t);
  return (
    <section className={`analytics-panel-react analytics-fair-panel game-${game}`}>
      <div className="analytics-fair-toolbar">
        <div>
          <strong>{t('analyticsFairTitle')}</strong>
          <span>{t('analyticsFairRoundsCount').replace('%n%', String(rounds.length))}</span>
        </div>
        <div className="analytics-fair-range-react" role="group" aria-label={t('analyticsFairTitle')}>
          {[72, 144, 288].map((count) => (
            <button key={count} type="button" className={fairWindow === count ? 'active' : ''} onClick={() => setFairWindow(count)}>
              <span>{count / 12}h</span>
            </button>
          ))}
        </div>
      </div>
      <div className="analytics-fair-list">
        {cards.map((card) => (
          <article className="analytics-fair-card" key={card.title}>
            <header>
              <strong>{card.title}</strong>
              <span>{t('analyticsFairRoundsCount').replace('%n%', String(rounds.length))}</span>
            </header>
            <div className="analytics-fair-bar">
              {card.segments.map((segment) => (
                <span className={`seg-${segment.color}`} style={{ width: `${Math.max(2, segment.pct)}%` }} key={segment.key}>
                  {segment.pct >= 10 ? `${segment.pct.toFixed(1).replace('.', ',')}%` : ''}
                </span>
              ))}
            </div>
            <div className="analytics-fair-labels" style={{ gridTemplateColumns: `repeat(${card.segments.length}, minmax(0, 1fr))` }}>
              {card.segments.map((segment) => (
                <span key={segment.key}>
                  <b>{segment.label}</b>
                  <strong>{segment.count} · {segment.pct.toFixed(1).replace('.', ',')}%</strong>
                  <em>{t('analyticsFairTheory')} {String(Number(segment.theory.toFixed(2))).replace('.', ',')}%</em>
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function normalizeAnalyticsRounds(rounds: ArchiveRound[]): AnalyticsRound[] {
  return rounds
    .filter((round) => round.games && round.status !== 'VOID')
    .map((round) => {
      const dice = (round.games?.sicbo || []).map((item) => Number(item)).filter((item) => Number.isFinite(item));
      const roulette = round.games?.roulette == null ? null : Number(round.games.roulette);
      return {
        round: Number(round.round_number || 0),
        cutoffTs: Number(round.cutoff_ts || round.starts_at || 0),
        lastdigit: round.games?.lastdigit == null ? null : Number(round.games.lastdigit),
        dice,
        sicboSum: dice.length ? dice.reduce((sum, item) => sum + item, 0) : null,
        sicboTriple: dice.length === 3 && dice[0] === dice[1] && dice[1] === dice[2] ? dice[0] : null,
        roulette: roulette != null && Number.isFinite(roulette) ? roulette : null,
        rouletteColor: roulette != null && Number.isFinite(roulette) ? rouletteColor(roulette) : null,
      };
    })
    .filter((round) => round.round > 0)
    .sort((a, b) => b.cutoffTs - a.cutoffTs || b.round - a.round);
}

function hasAnalyticsValue(round: AnalyticsRound, game: GameCode) {
  if (game === 'sicbo') return round.sicboSum != null;
  if (game === 'roulette') return round.roulette != null;
  return round.lastdigit != null;
}

function analyticsDate(offset: number) {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + offset);
  return date.toISOString().slice(0, 10);
}

/**
 * Today / yesterday in the VIEWER's own calendar day.
 *
 * This used to subtract a hardcoded three hours, i.e. group by Brazil's day,
 * while the archive screen grouped the very same rounds by the device's day —
 * two different answers to "what is today" one tap apart. Day headings follow
 * the viewer everywhere now (decision rule 4, 2026-09-10).
 */
function groupArchiveRoundsByDay(rounds: ArchiveRound[]) {
  const nowSec = Math.floor(Date.now() / 1000);
  const groups = [
    { key: 'today' as const, date: localDayKey(nowSec), rounds: [] as ArchiveRound[] },
    { key: 'yesterday' as const, date: localDayKey(nowSec - 86_400), rounds: [] as ArchiveRound[] },
  ];
  for (const round of rounds) {
    const ts = Number(round.cutoff_ts || round.ends_at || round.starts_at || 0);
    if (!ts) continue;
    const group = groups.find((item) => item.date === localDayKey(ts));
    if (group) group.rounds.push(round);
  }
  return groups.filter((group) => group.rounds.length > 0);
}

function pairBreakdownBars(bars: Array<{ key?: string; count?: number; bettors?: number; amount?: number }>) {
  const pairs: Array<Array<{ key?: string; count?: number; bettors?: number; amount?: number }>> = [];
  for (let index = 0; index < bars.length; index += 2) {
    pairs.push(bars.slice(index, index + 2));
  }
  return pairs.filter((pair) => pair.length > 0);
}

/**
 * The bet-sheet market a distribution tile stands for, or null when the tile
 * is a counting cell with nowhere to send a player. Resolved through the real
 * market list so a tile can never deep-link into a pick the sheet cannot show.
 */
function poolBetTarget(game: GameCode, key: string | number | undefined): BetTarget | null {
  const raw = String(key ?? '').trim().toLowerCase();
  if (!raw) return null;
  for (const tab of BET_TABS[game]) {
    const option = tab.options.find((item) => item.pick === raw);
    if (option) return { tab: tab.key, pick: option.pick };
  }
  return null;
}

/** Fixed payout for the number grid, where every cell shares one multiplier. */
function gridOddsLabel(game: GameCode) {
  if (game === 'lastdigit') return '×9.5';
  if (game === 'roulette') return '×35';
  return '';
}

/**
 * One colour per market, shared with the bet history's draw tags.
 *
 * The two screens used to disagree, and not harmlessly: gold meant Big in the
 * history and Odd here, so the same word changed colour and the same colour
 * changed meaning depending on which sheet you were looking at. This follows
 * `BetHistorySheet`'s vocabulary — it is the one a player sees on their own
 * settled bets, so it is the one worth being canonical.
 *
 * Hi/Lo has no history equivalent; it rides with Big/Small, which is what it
 * is on a roulette table.
 */
function poolTone(key: string | undefined) {
  if (key === 'big' || key === 'hi') return 'gold';
  if (key === 'small' || key === 'lo') return 'cyan';
  if (key === 'odd') return 'rose';
  if (key === 'even') return 'blue';
  if (key === 'red') return 'red';
  if (key === 'black') return 'dark';
  return 'violet';
}

function pickDanmakuRow(recentRows: Array<{ row: number; at: number }>) {
  const now = Date.now();
  const active = recentRows.filter((item) => now - item.at < 2000);
  const used = new Set(active.map((item) => item.row));
  const free = [0, 1, 2].filter((row) => !used.has(row));
  const row = free.length ? free[Math.floor(Math.random() * free.length)] : Math.floor(Math.random() * 3);
  recentRows.splice(0, recentRows.length, ...active, { row, at: now });
  return row;
}

function danmakuDrawFromRound(round: Round): DanmakuEvalDraw | null {
  const games = round.games || {};
  const lastdigit = games.lastdigit == null ? undefined : Number(games.lastdigit);
  const sicboDice = Array.isArray(games.sicbo)
    ? games.sicbo.map((value) => Number(value)).filter((value) => Number.isFinite(value))
    : [];
  const rouletteNum = games.roulette == null ? undefined : Number(games.roulette);
  const hasLastdigit = Number.isFinite(lastdigit);
  const hasSicbo = sicboDice.length === 3;
  const hasRoulette = Number.isFinite(rouletteNum);
  if (!hasLastdigit && !hasSicbo && !hasRoulette) return null;
  const sicboSum = hasSicbo ? sicboDice.reduce((sum, item) => sum + item, 0) : undefined;
  const sicboTriple = hasSicbo && sicboDice[0] === sicboDice[1] && sicboDice[1] === sicboDice[2] ? sicboDice[0] : null;
  return {
    lastdigit: hasLastdigit ? lastdigit : undefined,
    sicboDice,
    sicboSum,
    sicboTriple,
    rouletteNum: hasRoulette ? rouletteNum : undefined,
    rouletteColor: hasRoulette ? rouletteColor(Number(rouletteNum)) : undefined,
  };
}

/** Settle a ticker bet against the round that just landed. The evaluator is
 *  shared with the pre-confirm win preview, which runs it over hypothetical
 *  draws — one settlement rule, whether the draw already happened or not. */
function evaluateDanmakuBet(game: GameCode, tab: string | undefined, rawPick: string | number | undefined, draw: DanmakuEvalDraw) {
  return evalBetAgainstDraw(game, tab, rawPick, draw);
}

function formatTicker(template: string, params: Record<string, string>) {
  return Object.entries(params).reduce((text, [key, value]) => text.split(`%${key}%`).join(value), template);
}

function initialDanmakuMode(): DanmakuMode {
  const storage = typeof window === 'undefined' ? undefined : window.localStorage;
  const saved = readStorage(storage, DANMAKU_MODE_KEY);
  if (saved === 'on' || saved === 'min' || saved === 'off') return saved;

  const legacyEnabled = readStorage(storage, LEGACY_DANMAKU_ENABLED_KEY);
  const legacyMin = readStorage(storage, LEGACY_DANMAKU_MIN_KEY);
  if (legacyEnabled === '0') return 'off';
  if (legacyMin === '1') return 'min';
  return 'on';
}

function persistDanmakuMode(mode: DanmakuMode) {
  const storage = typeof window === 'undefined' ? undefined : window.localStorage;
  writeStorage(storage, DANMAKU_MODE_KEY, mode);
  writeStorage(storage, LEGACY_DANMAKU_ENABLED_KEY, mode === 'off' ? '0' : '1');
  writeStorage(storage, LEGACY_DANMAKU_MIN_KEY, mode === 'min' ? '1' : '0');
}

function trendAxis(game: GameCode) {
  if (game === 'sicbo') return { min: 3, max: 18, threshold: 10.5, ticks: [3, 6, 9, 12, 15, 18] };
  if (game === 'roulette') return { min: 0, max: 36, threshold: 18.5, ticks: [0, 6, 12, 18, 24, 30, 36] };
  return { min: 0, max: 9, threshold: 4.5, ticks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] };
}

function trendChartHeight() {
  return Math.min(224, Math.max(188, Math.round(window.innerHeight * 0.26)));
}

function trendLegendItems(game: GameCode, t: ReturnType<typeof useI18nStore.getState>['t']): Array<{ cls: string; label: string }> {
  // The ranges stay numeric; only the words are translated. These used to be
  // hardcoded English ('Big 5–9', 'Small 0–4') sitting next to a translated
  // threshold caption, so one legend row carried two languages.
  if (game === 'sicbo') {
    return [
      { cls: 'legend-big', label: `${t('big')} 11–17` },
      { cls: 'legend-small', label: `${t('small')} 4–10` },
      { cls: 'legend-triple', label: t('triple') },
    ];
  }
  if (game === 'roulette') {
    return [{ cls: 'legend-red', label: t('red') }, { cls: 'legend-black', label: t('black') }, { cls: 'legend-green', label: `0 (${t('green')})` }];
  }
  return [{ cls: 'legend-big', label: `${t('big')} 5–9` }, { cls: 'legend-small', label: `${t('small')} 0–4` }];
}

function trendValue(round: AnalyticsRound, game: GameCode) {
  if (game === 'sicbo') return round.sicboSum;
  if (game === 'roulette') return round.roulette;
  return round.lastdigit;
}

function trendTone(round: AnalyticsRound, game: GameCode) {
  if (game === 'roulette') return `tone-${round.rouletteColor || 'green'}`;
  if (game === 'sicbo') return round.sicboTriple ? 'tone-gold' : Number(round.sicboSum || 0) >= 11 ? 'tone-red' : 'tone-teal';
  return Number(round.lastdigit || 0) >= 5 ? 'tone-red' : 'tone-teal';
}

/**
 * Sort weight for a miss count. -1 means "never landed in the history we hold",
 * which is the coldest thing on the board — but two of them subtracted as
 * Infinity give NaN, and a NaN comparator scrambles the whole grid.
 */
function coldRank(missing: number) {
  return missing < 0 ? Number.MAX_SAFE_INTEGER : missing;
}

function coldStats(game: GameCode, rounds: AnalyticsRound[], sort: 'natural' | 'missing') {
  const buckets = analyticsBuckets(game);
  const stats = buckets.map((bucket) => {
    const index = rounds.findIndex((round) => bucket.matches(round));
    return {
      key: bucket.key,
      label: bucket.label,
      missing: index === -1 ? -1 : index,
      mean: bucket.mean,
      target: bucket.target,
    };
  });
  if (sort === 'missing') stats.sort((a, b) => coldRank(b.missing) - coldRank(a.missing));
  return stats;
}

type AnalyticsBucket = {
  key: string;
  label: string;
  mean: number;
  target: BetTarget;
  matches: (round: AnalyticsRound) => boolean;
};

// `target` names the market each bucket counts, so the tile can hand the bet
// sheet that market's coordinates. Sums 3 and 18 have no market (three dice
// cannot make them) — findBetOption drops those, leaving them plain readouts.
function analyticsBuckets(game: GameCode): AnalyticsBucket[] {
  if (game === 'sicbo') {
    return Array.from({ length: 16 }, (_, index) => {
      const sum = index + 3;
      return {
        key: `sum-${sum}`,
        label: String(sum),
        mean: 16,
        target: { tab: 'sicbo-sum', pick: `s${sum}` },
        matches: (round: AnalyticsRound) => round.sicboSum === sum,
      };
    });
  }
  if (game === 'roulette') {
    return Array.from({ length: 37 }, (_, value) => ({
      key: `roulette-${value}`,
      label: String(value),
      mean: 37,
      target: { tab: 'roulette-num', pick: String(value) },
      matches: (round: AnalyticsRound) => round.roulette === value,
    }));
  }
  return Array.from({ length: 10 }, (_, value) => ({
    key: `digit-${value}`,
    label: String(value),
    mean: 10,
    target: { tab: 'specific', pick: String(value) },
    matches: (round: AnalyticsRound) => round.lastdigit === value,
  }));
}

function coldTier(missing: number, mean: number) {
  if (missing < 0) return 'red';
  if (missing < mean * 0.8) return 'green';
  if (missing <= mean * 1.3) return 'yellow';
  if (missing <= mean * 2) return 'orange';
  return 'red';
}

function coldSubtitle(game: GameCode, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (game === 'sicbo') return t('analyticsColdSubtitleSicbo');
  if (game === 'roulette') return t('analyticsColdSubtitleRoulette');
  return t('analyticsTheoretical');
}

function coldHint(game: GameCode, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (game === 'sicbo') return t('analyticsColdHintSicbo');
  if (game === 'roulette') return t('analyticsColdHintRoulette');
  return t('analyticsColdHint');
}

function naturalSortLabel(game: GameCode, t: ReturnType<typeof useI18nStore.getState>['t']) {
  // Sic Bo's Missing tab is four grids with four different ranges, so there is
  // no one span to name the way "0 → 36" names roulette's.
  if (game === 'sicbo') return t('analyticsSortDefault');
  if (game === 'roulette') return '0 → 36';
  return t('analyticsSortNatural');
}

function formatMean(mean: number) {
  return mean >= 10 ? String(Math.round(mean)) : mean.toFixed(1).replace('.', ',');
}

// Dragon (长龙) never breaks: a same-side streak stacks down to ROAD_MAX_ROW,
// then trails right along the bottom row (龙尾) instead of starting a fresh column.
const ROAD_MAX_ROW = 7;

function buildRoadCells(game: GameCode, rounds: AnalyticsRound[], roadKind: RoadKind) {
  const ordered = [...rounds].reverse();
  const cells: Array<{ col: number; row: number; side: string; value: number; round: number }> = [];
  let col = -1;
  let row = 0;
  let lastSide = '';
  for (const round of ordered) {
    const side = roadSide(round, game, roadKind);
    const value = trendValue(round, game);
    if (!side || value == null) continue;
    if (side !== lastSide) {
      // A flip opens a fresh column from the top.
      col += 1;
      row = 1;
      lastSide = side;
    } else if (row < ROAD_MAX_ROW) {
      // Same side, column still has vertical room: keep stacking down.
      row += 1;
    } else {
      // Same side but the column is full: turn right and trail along the
      // bottom row (dragon tail) — the streak stays unbroken.
      col += 1;
      // row stays pinned at ROAD_MAX_ROW
    }
    cells.push({ col, row, side, value, round: round.round });
  }
  const minCol = Math.max(0, col - 34);
  return cells.filter((cell) => cell.col >= minCol).map((cell) => ({ ...cell, col: cell.col - minCol }));
}

function roadSide(round: AnalyticsRound, game: GameCode, roadKind: RoadKind) {
  if (game === 'roulette') {
    if (round.roulette == null || round.roulette === 0) return '';
    if (roadKind === 'hilo') return round.roulette >= 19 ? 'big' : 'small';
    return round.rouletteColor === 'red' ? 'red' : 'black';
  }
  if (game === 'sicbo') {
    if (round.sicboSum == null || round.sicboTriple) return '';
    return round.sicboSum >= 11 ? 'big' : 'small';
  }
  if (round.lastdigit == null) return '';
  if (roadKind === 'oddeven') return round.lastdigit % 2 === 0 ? 'even' : 'odd';
  return round.lastdigit >= 5 ? 'big' : 'small';
}

// The road has exactly two sides, so a break lands on the other one. Named
// here rather than inferred at the call site, because which pair is in play
// depends on the road kind (big/small, odd/even, red/black).
function oppositeRoadSide(side: string): string {
  if (side === 'big') return 'small';
  if (side === 'small') return 'big';
  if (side === 'odd') return 'even';
  if (side === 'even') return 'odd';
  if (side === 'red') return 'black';
  if (side === 'black') return 'red';
  return '';
}

// The market a road side is drawn from. Note the road SKIPS the rounds that
// belong to neither side — a sic bo triple, a roulette zero — whereas the
// even-money bet LOSES them. That gap is already carried on the bet option
// itself (sicboBsTripleNote, and roulette's single zero), so the jump lands
// somewhere that states its own exception rather than hiding it.
function roadBetTarget(game: GameCode, roadKind: RoadKind, side: string): BetTarget | null {
  if (game === 'roulette') {
    if (roadKind === 'redblack') return side === 'red' || side === 'black' ? { tab: 'roulette-rb', pick: side } : null;
    if (roadKind === 'hilo') return side === 'big' ? { tab: 'roulette-hilo', pick: 'hi' } : side === 'small' ? { tab: 'roulette-hilo', pick: 'lo' } : null;
    return null;
  }
  if (game === 'sicbo') return side === 'big' || side === 'small' ? { tab: 'sicbo-bs', pick: side } : null;
  if (roadKind === 'oddeven') return side === 'odd' || side === 'even' ? { tab: 'odd-even', pick: side } : null;
  return side === 'big' || side === 'small' ? { tab: 'big-small', pick: side } : null;
}

// The legend already localizes every side this road can show, so it doubles as
// the label source for the dashed cells' screen-reader names.
function roadSideLabel(game: GameCode, roadKind: RoadKind, side: string, t: ReturnType<typeof useI18nStore.getState>['t']) {
  return roadLegend(game, roadKind, t).find((entry) => entry.side === side)?.label || side;
}

function roadColumnCount(cells: Array<{ col: number }>) {
  return cells.reduce((max, cell) => Math.max(max, cell.col + 1), 1);
}

function rouletteDozenLabel(value: number) {
  if (value <= 0) return '0';
  if (value <= 12) return '1st 12';
  if (value <= 24) return '2nd 12';
  return '3rd 12';
}

function revealDrawForGame(round: Round, game: GameCode): RoomRevealDraw | null {
  const games = round.games || {};
  // One global draw feeds all three games — carry every result so the reveal
  // can show the room's game as the hero plus the other two as a footer.
  const lastdigit = Number(games.lastdigit);
  const sicbo = Array.isArray(games.sicbo)
    ? games.sicbo.map((value) => Number(value)).filter((value) => Number.isFinite(value))
    : [];
  const roulette = Number(games.roulette);
  const draw: RoomRevealDraw = {
    ...(Number.isFinite(lastdigit) ? { lastdigit } : {}),
    ...(sicbo.length === 3 ? { sicbo } : {}),
    ...(Number.isFinite(roulette) ? { roulette } : {}),
  };
  if (game === 'lastdigit' && draw.lastdigit === undefined) return null;
  if (game === 'sicbo' && !draw.sicbo) return null;
  if (game === 'roulette' && draw.roulette === undefined) return null;
  return draw;
}

// DS §4.5 — one concrete example per major bet tab (help bubble).
function tabHelpKey(tabKey: string): MessageKey | null {
  const map: Record<string, MessageKey> = {
    'odd-even': 'howEx_ld_oddeven',
    'big-small': 'howEx_ld_bigsmall',
    'specific': 'howEx_ld_specific',
    'sicbo-bs': 'howEx_sicbo_bs',
    'sicbo-sum': 'howEx_sicbo_sum',
    'sicbo-single': 'howEx_sicbo_single',
    'sicbo-combo': 'howEx_sicbo_combo',
    'sicbo-pairs': 'howEx_sicbo_pairs',
    'roulette-rb': 'howEx_rou_rb',
    'roulette-oe': 'howEx_rou_oe',
    'roulette-hilo': 'howEx_rou_hilo',
    'roulette-num': 'howEx_rou_num',
  };
  return map[tabKey] || null;
}

function summaryDrawFromRound(round: Round): RoundSummaryResponse['draw'] | null {
  const games = round.games || {};
  const draw: NonNullable<RoundSummaryResponse['draw']> = {};
  const lastdigit = Number(games.lastdigit);
  if (Number.isFinite(lastdigit)) draw.lastdigit = lastdigit;
  const sicbo = Array.isArray(games.sicbo)
    ? games.sicbo.map((value) => Number(value)).filter((value) => Number.isFinite(value))
    : [];
  if (sicbo.length === 3) draw.sicbo = sicbo;
  const roulette = Number(games.roulette);
  if (Number.isFinite(roulette)) draw.roulette = roulette;
  return Object.keys(draw).length > 0 ? draw : null;
}

function fairnessCards(game: GameCode, rounds: AnalyticsRound[], t: ReturnType<typeof useI18nStore.getState>['t']) {
  const n = Math.max(1, rounds.length);
  const segment = (key: string, label: string, count: number, theory: number, color: string) => ({
    key,
    label,
    count,
    theory,
    color,
    pct: (count / n) * 100,
  });
  if (game === 'roulette') {
    const green = rounds.filter((round) => round.roulette === 0).length;
    return [
      {
        title: t('analyticsFairRedBlack'),
        segments: [
          segment('red', t('red'), rounds.filter((round) => round.rouletteColor === 'red').length, (18 / 37) * 100, 'rose'),
          segment('black', t('black'), rounds.filter((round) => round.rouletteColor === 'black').length, (18 / 37) * 100, 'slate'),
          segment('green', t('green'), green, (1 / 37) * 100, 'green'),
        ],
      },
      {
        key: 'roul-oe',
        title: t('analyticsFairOddEven'),
        segments: [
          segment('odd', t('odd'), rounds.filter((round) => Number(round.roulette) >= 1 && Number(round.roulette) % 2 === 1).length, (18 / 37) * 100, 'gold'),
          segment('even', t('even'), rounds.filter((round) => Number(round.roulette) >= 1 && Number(round.roulette) % 2 === 0).length, (18 / 37) * 100, 'sky'),
          segment('zero', '0', rounds.filter((round) => Number(round.roulette) === 0).length, (1 / 37) * 100, 'green'),
        ],
      },
      {
        title: t('analyticsFairHiLo'),
        segments: [
          segment('lo', t('analyticsFairLo'), rounds.filter((round) => Number(round.roulette) >= 1 && Number(round.roulette) <= 18).length, (18 / 37) * 100, 'teal'),
          segment('hi', t('analyticsFairHi'), rounds.filter((round) => Number(round.roulette) >= 19).length, (18 / 37) * 100, 'rose'),
          segment('zero', '0', green, (1 / 37) * 100, 'green'),
        ],
      },
    ];
  }
  if (game === 'sicbo') {
    return [
      {
        title: t('analyticsFairBigSmall'),
        segments: [
          segment('small', t('small'), rounds.filter((round) => !round.sicboTriple && Number(round.sicboSum) >= 4 && Number(round.sicboSum) <= 10).length, (105 / 216) * 100, 'teal'),
          segment('big', t('big'), rounds.filter((round) => !round.sicboTriple && Number(round.sicboSum) >= 11).length, (105 / 216) * 100, 'rose'),
          segment('triple', t('analyticsFairTriple'), rounds.filter((round) => !!round.sicboTriple).length, (6 / 216) * 100, 'gold'),
        ],
      },
    ];
  }
  return [
    {
      title: t('analyticsFairOddEven'),
      segments: [
        segment('odd', t('odd'), rounds.filter((round) => Number(round.lastdigit) % 2 === 1).length, 50, 'amber'),
        segment('even', t('even'), rounds.filter((round) => Number(round.lastdigit) % 2 === 0).length, 50, 'sky'),
      ],
    },
    {
      title: t('analyticsFairBigSmall'),
      segments: [
        segment('small', `${t('small')} (0–4)`, rounds.filter((round) => Number(round.lastdigit) <= 4).length, 50, 'teal'),
        segment('big', `${t('big')} (5–9)`, rounds.filter((round) => Number(round.lastdigit) >= 5).length, 50, 'rose'),
      ],
    },
  ];
}

type StagedBet = {
  key: string;
  tab: string;
  pick: string;
  label: string;
  amount: number;
  chips: Record<string, number>;
};

const ROULETTE_RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
// Legacy sicbo Pairs panel: Any-double banner → specific doubles ×12 →
// Triples section (Any-triple banner → specific triples ×180), 3 per row.
function SicboPairsPanel({ stagedByKey, placedByKey, onStage, onQuickEdit, t, focusKey = '' }: {
  stagedByKey: Map<string, StagedBet>;
  placedByKey: Map<string, number>;
  onStage: (option: BetOption) => void;
  onQuickEdit: (tab: string, pick: string, label: string) => void;
  t: ReturnType<typeof useI18nStore.getState>['t'];
  /** `tab:pick` the player arrived for from a Missing tile, if any. */
  focusKey?: string;
}) {
  const TAB = 'sicbo-pairs';
  const focusOf = (pick: string) => (focusKey === `${TAB}:${pick}` ? ' is-bet-focus' : '');
  function zone(pick: string) {
    const key = `${TAB}:${pick}`;
    return { staged: stagedByKey.get(key)?.amount || 0, placed: placedByKey.get(key) || 0 };
  }
  function editIf(pick: string, label: string, event: { preventDefault: () => void }) {
    const z = zone(pick);
    if (z.staged + z.placed > 0) { event.preventDefault(); onQuickEdit(TAB, pick, label); }
  }
  function Badges({ pick, label }: { pick: string; label: string }) {
    const z = zone(pick);
    return (
      <>
        {z.placed > 0 ? <em className="bet-placed-badge">{fmtInt(z.placed)}</em> : null}
        {z.staged > 0 ? (
          <em
            className="bet-option-badge"
            onClick={(event) => { event.stopPropagation(); onQuickEdit(TAB, pick, label); }}
            title={t('rouQEditEdit')}
          >
            {fmtInt(z.staged)}
          </em>
        ) : null}
      </>
    );
  }
  // Legacy "?" die: any value qualifies — avoids implying specific numbers win
  function QDie({ color }: { color: string }) {
    return (
      <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="3.5" fill="#0f172a" stroke={color} strokeWidth="0.8" />
        <text x="12" y="16" textAnchor="middle" fill={color} fontSize="13" fontWeight="700" fontFamily="Inter, sans-serif">?</text>
      </svg>
    );
  }
  const faces = [1, 2, 3, 4, 5, 6];
  return (
    <div className="sicbo-pairs-panel">
      <div className="sicbo-sec-head"><span>{t('sicboAnyDouble')}</span><b>×2.2</b></div>
      <button
        type="button"
        data-bet-pick={`${TAB}:ad`}
        className={`bet-option tone-gold sicbo-banner-cell ${zone('ad').staged > 0 ? 'active' : ''}${focusOf('ad')}`}
        onClick={() => onStage({ tab: TAB, pick: 'ad', label: 'Any Double', labelKey: 'sicboAnyDouble', tone: 'gold' })}
        onContextMenu={(event) => editIf('ad', t('sicboAnyDouble'), event)}
      >
        <span className="sicbo-banner-dice">
          <QDie color="#fbbf24" /><QDie color="#fbbf24" /><QDie color="#fbbf24" />
          <i>{t('sicboAny2Same')}</i>
        </span>
        <b className="sicbo-banner-odds">×2.2</b>
        <Badges pick="ad" label={t('sicboAnyDouble')} />
      </button>

      <div className="sicbo-sec-head"><span>{t('analyticsColdDoubles')}</span><b>×12</b></div>
      <div className="sicbo-pairs-grid">
        {faces.map((n) => (
          <button
            key={`d${n}`}
            type="button"
            data-bet-pick={`${TAB}:d${n}`}
            className={`bet-option ${zone(`d${n}`).staged > 0 ? 'active' : ''}${focusOf(`d${n}`)}`}
            onClick={() => onStage({ tab: TAB, pick: `d${n}`, label: t('sicboDoubleN').replace('%n%', String(n)), dice: [n, n], tone: 'blue' })}
            onContextMenu={(event) => editIf(`d${n}`, t('sicboDoubleN').replace('%n%', String(n)), event)}
          >
            <span className="sicbo-cell-dice"><DiceFaceSvg n={n} size={20} /><DiceFaceSvg n={n} size={20} /></span>
            <b className="bet-option-odds">×12</b>
            <Badges pick={`d${n}`} label={t('sicboDoubleN').replace('%n%', String(n))} />
          </button>
        ))}
      </div>

      <div className="sicbo-sec-head"><span>{t('analyticsColdTriples')}</span><b>×30 / ×180</b></div>
      <button
        type="button"
        data-bet-pick={`${TAB}:any`}
        className={`bet-option tone-violet sicbo-banner-cell ${zone('any').staged > 0 ? 'active' : ''}${focusOf('any')}`}
        onClick={() => onStage({ tab: TAB, pick: 'any', label: 'Any Triple', labelKey: 'sicboAnyTriple', tone: 'violet' })}
        onContextMenu={(event) => editIf('any', t('sicboAnyTriple'), event)}
      >
        <span className="sicbo-banner-dice">
          <QDie color="#c4b5fd" /><QDie color="#c4b5fd" /><QDie color="#c4b5fd" />
          <i>{t('sicboAny3Same')}</i>
        </span>
        <b className="sicbo-banner-odds">×30</b>
        <Badges pick="any" label={t('sicboAnyTriple')} />
      </button>
      <div className="sicbo-pairs-grid">
        {faces.map((n) => (
          <button
            key={`t${n}`}
            type="button"
            data-bet-pick={`${TAB}:t${n}`}
            className={`bet-option ${zone(`t${n}`).staged > 0 ? 'active' : ''}${focusOf(`t${n}`)}`}
            onClick={() => onStage({ tab: TAB, pick: `t${n}`, label: t('sicboTripleN').replace('%n%', String(n)), dice: [n, n, n], tone: 'violet' })}
            onContextMenu={(event) => editIf(`t${n}`, t('sicboTripleN').replace('%n%', String(n)), event)}
          >
            <span className="sicbo-cell-dice"><DiceFaceSvg n={n} size={16} /><DiceFaceSvg n={n} size={16} /><DiceFaceSvg n={n} size={16} /></span>
            <b className="bet-option-odds is-gold">×180</b>
            <Badges pick={`t${n}`} label={t('sicboTripleN').replace('%n%', String(n))} />
          </button>
        ))}
      </div>
    </div>
  );
}

function roulColor(n: number): 'red' | 'black' | 'green' {
  if (n === 0) return 'green';
  return ROULETTE_RED.has(n) ? 'red' : 'black';
}

/**
 * Legacy roulette table board — the unified 0 + 1-36 grid with red/black
 * cells and direct-tap split/corner zones. Chips render at the exact bet
 * position (cell top-right, split-edge / corner-intersection centered) and
 * stage into the same `staged` map as every other bet via onStage.
 */
function RouletteBoard({ stagedByKey, placedByKey, onStage, onQuickEdit, t, focusKey = '' }: {
  stagedByKey: Map<string, StagedBet>;
  placedByKey: Map<string, number>;
  onStage: (option: BetOption) => void;
  onQuickEdit: (tab: string, pick: string, label: string) => void;
  t: ReturnType<typeof useI18nStore.getState>['t'];
  /** `tab:pick` the player arrived for from a Missing tile, if any. */
  focusKey?: string;
}) {
  function zone(tab: string, pick: string) {
    const key = `${tab}:${pick}`;
    const staged = stagedByKey.get(key)?.amount || 0;
    const placed = placedByKey.get(key) || 0;
    return { key, total: staged + placed, pending: staged > 0 };
  }
  function editIf(tab: string, pick: string, label: string, event: { preventDefault: () => void }) {
    if (zone(tab, pick).total > 0) { event.preventDefault(); onQuickEdit(tab, pick, label); }
  }
  // Legacy roulNumHi: numbers covered by a staged/placed column, dozen or
  // six-line bet light up, so a group bet reads directly on the felt.
  const covered = new Set<number>();
  const addCoverage = (key: string) => {
    const [tab, pick] = key.split(':');
    if (tab === 'roulette-column') {
      const idx = Number(pick.slice(3));
      for (let n = 1; n <= 36; n++) if (n % 3 === idx % 3) covered.add(n);
    } else if (tab === 'roulette-dozen') {
      const idx = Number(pick.slice(2));
      for (let n = (idx - 1) * 12 + 1; n <= idx * 12; n++) covered.add(n);
    } else if (tab === 'roulette-line') {
      rouletteLineNumbers(Number(pick.slice(2))).forEach((n) => covered.add(n));
    }
  };
  stagedByKey.forEach((bet, key) => { if (bet.amount > 0) addCoverage(key); });
  placedByKey.forEach((amount, key) => { if (amount > 0) addCoverage(key); });
  function Chip({ tab, pick, label, kind }: { tab: string; pick: string; label: string; kind: 'cell' | 'zone' }) {
    const z = zone(tab, pick);
    if (z.total <= 0) return null;
    return (
      <span
        className={`${kind === 'cell' ? 'zone-chip-cell' : 'zone-chip'} ${z.pending ? 'zone-chip-pending' : ''}`}
        onClick={(event) => { event.stopPropagation(); onQuickEdit(tab, pick, label); }}
      >
        {fmtInt(z.total)}
      </span>
    );
  }
  function stageNum(n: number) {
    const color = roulColor(n);
    onStage({ tab: 'roulette-num', pick: String(n), label: String(n), tone: color === 'red' ? 'red' : color === 'green' ? 'green' : 'dark' });
  }
  function stageSplit(a: number, b: number) {
    onStage({ tab: 'roulette-split', pick: `sp_${a}_${b}`, label: `${a}/${b}`, tone: 'gold' });
  }
  function stageCorner(tl: number) {
    onStage({ tab: 'roulette-corner', pick: `cn${tl}`, label: t('roulCornerN').replace('%n%', String(tl)), tone: 'blue' });
  }
  function stageStreet(r: number) {
    const start = (r - 1) * 3 + 1;
    onStage({ tab: 'roulette-street', pick: `st${r}`, label: `${start}-${start + 2}`, tone: 'violet' });
  }
  function stageSixLine(r: number) {
    const start = (r - 1) * 3 + 1;
    onStage({ tab: 'roulette-line', pick: `ln${r}`, label: `${start}-${start + 5}`, tone: 'green' });
  }
  function stageColumn(col: number) {
    onStage({ tab: 'roulette-column', pick: `col${col}`, label: `Column ${col}`, tone: 'blue' });
  }
  function stageDozen(dz: number, label: string) {
    onStage({ tab: 'roulette-dozen', pick: `dz${dz}`, label, tone: 'violet' });
  }
  function NumCell({ n }: { n: number }) {
    const z = zone('roulette-num', String(n));
    return (
      <button
        type="button"
        data-bet-pick={`roulette-num:${n}`}
        className={`bet-option roulette-cell-${roulColor(n)} ${z.total > 0 ? (z.pending ? 'cell-has-pending' : 'cell-has-bet') : ''}${covered.has(n) ? ' roul-num-hi' : ''}${focusKey === `roulette-num:${n}` ? ' is-bet-focus' : ''}`}
        onClick={() => stageNum(n)}
        onContextMenu={(event) => editIf('roulette-num', String(n), String(n), event)}
      >
        <span className="roul-num">{n}</span>
        <Chip tab="roulette-num" pick={String(n)} label={String(n)} kind="cell" />
      </button>
    );
  }
  function SplitZone({ a, b, vertical }: { a: number; b: number; vertical?: boolean }) {
    return (
      <button
        type="button"
        className={vertical ? 'roul-split-v' : 'roul-split'}
        onClick={() => stageSplit(a, b)}
        onContextMenu={(event) => editIf('roulette-split', `sp_${a}_${b}`, `${a}/${b}`, event)}
      >
        <Chip tab="roulette-split" pick={`sp_${a}_${b}`} label={`${a}/${b}`} kind="zone" />
      </button>
    );
  }
  function CornerZone({ tl }: { tl: number }) {
    return (
      <button
        type="button"
        className="roul-corner"
        onClick={() => stageCorner(tl)}
        onContextMenu={(event) => editIf('roulette-corner', `cn${tl}`, t('roulCornerN').replace('%n%', String(tl)), event)}
      >
        <Chip tab="roulette-corner" pick={`cn${tl}`} label={t('roulCornerN').replace('%n%', String(tl))} kind="zone" />
      </button>
    );
  }
  function StreetZone({ r }: { r: number }) {
    const start = (r - 1) * 3 + 1;
    const label = `${start}-${start + 2}`;
    return (
      <button
        type="button"
        className="roul-street"
        title={label}
        onClick={() => stageStreet(r)}
        onContextMenu={(event) => editIf('roulette-street', `st${r}`, label, event)}
      >
        <Chip tab="roulette-street" pick={`st${r}`} label={label} kind="zone" />
      </button>
    );
  }
  function SixLineZone({ r }: { r: number }) {
    const start = (r - 1) * 3 + 1;
    const label = `${start}-${start + 5}`;
    return (
      <button
        type="button"
        className={`roul-seisena${(stagedByKey.get(`roulette-line:ln${r}`) || placedByKey.get(`roulette-line:ln${r}`)) ? ' has-bet' : ''}`}
        title={label}
        onClick={() => stageSixLine(r)}
        onContextMenu={(event) => editIf('roulette-line', `ln${r}`, label, event)}
      >
        <span className="roul-seisena-odds">×6</span>
        <Chip tab="roulette-line" pick={`ln${r}`} label={label} kind="zone" />
      </button>
    );
  }
  function ColumnCell({ col }: { col: number }) {
    const z = zone('roulette-column', `col${col}`);
    return (
      <button
        type="button"
        className={`bet-option roul-outside-cell ${z.total > 0 ? (z.pending ? 'cell-has-pending' : 'cell-has-bet') : ''}`}
        onClick={() => stageColumn(col)}
        onContextMenu={(event) => editIf('roulette-column', `col${col}`, `Column ${col}`, event)}
      >
        <span className="roul-outside-name is-cyan">{t('tab_roulette-column')}</span>
        <span className="roul-outside-odds">×3</span>
        <Chip tab="roulette-column" pick={`col${col}`} label={t('roulColumnN').replace('%n%', String(col))} kind="cell" />
      </button>
    );
  }
  function DozenCell({ dz, label, range }: { dz: number; label: string; range: string }) {
    const z = zone('roulette-dozen', `dz${dz}`);
    return (
      <button
        type="button"
        className={`bet-option roul-outside-cell ${z.total > 0 ? (z.pending ? 'cell-has-pending' : 'cell-has-bet') : ''}`}
        onClick={() => stageDozen(dz, label)}
        onContextMenu={(event) => editIf('roulette-dozen', `dz${dz}`, label, event)}
      >
        <span className="roul-outside-name is-violet">{label}</span>
        <span className="roul-outside-range">{range}</span>
        <span className="roul-outside-odds">×3</span>
        <Chip tab="roulette-dozen" pick={`dz${dz}`} label={label} kind="cell" />
      </button>
    );
  }
  const zero = zone('roulette-num', '0');
  return (
    <div className="roul-board">
      <button
        type="button"
        className={`bet-option roulette-cell-green roul-zero ${zero.total > 0 ? (zero.pending ? 'cell-has-pending' : 'cell-has-bet') : ''}`}
        onClick={() => stageNum(0)}
        onContextMenu={(event) => editIf('roulette-num', '0', '0', event)}
      >
        <span className="roul-num">0</span>
        <Chip tab="roulette-num" pick="0" label="0" kind="cell" />
      </button>
      <div className="roul-zerosplit-row">
        <SplitZone a={0} b={1} />
        <SplitZone a={0} b={2} />
        <SplitZone a={0} b={3} />
      </div>
      <div className="roul-table">
        {Array.from({ length: 12 }, (_, ri) => {
          const r = ri + 1;
          const c1 = (r - 1) * 3 + 1;
          const c2 = c1 + 1;
          const c3 = c1 + 2;
          return (
            <div key={r}>
              <div className="roul-num-row">
                <NumCell n={c1} />
                <SplitZone a={c1} b={c2} />
                <NumCell n={c2} />
                <SplitZone a={c2} b={c3} />
                <NumCell n={c3} />
              </div>
              {r < 12 ? (
                <div className="roul-gap-row">
                  <SixLineZone r={r} />
                  <SplitZone a={c1} b={c1 + 3} vertical />
                  <CornerZone tl={c1} />
                  <SplitZone a={c2} b={c2 + 3} vertical />
                  <CornerZone tl={c2} />
                  <SplitZone a={c3} b={c3 + 3} vertical />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="roul-col-row">
        <span aria-hidden="true" />
        <ColumnCell col={1} />
        <span aria-hidden="true" />
        <ColumnCell col={2} />
        <span aria-hidden="true" />
        <ColumnCell col={3} />
      </div>
      <div className="roul-dozen-row">
        <DozenCell dz={1} label={t('roulDozen_dz1')} range="1–12" />
        <DozenCell dz={2} label={t('roulDozen_dz2')} range="13–24" />
        <DozenCell dz={3} label={t('roulDozen_dz3')} range="25–36" />
      </div>
      <p className="roul-board-hint">{t('tapPickToStage')}</p>
    </div>
  );
}

function BetSheet({
  room,
  roundNumber,
  displayRound,
  roundAcceptsBets,
  phase,
  currentRoundBets,
  previousSettlementPending,
  focusTarget,
  onClose,
  onOpenAnalytics,
  onOpenBasket,
  onPlaced,
}: {
  room: Room;
  roundNumber: number;
  displayRound?: string;
  roundAcceptsBets: boolean;
  // Unified countdown state, identical to the room widget (see roundPhaseDisplay).
  phase: RoundPhase;
  currentRoundBets: NormalizedBet[];
  previousSettlementPending: boolean;
  /** Arrived from a Missing tile: open on that tab and point at that pick.
   *  Pointing only — the stake stays the player's own deliberate tap. */
  focusTarget?: BetTarget | null;
  onClose: () => void;
  onOpenAnalytics: () => void;
  onOpenBasket: () => void;
  onPlaced: (user?: PlayerUser) => void;
}) {
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const showToast = useAppStore((state) => state.showToast);
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const betUser = useAuthStore((state) => state.user);
  const fiatCurrency = useCountryStore((state) => state.country.fiatCurrency);
  const [betSuccess, setBetSuccess] = useState<{ round: number; amount: number; pickLabel: string; payout: number; payoutMin?: number; edgeDigit?: string; count: number } | null>(null);
  const lastConfirmedBatch = useRef<StagedBet[]>([]);
  const betSheetRef = useRef<HTMLElement | null>(null);
  const tabs = BET_TABS[room.game];
  const landingKey = findBetOption(room.game, focusTarget) && focusTarget ? `${focusTarget.tab}:${focusTarget.pick}` : '';
  const [activeTab, setActiveTab] = useState(landingKey ? landingKey.split(':')[0] : defaultBetTab(room.game));
  // The cell the player came in for. Highlighted, scrolled to, never staked —
  // it clears the moment they act (tap it, switch tabs) or after a few seconds.
  const [focusKey, setFocusKey] = useState(landingKey);
  const [tabHelpOpen, setTabHelpOpen] = useState(false);
  const betTabsRef = useMouseDragScroll<HTMLElement>();
  const chips = room.chipValuesBrl?.length ? room.chipValuesBrl : [2, 5, 10, 50, 100];
  const presetChips = chips.slice(0, 5);
  const walletKnown = Boolean(betUser && (
    betUser.wallet_fiat !== undefined
    || betUser.walletFiat !== undefined
    || betUser.wallet_brl !== undefined
    || betUser.walletBrl !== undefined
  ));
  const walletLimit = walletKnown ? Math.max(0, userWalletFiat(betUser)) : Infinity;
  const roomLimit = Number(room.perRoundMaxBrl || 0) > 0 ? Number(room.perRoundMaxBrl) : Infinity;
  const maxSelectableAmount = Math.min(walletLimit, roomLimit);
  const betChipPreferenceStorageKey = betChipPreferenceKey(betUser?.id, room.code, fiatCurrency);
  const rememberedBetChip = readRememberedBetChip(
    typeof window === 'undefined' ? undefined : window.localStorage,
    betChipPreferenceStorageKey,
  );
  const initialBetChip = resolveInitialBetChip(
    presetChips,
    rememberedBetChip,
    maxSelectableAmount,
  );
  const [amount, setAmount] = useState(initialBetChip);
  const adjustedChipNotified = useRef(false);
  const [customAmountOpen, setCustomAmountOpen] = useState(false);
  const [staged, setStaged] = useState<StagedBet[]>([]);
  const [quickEdit, setQuickEdit] = useState<{ tab: string; pick: string; label: string } | null>(null);
  const active = tabFor(room.game, activeTab);
  const stagedByKey = useMemo(() => new Map(staged.map((bet) => [bet.key, bet])), [staged]);
  const placedByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const bet of currentRoundBets) {
      const key = `${bet.tab}:${bet.pick}`;
      map.set(key, (map.get(key) || 0) + bet.amount);
    }
    return map;
  }, [currentRoundBets]);
  const totalStake = staged.reduce((sum, bet) => sum + bet.amount, 0);
  const placedStake = sumBets(currentRoundBets, 'amount');
  const placedMaxWin = batchWinRange(currentRoundBets, room.game).max;
  const perRoundMax = Number(room.perRoundMaxBrl || 0);
  // The BEST the staged batch can do — shown BEFORE confirming. Until this line
  // existed the player only learned the payout on the success sheet, i.e. after
  // the money was already committed.
  //
  // "Best", not "sum": a round lands on one draw, so picks that exclude each
  // other cannot all pay. Adding them up quoted a number the game cannot
  // produce — cover all 37 roulette pockets at R$1 and it promised +R$1.295
  // against a real ceiling of +R$35.
  const stagedWin = batchWinRange(staged, room.game);
  const stagedMaxWin = stagedWin.max;

  useEffect(() => {
    if (
      adjustedChipNotified.current
      || rememberedBetChip === null
      || rememberedBetChip === initialBetChip
      || initialBetChip > maxSelectableAmount
    ) return;

    adjustedChipNotified.current = true;
    showToast(t('betChipAdjusted').replace('%chip%', fmtCurrency(initialBetChip, fiatCurrency)), 'info');
    if (betChipPreferenceStorageKey) {
      writeStorage(
        typeof window === 'undefined' ? undefined : window.localStorage,
        betChipPreferenceStorageKey,
        String(initialBetChip),
      );
    }
  }, [
    betChipPreferenceStorageKey,
    initialBetChip,
    maxSelectableAmount,
    rememberedBetChip,
    showToast,
    t,
  ]);

  // The state initialisers above only run on mount, and the sheet can already be
  // mounted when a deep link arrives (the read-the-table overlay opens on top of
  // it rather than replacing it). Without this, the second tile a player taps
  // would land nowhere. Keyed on the string, so re-rendering with the same
  // target does not re-point a sheet the player has since moved on from.
  useEffect(() => {
    if (!landingKey) return;
    setActiveTab(landingKey.split(':')[0]);
    setFocusKey(landingKey);
  }, [landingKey]);

  // Land the player on the cell they tapped in Missing: bring its tab into the
  // horizontal strip, centre the cell in the grid, and let the pulse fade. One
  // frame's delay so the grid for the landing tab has actually been painted.
  useEffect(() => {
    if (!focusKey) return undefined;
    const [targetTab] = focusKey.split(':');
    const frame = requestAnimationFrame(() => {
      const sheet = betSheetRef.current;
      if (!sheet) return;
      sheet.querySelector<HTMLElement>(`[data-bet-tab="${targetTab}"]`)?.scrollIntoView({ inline: 'center', block: 'nearest' });
      sheet.querySelector<HTMLElement>(`[data-bet-pick="${focusKey}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    const timer = window.setTimeout(() => setFocusKey(''), 3200);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [focusKey]);

  function selectPresetChip(chip: number) {
    if (chip > maxSelectableAmount) {
      if (walletLimit <= roomLimit) {
        showToast(t(previousSettlementPending ? 'betPreviousSettlementWaiting' : 'betInsufficientBalance'), 'info');
      } else {
        showToast(t('chipCapToast').replace('%cap%', fmtCurrency(roomLimit, fiatCurrency)), 'info');
      }
      return;
    }
    setAmount(chip);
    if (betChipPreferenceStorageKey) {
      writeStorage(
        typeof window === 'undefined' ? undefined : window.localStorage,
        betChipPreferenceStorageKey,
        String(chip),
      );
    }
  }

  const mutation = useMutation({
    mutationFn: () => {
      if (staged.length === 0) throw new Error('NO_PICK');
      return placeBetsBatch({
        roomId: room.code,
        roundNumber,
        game: room.game,
        bets: staged.map((bet) => ({
          game: room.game,
          tab: bet.tab,
          pick: bet.pick,
          amount: bet.amount,
          chips: chipsToLines(bet.chips),
        })),
      }, token);
    },
    onSuccess: (payload) => {
      // Legacy replaces the sheet body with the bet-success state (no toast).
      const first = staged[0];
      const totalAmount = staged.reduce((sum, bet) => sum + bet.amount, 0);
      // Same figures the pre-confirm preview showed — a batch whose payout is a
      // range there must not collapse to its ceiling here.
      const { max: totalWin, min: minWin } = batchWinRange(staged, room.game);
      setBetSuccess({
        round: Number(displayRound) || roundNumber,
        amount: totalAmount,
        // Already translated at stage time (see addOption).
        pickLabel: staged.length === 1 && first ? first.label : `${staged.length} ${t('sicboPicksLabel')}`,
        payout: totalWin,
        payoutMin: minWin,
        edgeDigit: edgeDigitFor(room.game, staged),
        count: staged.length,
      });
      lastConfirmedBatch.current = staged;
      setStaged([]);
      onPlaced(payload.user);
    },
    onError: (error) => showToast(betErrorToast(error, t), 'error'),
  });

  function removeStaged(key: string) {
    setStaged((current) => current.filter((bet) => bet.key !== key));
  }

  function openQuickEdit(tab: string, pick: string, label: string) {
    setQuickEdit({ tab, pick, label });
  }

  function qeAdd() {
    if (!quickEdit) return;
    addOption({ tab: quickEdit.tab, pick: quickEdit.pick, label: quickEdit.label });
  }

  function qeSub() {
    if (!quickEdit || mutation.isPending) return;
    const key = `${quickEdit.tab}:${quickEdit.pick}`;
    setStaged((current) => {
      const bet = current.find((item) => item.key === key);
      if (!bet) return current;
      const nextChips = subChip(bet.chips, amount);
      const nextAmount = chipsTotal(nextChips);
      if (nextAmount <= 0) return current.filter((item) => item.key !== key);
      return current.map((item) => (item.key === key ? { ...item, amount: nextAmount, chips: nextChips } : item));
    });
  }

  function qeClear() {
    if (!quickEdit) return;
    removeStaged(`${quickEdit.tab}:${quickEdit.pick}`);
    setQuickEdit(null);
  }

  function addOption(option: BetOption) {
    if (mutation.isPending) return;
    const key = `${option.tab}:${option.pick}`;
    // The player has found it — stop pointing.
    setFocusKey((current) => (current === key ? '' : current));
    setStaged((current) => {
      const existing = current.find((bet) => bet.key === key);
      if (existing) {
        return current.map((bet) => bet.key === key
          ? {
              ...bet,
              amount: bet.amount + amount,
              chips: addChip(bet.chips, amount),
            }
          : bet);
      }
      return [
        ...current,
        {
          key,
          tab: option.tab,
          pick: option.pick,
          // Translated at stage time: everything downstream (bet-success sheet,
          // quick-edit) reads this and must never surface the English fallback.
          label: optionLabel(option, t),
          amount,
          chips: addChip({}, amount),
        },
      ];
    });
  }

  const disabledReason = guestMode
    ? t('enterToPlay')
    : !roundAcceptsBets
      ? t('roundNotOpen')
      : staged.length === 0
        ? t('noStagedBets')
        : perRoundMax > 0 && totalStake > perRoundMax
          ? t('betLimitExceeded')
          : totalStake > userWalletFiat(betUser)
            ? t(previousSettlementPending ? 'betPreviousSettlementWaiting' : 'betInsufficientBalance')
            : '';

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section ref={betSheetRef} className={`bet-sheet ${room.game === 'roulette' ? 'bet-sheet-roulette' : ''}`} role="dialog" aria-modal="true" aria-label={t('betCta')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        {betSuccess ? (
          <div className="px-6 py-5 text-center" data-bet-success-react="true">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 border-[3px] border-emerald-500 flex items-center justify-center check-pop">
              <Check size={30} className="text-emerald-400" aria-hidden="true" />
            </div>
            <h2 className="mt-2.5 font-bold text-xl text-ink-1">{t('betPlacedTitle')}</h2>
            {/* One line carries round + the live close countdown (was a separate pill) */}
            <p className="mt-1 text-[12px] text-ink-3">
              {t('roundShort').replace('%n%', String(betSuccess.round || '---'))}
              <span aria-hidden="true"> · </span>
              <span className="font-mono font-bold tabular-nums" style={{ color: ROUND_TONE_COLOR[phase.tone] }}>
                {`${phase.label} ${phase.value}`}
              </span>
            </p>

            <div className="mt-3 bg-card2 rounded-2xl border border-border1 p-4 mx-auto max-w-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-3">{t('betYouBet')}</div>
                  <div className="text-2xl font-mono font-bold mt-1 text-ink-1 whitespace-nowrap">{fmtMoney(betSuccess.amount)}</div>
                </div>
                <div>
                  {/* "If you win" only makes sense when there is one thing to
                      win. Several picks in one round exclude each other, so the
                      honest headline for a batch is its ceiling. */}
                  <div className="text-[10px] uppercase tracking-widest text-ink-3">{t(betSuccess.count > 1 ? 'basketMaxWin' : 'betIfYouWin')}</div>
                  {/* One number, big — same shape as the option card the player
                      just tapped. The lower boundary payout is a footnote, not
                      half of a range: on Last-Digit odd/even four of the five
                      winning digits pay the headline and only one pays less.
                      Rendered as a range this wrapped to three lines and broke
                      between "+R$" and "200".
                      No sign: this is the RETURN (stake × odds, stake included),
                      and a sign is reserved for net balance moves (D-43). */}
                  <div className="text-2xl font-mono font-bold mt-1 text-emerald-400 whitespace-nowrap">{fmtMoney(betSuccess.payout)}</div>
                  {betSuccess.payoutMin !== undefined && betSuccess.payoutMin < betSuccess.payout ? (
                    <div className="text-[10.5px] text-ink-3 mt-0.5">
                      {/* The floor payout, and nothing else. This used to fall
                          back to ldTierExplain -- Last Digit's "edge digits 0
                          and 9 pay ×1.5, locks RTP at 95%" -- for ANY game with
                          a payout range, so a Sic Bo single-number bet on "1"
                          was handed a rule about digits its board does not
                          have. RTP is an operator metric besides; a player who
                          just staked money wants the number, not the model. */}
                      {betSuccess.edgeDigit
                        ? t('betEdgeWinNote').replace('%v%', fmtMoney(betSuccess.payoutMin)).replace('%d%', betSuccess.edgeDigit)
                        : t('betMinWinNote').replace('%v%', fmtMoney(betSuccess.payoutMin))}
                    </div>
                  ) : null}
                </div>
              </div>
              {/* Latin quotes: 「」 are CJK corner brackets and read as a
                  rendering bug in pt-BR/en/es. */}
              <div className="text-[12.5px] text-ink-2 mt-2.5">{t('betOn')} <span className="text-ink-1 font-semibold">“{betSuccess.pickLabel}”</span></div>
            </div>

            {currentRoundBets.length > 1 ? (
              <div className="mt-2 text-[11px] text-ink-3">
                {t('basketBetsCount').replace('%n%', String(currentRoundBets.length))} · {t('basketStake')}: {fmtMoney(placedStake)}
              </div>
            ) : null}

            <button className="mt-3 w-full py-3 bg-gradient-to-r from-brand-500 to-brand-700 text-white rounded-xl font-bold tap inline-flex items-center justify-center gap-2" type="button" onClick={() => setBetSuccess(null)}>
              <span className="text-lg leading-none">+</span>
              <span>{t('placeAnother')}</span>
            </button>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button className="py-2 bg-card2 text-ink-2 border border-border1 rounded-xl font-semibold tap text-xs" type="button" onClick={() => { setBetSuccess(null); onOpenBasket(); }}>
                {t('viewBasket').replace('%n%', String(currentRoundBets.length))}
              </button>
              <button className="py-2 bg-card2 text-ink-2 border border-border1 rounded-xl font-semibold tap text-xs" type="button" onClick={onClose}>
                {t('btnDone')}
              </button>
            </div>
          </div>
        ) : (
          <>
        <header className="sheet-header">
          <div>
            <h2>{t('placeBet')} · #{displayRound || roundNumber || '---'}</h2>
          </div>
          <div className="sheet-header-actions">
            <button className="tap inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border border-brand-500/40 text-brand-200 bg-brand-500/15" type="button" onClick={onOpenAnalytics}>
              <BarChart3 size={12} aria-hidden="true" />
              <span>{t('analyticsFromBet')}</span>
            </button>
            <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className={`bets-close-banner tone-${phase.tone}`}>
          <span>{phase.label}</span>
          <strong>{phase.value}</strong>
        </div>

        {/* Confirmed bets only — one slim tappable line. Staged work-in-progress
            already lives on the card badges, Clear · N and the footer CTA. */}
        {currentRoundBets.length > 0 ? (
          <button className="bet-myround-line tap" type="button" onClick={onOpenBasket} data-bet-round-status-react="true">
            <Check size={13} aria-hidden="true" />
            <span>{t('basketBetsCount').replace('%n%', String(currentRoundBets.length))} · {fmtMoney(placedStake)}</span>
            {placedMaxWin > 0 ? <b>{t('basketMaxWin')} {fmtMoney(placedMaxWin)}</b> : null}
            <ChevronRight size={14} aria-hidden="true" />
          </button>
        ) : null}

        <div className="bet-step-heading">
          <span>① {t('pickHeading')}</span>
          {tabHelpKey(activeTab) ? (
            <button className="ds-tab-help-btn tap" type="button" aria-label={t('howToPlay')} onClick={() => setTabHelpOpen((value) => !value)}>?</button>
          ) : null}
        </div>
        <nav className="bet-tabs" aria-label={t('pickHeading')} ref={betTabsRef}>
          {tabs
            .filter((tab) => !(room.game === 'roulette' && ['roulette-split', 'roulette-corner', 'roulette-street', 'roulette-line', 'roulette-dozen', 'roulette-column'].includes(tab.key)))
            .map((tab) => (
              <button
                key={tab.key}
                data-bet-tab={tab.key}
                className={activeTab === tab.key ? 'active' : ''}
                type="button"
                onClick={() => { setActiveTab(tab.key); setTabHelpOpen(false); setFocusKey(''); }}
              >
                {tab.key === 'roulette-num' ? t('roulTableTab') : t(`tab_${tab.key}` as MessageKey)}
              </button>
            ))}
        </nav>
        {tabHelpOpen && tabHelpKey(activeTab) ? (
          <button className="ds-tab-help tap" type="button" onClick={() => setTabHelpOpen(false)} data-tab-help-react="true">
            {t(tabHelpKey(activeTab) as MessageKey)}
          </button>
        ) : null}

        {['roulette-rb', 'roulette-oe', 'roulette-hilo'].includes(active.key) ? (
          <p className="bet-tab-hint">{t('roulHintSide')} · {fmtCurrency(amount, fiatCurrency)}</p>
        ) : null}
        {active.key === 'sicbo-single' ? <p className="bet-tab-help">{t('sicboSingleHelp')}</p> : null}
        {['sicbo-single', 'sicbo-combo'].includes(active.key) ? (
          <p className="bet-tab-hint">{t('sicboStageHint')}</p>
        ) : null}
        {room.game === 'roulette' && active.key === 'roulette-num' ? (
          <RouletteBoard stagedByKey={stagedByKey} placedByKey={placedByKey} onStage={addOption} onQuickEdit={openQuickEdit} t={t} focusKey={focusKey} />
        ) : room.game === 'sicbo' && active.key === 'sicbo-pairs' ? (
          <SicboPairsPanel stagedByKey={stagedByKey} placedByKey={placedByKey} onStage={addOption} onQuickEdit={openQuickEdit} t={t} focusKey={focusKey} />
        ) : (
        <div className={`bet-option-grid ${room.game} ${active.key}`}>
          {active.options.map((option) => {
            const optKey = `${option.tab}:${option.pick}`;
            const stagedBet = stagedByKey.get(optKey);
            const placed = placedByKey.get(optKey) || 0;
            return (
              <button
                key={optKey}
                data-bet-pick={optKey}
                className={`bet-option tone-${option.tone || 'gold'} ${stagedBet ? 'active' : ''}${focusKey === optKey ? ' is-bet-focus' : ''}`}
                type="button"
                onClick={() => addOption(option)}
                onContextMenu={(event) => { event.preventDefault(); openQuickEdit(option.tab, option.pick, optionLabel(option, t)); }}
              >
                {placed > 0 ? <em className="bet-placed-badge">{fmtInt(placed)}</em> : null}
                {stagedBet ? (
                  <em
                    className="bet-option-badge"
                    onClick={(event) => { event.stopPropagation(); openQuickEdit(option.tab, option.pick, optionLabel(option, t)); }}
                    title={t('rouQEditEdit')}
                  >
                    {fmtInt(stagedBet.amount)}
                  </em>
                ) : null}
                {option.dice ? (
                  <span className="bet-option-dice">
                    {option.dice.map((die, dieIndex) => (
                      <span key={dieIndex} className="inline-flex items-center gap-1">
                        {dieIndex > 0 ? <i className="bet-dice-amp">&amp;</i> : null}
                        <DiceFaceSvg n={die} size={option.dice && option.dice.length > 1 ? 18 : 32} />
                      </span>
                    ))}
                  </span>
                ) : (
                  <strong>{optionLabel(option, t)}</strong>
                )}
                {option.sublabelKey ? <span>{t(option.sublabelKey as MessageKey)}</span> : option.sublabel ? <span>{option.sublabel}</span> : null}
                {option.odds ? <b className="bet-option-odds">{option.odds}</b> : null}
                {option.note ? <small className="bet-option-note">{option.note}</small> : null}
                {option.noteKey ? (
                  <small className="bet-option-note">
                    <AlertTriangle size={10} aria-hidden="true" /> {t(option.noteKey as MessageKey)}
                  </small>
                ) : null}
              </button>
            );
          })}
        </div>
        )}
        {['odd-even', 'big-small'].includes(active.key) ? (
          <p className="bet-tab-footnote">{t('ldTierExplain')}</p>
        ) : null}
        {active.key === 'specific' ? (
          <>
            <p className="bet-tab-odds-line">×9.5 {t('ifHit')}</p>
            <p className="bet-tab-footnote">{t('sicboLongPressHint')}</p>
          </>
        ) : null}
        {active.key === 'sicbo-bs' ? (
          <p className="bet-tab-footnote bet-tab-footnote-left">
            <AlertTriangle size={12} className="text-amber-400/85 shrink-0" aria-hidden="true" /> {t('sicboBsTripleExample')}
          </p>
        ) : null}
        {active.key === 'sicbo-sum' ? <p className="bet-tab-footnote">{t('sicboLongPressHint')}</p> : null}
        {active.key === 'sicbo-pairs' ? <p className="bet-tab-footnote">{t('sicboPairsHelp')}</p> : null}
        {active.key === 'sicbo-single' ? <p className="bet-tab-odds-line">{t('sicboSingleOddsLine')}</p> : null}
        {active.key === 'sicbo-combo' ? (
          <>
            <p className="bet-tab-odds-line">×6,5 {t('ifHit')}</p>
            <p className="bet-tab-footnote">{t('sicboComboDiceHelp')}</p>
          </>
        ) : null}

        {/* The balance rides along the chip heading, at the exact moment the
            stake is chosen, and costs no extra row — it turns red once what is
            staged is already past it. The roulette sheet did this first, to
            keep the board tall, and kept a separate wallet strip on the other
            games; that strip printed the same number a few rows above this
            one. The per-round cap went with it: capped chips grey out, the
            custom-amount sheet clamps to it, and the submit button names it,
            so it was the third place saying so. */}
        <div className="bet-step-heading">
          <span>② {t('tapChips')}</span>
          {walletKnown ? (
            <b className={`bet-step-balance${totalStake > walletLimit ? ' is-over' : ''}`} data-bet-balance-react="true">
              {t('balance')} {fmtMoney(walletLimit)}
            </b>
          ) : null}
        </div>
        {previousSettlementPending && (
          <p role="status" className="mb-3 text-sm text-amber-300" data-pending-settlement-react="true">
            {t('betPreviousSettlementNotice')}
          </p>
        )}
        <div className="chip-rail-legacy grid grid-cols-6 gap-1.5" aria-label={t('amount')}>
          {presetChips.map((chip, index) => {
            const capped = chip > maxSelectableAmount;
            return (
              <button
                key={chip}
                className={`chip-btn chip-r${index + 1}${amount === chip ? ' chip-active' : ''}${capped ? ' chip-disabled-soft' : ''}`}
                type="button"
                title={capped ? t('chipCapTooltip') : ''}
                onClick={() => selectPresetChip(chip)}
              >
                <span>{chipLabel(chip)}</span>
              </button>
            );
          })}
          <button className={`chip-btn chip-custom${amount > 0 && !presetChips.includes(amount) ? ' chip-active' : ''}`} type="button" onClick={() => setCustomAmountOpen(true)}>
            <Pencil size={14} className="chip-custom-icon" aria-hidden="true" />
            {amount > 0 && !presetChips.includes(amount)
              ? <span className="chip-custom-amt">{chipLabel(amount)}</span>
              : <span className="chip-custom-label">{t('chipCustom')}</span>}
          </button>
        </div>

        {/* Clear-all / Repeat-last toolbar — legacy geometry: two half-width tiles,
            always visible, greyed when there is nothing to act on */}
        <div className="bet-actions-row">
          <button
            className={`px-3 py-1.5 rounded-lg bg-rose-500/15 border border-rose-500/35 text-rose-300 text-[11px] font-semibold tap inline-flex items-center justify-center gap-1 ${staged.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}
            type="button"
            onClick={() => setStaged([])}
          >
            <X size={12} aria-hidden="true" />
            <span>{t('clearAll')}</span>
          </button>
          <button
            className={`px-3 py-1.5 rounded-lg bg-brand-500/15 border border-brand-500/35 text-brand-200 text-[11px] font-semibold tap inline-flex items-center justify-center gap-1 ${lastConfirmedBatch.current.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}
            type="button"
            onClick={() => setStaged(lastConfirmedBatch.current.map((bet) => ({ ...bet })))}
          >
            <History size={12} aria-hidden="true" />
            <span>{t('repeatLast')}</span>
          </button>
        </div>

        {/* The dock is the sheet's pinned footer: the action AND everything that
            reports on it. The error line used to sit outside, so once the dock
            was pinned it covered "Could not place bet" — a failed bet looked
            exactly like a dead button, and the player's next move is to tap
            again. Feedback travels with the button that produces it. */}
        <div className="bet-confirm-dock">
        {mutation.isError ? <p className="form-error">{
          previousSettlementPending && betErrorMessage(mutation.error, t) === t('betInsufficientBalance')
            ? t('betPreviousSettlementWaiting') : betErrorMessage(mutation.error, t)
        }</p> : null}
        {mutation.isSuccess ? <p className="success-note">{t('betPlaced')}</p> : null}

        {staged.length > 0 && !disabledReason ? (
          <>
          <div className="bet-payout-preview">
            {/* One pick has an "if you win"; several picks in one round have a
                ceiling, because only one of them can land. */}
            <span>{t(staged.length > 1 ? 'basketMaxWin' : 'betIfYouWin')}</span>
            {/* Headline only. A range here read as a coin flip between the two
                payouts, and the option card directly above already spells the
                edge case out ("9 → ×1.5"), so repeating it cost a line break
                and bought nothing. Unsigned: it is the return, stake included;
                only net balance moves carry a sign (D-43). */}
            <b>{fmtMoney(stagedMaxWin)}</b>
          </div>
          <button
            className="sheet-submit w-full mt-2 py-3 rounded-xl text-[13px] font-extrabold tap roul-confirm-btn inline-flex items-center justify-center gap-2"
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            <Check size={18} aria-hidden="true" />
            <span>
              {room.game === 'roulette'
                ? rouletteConfirmLabel(
                  t(staged.length === 1 ? 'rouConfirmCTA' : 'rouConfirmCTAPlural'),
                  staged.length,
                  fmtAmount(totalStake),
                  fiatCurrency,
                )
                : `${t('confirmBet')} · ${staged.length} ${t(staged.length === 1 ? 'sicboPickLabelOne' : 'sicboPicksLabel')} · ${fmtMoney(totalStake)}`}
            </span>
          </button>
          </>
        ) : (
          room.game === 'roulette' && roundAcceptsBets && currentRoundBets.length > 0 ? (
            <div className="mt-2 flex items-center justify-between gap-2">
              <button className="flex-1 px-3 py-2 rounded-lg bg-brand-500/15 border border-brand-500/30 text-brand-200 text-[12px] font-semibold inline-flex items-center justify-between gap-2 tap" type="button" onClick={onOpenBasket}>
                <span className="inline-flex items-center gap-1.5">
                  <Ticket size={14} aria-hidden="true" />
                  <span>{currentRoundBets.length} {t('bets')} · {fmtCurrency(placedStake, fiatCurrency)}</span>
                </span>
                <span className="font-mono text-emerald-400 text-[11px]">max +{chipLabel(placedMaxWin)}</span>
              </button>
              <button className="px-3 py-2 rounded-lg bg-card2 text-ink-2 border border-border1 font-semibold text-[12px] tap" type="button" onClick={onClose}>{t('close')}</button>
            </div>
          ) : (
          staged.length > 0 && !guestMode && roundAcceptsBets && disabledReason === t('betInsufficientBalance') ? (
            <button
              className="sheet-submit sheet-submit-topup w-full mt-2 py-3 rounded-xl font-extrabold tap inline-flex items-center justify-center gap-2 text-[13px]"
              type="button"
              onClick={() => { useAppStore.getState().setWalletTargetAction('deposit'); useAppStore.getState().setRoute('wallet'); onClose(); }}
            >
              <span>{t('betInsufficientBalance')} · {t('walletDeposit')} →</span>
            </button>
          ) : (
          <button className="sheet-submit w-full mt-2 bg-card2 text-ink-3 font-bold py-2.5 rounded-xl inline-flex items-center justify-center gap-2 text-[12px]" type="button" disabled>
            <Check size={16} aria-hidden="true" />
            <span>{!roundAcceptsBets ? t('roundNotOpen') : staged.length === 0 ? t('sicboStagePrompt') : disabledReason}</span>
          </button>
          )
          )
        )}
        </div>
          </>
        )}
        {customAmountOpen ? (
          <CustomAmountSheet
            amount={amount}
            currency={fiatCurrency}
            minAmount={Math.max(1, Math.min(...chips))}
            maxAmount={Math.min(perRoundMax || 50_000, Math.max(0, userWalletFiat(betUser)) || (perRoundMax || 50_000))}
            onApply={setAmount}
            onClose={() => setCustomAmountOpen(false)}
          />
        ) : null}
      </section>
      {quickEdit ? (
        <div className="qe-portal-react" onMouseDown={(event) => event.stopPropagation()}>
          <div className="qe-backdrop" onClick={() => setQuickEdit(null)} />
          <div className="quick-edit-card" role="dialog" aria-modal="true">
            <div className="qe-head">
              <div>
                <span className="qe-eyebrow">{t('rouQEditEdit')}</span>
                <strong className="qe-label">{quickEdit.label}</strong>
              </div>
              <button className="qe-close" type="button" onClick={() => setQuickEdit(null)} aria-label={t('close')}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="qe-stats">
              <div className="qe-stat placed">
                <span><Lock size={10} aria-hidden="true" /> {t('rouQEditPlaced')}</span>
                <b>{fmtCurrency(placedByKey.get(`${quickEdit.tab}:${quickEdit.pick}`) || 0, fiatCurrency)}</b>
              </div>
              <div className="qe-stat pending">
                <span>{t('rouQEditPending')}</span>
                <b>{fmtCurrency(stagedByKey.get(`${quickEdit.tab}:${quickEdit.pick}`)?.amount || 0, fiatCurrency)}</b>
              </div>
            </div>
            <div className="qe-chip-rail">
              {presetChips.map((chip) => {
                const capped = chip > maxSelectableAmount;
                return (
                  <button
                    key={chip}
                    type="button"
                    className={`chip-btn ${amount === chip ? 'chip-active' : ''}${capped ? ' chip-disabled-soft' : ''}`}
                    title={capped ? t('chipCapTooltip') : ''}
                    onClick={() => selectPresetChip(chip)}
                  >
                    {chipLabel(chip)}
                  </button>
                );
              })}
              <button type="button" className={`chip-btn chip-custom${amount > 0 && !presetChips.includes(amount) ? ' chip-active' : ''}`} onClick={() => setCustomAmountOpen(true)}>
                {amount > 0 && !presetChips.includes(amount)
                  ? <span className="chip-custom-amt">{chipLabel(amount)}</span>
                  : <span className="chip-custom-label">{t('chipCustom')}</span>}
              </button>
            </div>
            <div className="qe-btn-row">
              <button className="qe-btn qe-btn-sub tap" type="button" disabled={(stagedByKey.get(`${quickEdit.tab}:${quickEdit.pick}`)?.amount || 0) === 0} onClick={qeSub}>
                <span className="qe-sign">−</span><span className="qe-amt">{chipLabel(amount)}</span><span className="qe-unit">{fiatCurrency}</span>
              </button>
              <button className="qe-btn qe-btn-add tap" type="button" onClick={qeAdd}>
                <span className="qe-sign">+</span><span className="qe-amt">{chipLabel(amount)}</span><span className="qe-unit">{fiatCurrency}</span>
              </button>
            </div>
            <button className="qe-clear" type="button" disabled={(stagedByKey.get(`${quickEdit.tab}:${quickEdit.pick}`)?.amount || 0) === 0} onClick={qeClear}>
              {t('rouQEditClear')}
            </button>
            <div className="qe-total-line">
              {t('rouQEditRoundTotal')}: <span className="qe-total-amt">{staged.length} {t('sicboPicksLabel')} · {fmtMoney(totalStake)}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CustomAmountSheet({
  amount,
  currency,
  minAmount,
  maxAmount,
  onApply,
  onClose,
}: {
  amount: number;
  currency: string;
  minAmount: number;
  maxAmount: number;
  onApply: (amount: number) => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const [draft, setDraft] = useState(() => amount > 0 ? String(amount) : '');
  const parsed = parseMoneyInput(draft);
  const valid = parsed >= minAmount && parsed <= maxAmount;

  function apply() {
    if (!valid) return;
    onApply(parsed);
    onClose();
  }

  function pressKey(key: string) {
    setDraft((current) => {
      if (key === 'clear') return '';
      if (key === 'backspace') return current.slice(0, -1);
      if (key === '.') {
        if (!current || current.includes('.') || current.includes(',')) return current || '0.';
        return `${current}.`;
      }
      const next = `${current}${key}`.replace(/^0+(\d)/, '$1');
      const [whole, cents = ''] = next.split(/[.,]/);
      return cents ? `${whole}.${cents.slice(0, 2)}`.slice(0, 12) : next.slice(0, 12);
    });
  }

  return (
    <div className="sheet-backdrop nested-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="custom-amount-sheet-react" role="dialog" aria-modal="true" aria-label={t('customAmount')} onMouseDown={(event) => event.stopPropagation()} data-custom-amount-react="bet">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('customAmount')}</h2>
            <p>{t('maxBet')}: {fmtMoney(maxAmount)}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div className="custom-amount-display" aria-live="polite">
          <span>{t('youBet')}</span>
          <strong>{draft || '0'}</strong>
          <small>{currency}</small>
        </div>
        <div className="custom-keypad-react" data-custom-amount-keypad-react="true">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
            <button key={key} className={key === 'backspace' ? 'danger' : ''} type="button" onClick={() => pressKey(key)}>
              {key === 'backspace' ? '⌫' : key}
            </button>
          ))}
        </div>
        {draft && !valid ? <p className="form-error">{t('customAmountInvalid')}</p> : null}
        <div className="custom-amount-actions">
          <button className="secondary-button" type="button" onClick={() => pressKey('clear')}>{t('clear')}</button>
          <button className="primary-button" type="button" disabled={!valid} onClick={apply}>{t('caUse')}</button>
        </div>
      </section>
    </div>
  );
}

function parseMoneyInput(value: string) {
  const normalized = value.replace(',', '.');
  const number = Number.parseFloat(normalized);
  if (!Number.isFinite(number)) return 0;
  return Math.round(number * 100) / 100;
}

// A bet 401 means the 30-min access token expired (a page left open for hours).
// Public reads still work, so the app looked logged in. Try a silent refresh
// (works while the 30-day session is alive) so the next tap goes through — only
// drop to the login screen if the session is truly gone. Everything else keeps
// its specific message.
function betErrorToast(error: Error, t: ReturnType<typeof useI18nStore.getState>['t']): string {
  const message = error?.message || '';
  if (message.includes('UNAUTHORIZED') || message === 'HTTP_401') {
    const auth = useAuthStore.getState();
    if (auth.token && !auth.guestMode) {
      void auth.refreshSession().then((ok) => {
        if (!ok) auth.logout();
      });
    }
    return t('sessionExpired');
  }
  return betErrorMessage(error, t);
}

/** fetch() rejects with a TypeError whose message is locale-dependent and
 *  browser-specific ("Failed to fetch", "NetworkError when attempting to fetch
 *  resource", "Load failed"). None of it belongs on screen. */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const message = String((error as Error)?.message || '');
  return /failed to fetch|networkerror|load failed|network request failed/i.test(message);
}

function betErrorMessage(error: Error, t: ReturnType<typeof useI18nStore.getState>['t']) {
  // error.message is the backend `code` (or HTTP_<status>). The controller has
  // ~20 distinct reject codes; map the meaningful ones and — crucially — never
  // collapse the rest into an opaque "Could not place bet": append the raw code
  // so the actual reason is always visible.
  const message = error.message || '';
  // Transport failures are not backend reject codes — appending them raw put the
  // browser's English "Failed to fetch" in front of the player. The bet never
  // left the device in this case, so say so.
  if (isNetworkError(error)) return t('betNetworkError');
  if (message.includes('INSUFFICIENT_BALANCE')) return t('betInsufficientBalance');
  if (message.includes('VIP')) return t('vipTooLow');
  if (message.includes('RATE_LIMIT')) return t('betRateLimited');
  // Check before the broad ROUND match — PREV_ROUND_DRAWING contains "ROUND".
  if (message.includes('PREV_ROUND_DRAWING')) return t('betPrevRoundDrawing');
  // Per-round / single-bet caps — check BEFORE the broad ROUND match, since
  // EXCEED_PER_ROUND_MAX also contains "ROUND".
  if (
    message.includes('PER_ROUND_MAX') ||
    message.includes('SINGLE_BET_MAX') ||
    message.includes('TOO_MANY_BET_ITEMS')
  ) {
    return t('betLimitExceeded');
  }
  if (message.includes('ROUND')) return t('roundNotOpen'); // ROUND_NOT_OPEN / _LOCKED / _NUMBER_MISMATCH
  // Everything else (MAINTENANCE, ROOM_NOT_AVAILABLE, COOL_OFF, daily/weekly
  // limits, …) keeps its raw code so the real reason is never hidden.
  if (!message || message.startsWith('HTTP_')) return t('betFailed');
  return `${t('betFailed')} · ${message}`;
}

function formatArchiveTime(seconds: number) {
  if (!seconds) return '--:--';
  return new Date(seconds * 1000).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatClock(totalSeconds: number) {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// One source of truth for the round countdown shown in BOTH the room widget and
// every bet panel, so they never disagree. Phases: betting (green, red in the
// last 30s) → LOCKED + 5→1 (the lock buffer) → Waiting block N/3 → Computing
// result → (result lands → betting reopens).
export type RoundTone = 'open' | 'urgent' | 'locked' | 'waiting';
export type RoundPhase = { label: string; value: string; tone: RoundTone };

const ROUND_TONE_COLOR: Record<RoundTone, string> = {
  open: '#34d399', // green — relaxed, plenty of time
  urgent: '#fb7185', // red — last 30s to bet
  locked: '#fb7185', // red — bets locked, draw about to start
  waiting: '#22d3ee', // cyan — drawing / computing
};

function roundPhaseDisplay(args: {
  drawingRound: boolean;
  waitingSub: string | null;
  waitingConfirmed: number;
  waitingPill: string;
  lockSeconds: number;
  t: ReturnType<typeof useI18nStore.getState>['t'];
}): RoundPhase {
  const { drawingRound, waitingSub, waitingConfirmed, waitingPill, lockSeconds, t } = args;
  if (drawingRound) {
    if (waitingSub === 'lockbuffer') return { label: t('locked'), value: waitingPill, tone: 'locked' };
    if (waitingSub === 'computing') return { label: t('computingResult'), value: '···', tone: 'waiting' };
    return { label: t('waitingBlock'), value: `${waitingConfirmed + 1}/3`, tone: 'waiting' };
  }
  return { label: t('closes'), value: formatClock(lockSeconds), tone: lockSeconds <= 30 ? 'urgent' : 'open' };
}

function addChip(chips: Record<string, number>, denom: number) {
  const key = String(denom);
  return { ...chips, [key]: Number(chips[key] || 0) + 1 };
}

function chipsTotal(chips: Record<string, number>) {
  return Object.entries(chips).reduce((sum, [denom, qty]) => sum + Number(denom) * Number(qty), 0);
}

/** Remove one chip of `denom` (fall back to the largest present denomination). */
function subChip(chips: Record<string, number>, denom: number) {
  const next = { ...chips };
  const key = String(denom);
  let target = next[key] > 0 ? key : '';
  if (!target) {
    const denoms = Object.keys(next).filter((d) => next[d] > 0).map(Number).sort((a, b) => b - a);
    if (denoms.length === 0) return next;
    target = String(denoms[0]);
  }
  next[target] = Number(next[target]) - 1;
  if (next[target] <= 0) delete next[target];
  return next;
}

function chipsToLines(chips: Record<string, number>): ChipLine[] {
  return Object.entries(chips)
    .map(([denom, qty]) => ({ denom: Number(denom), qty: Number(qty) }))
    .filter((chip) => chip.denom > 0 && chip.qty > 0);
}

// Legacy fmtBig/chipLabel — short chip labels ("10" / "1K" / "1,5K" pt-BR comma).
function chipLabel(n: number) {
  if (n === null || n === undefined || Number.isNaN(n)) return '0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const trim = (v: string) => v.replace(/[.,]?0+$/, '');
  if (abs >= 1_000_000_000) return `${sign}${trim((abs / 1_000_000_000).toFixed(1).replace('.', ','))}B`;
  if (abs >= 1_000_000) return `${sign}${trim((abs / 1_000_000).toFixed(1).replace('.', ','))}M`;
  if (abs >= 1000) return `${sign}${trim((abs / 1000).toFixed(1).replace('.', ','))}K`;
  return `${sign}${abs}`;
}

function DiceFaceSvg({ n, size = 32 }: { n: number; size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} className="inline-block">
      <rect x="2" y="2" width="20" height="20" rx="3.5" fill="#fef3c7" stroke="#78350f" strokeWidth="0.6" />
      {diceFacePips(n).map(([cx, cy], pipIndex) => (
        <circle key={pipIndex} cx={cx} cy={cy} r="1.7" fill="#1c1917" />
      ))}
    </svg>
  );
}

// Legacy currentDailyN: 1-288 daily sequence from the round's 5-min cutoff (BRT = UTC-3).
// Rounds are 5 minutes apart, so a day holds 86400/300 = 288 of them. Users think
// in "which of today's 288", not the global counter (#22045) — see dailySeq.
const ROUNDS_PER_DAY = 288;

function dailySeq(round: { ends_at?: number; cutoff_ts?: number; starts_at?: number } | null | undefined) {
  return roundSeqFromRound(round);
}

function roadLegend(game: GameCode, roadKind: RoadKind, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (game === 'roulette') {
    if (roadKind === 'redblack') return [{ label: t('red'), tone: 'rose', side: 'red' }, { label: t('black'), tone: 'slate', side: 'black' }];
    if (roadKind === 'hilo') return [{ label: '19–36', tone: 'rose', side: 'big' }, { label: '1–18', tone: 'teal', side: 'small' }];
    return [{ label: t('odd'), tone: 'rose', side: 'odd' }, { label: t('even'), tone: 'teal', side: 'even' }];
  }
  if (roadKind === 'oddeven') return [{ label: t('odd'), tone: 'rose', side: 'odd' }, { label: t('even'), tone: 'teal', side: 'even' }];
  return [{ label: t('big'), tone: 'rose', side: 'big' }, { label: t('small'), tone: 'teal', side: 'small' }];
}

// Legacy analyticsSicboColdSections: 5 sections with per-cell means, dice pips and sum odds.
function SicboColdSections({ rounds, bounded = false, sort = 'natural', t, onQuickBet, locked }: {
  rounds: AnalyticsRound[];
  bounded?: boolean;
  sort?: 'natural' | 'missing';
  t: ReturnType<typeof useI18nStore.getState>['t'];
  onQuickBet?: (target: BetTarget) => void;
  locked?: boolean;
}) {
  const findIdx = (pred: (round: AnalyticsRound) => boolean) => rounds.findIndex(pred);
  // Each section's predicate is repeated verbatim by the settlement engine:
  // Single n pays on count>=1, Double n on count>=2, Triple n on triple===n,
  // Any Double on a non-triple pair, Any Triple on any triple, Sum on equality.
  // That equivalence is what makes tapping a count into a bet honest.
  const tile = (target: BetTarget, betLabel: string, className: string, children: ReactNode, key?: string) => (
    <ColdTile key={key} game="sicbo" target={target} onQuickBet={onQuickBet} locked={locked} betLabel={betLabel} className={className}>
      {children}
    </ColdTile>
  );
  const aria = (pick: string) => t('analyticsColdTapAria').replace('%pick%', pick);
  const isExactlyTwo = (round: AnalyticsRound, n: number) => round.dice.filter((v) => v === n).length === 2 && round.sicboTriple == null;
  const isAtLeastTwo = (round: AnalyticsRound, n: number) => round.dice.filter((v) => v === n).length >= 2;
  const anyPair = findIdx((round) => round.sicboTriple == null && [1, 2, 3, 4, 5, 6].some((n) => isExactlyTwo(round, n)));
  const anyTriple = findIdx((round) => round.sicboTriple != null);
  const faces = [1, 2, 3, 4, 5, 6];
  const sumOutcomes: Record<number, number> = { 4: 3, 5: 6, 6: 10, 7: 15, 8: 21, 9: 25, 10: 27, 11: 27, 12: 25, 13: 21, 14: 15, 15: 10, 16: 6, 17: 3 };
  // findIndex returns the real gap: how many rounds have been drawn since this
  // outcome last landed. -1 means it never landed inside the history we hold,
  // and the history now walks back until every outcome has been found, so that
  // is rare. When it does happen the count is a floor, not a measurement, and
  // the "+" says so — three tiles all reading a flat "288" was the window size
  // impersonating a gap.
  const missText = (idx: number) => (idx < 0 ? `${fmtInt(rounds.length)}${bounded ? '+' : ''}` : fmtInt(idx));
  // Each grid sorts inside itself: the sections mean different things and the
  // base rates differ by two orders of magnitude, so a single ranking across
  // all 32 tiles would put every triple on top and say nothing.
  const coldestFirst = <T extends { missing: number }>(items: T[]) => (
    sort === 'missing' ? [...items].sort((a, b) => coldRank(b.missing) - coldRank(a.missing)) : items
  );
  const singles = coldestFirst(faces.map((n) => ({ n, missing: findIdx((round) => round.dice.includes(n)) })));
  const doubles = coldestFirst(faces.map((n) => ({ n, missing: findIdx((round) => isAtLeastTwo(round, n)) })));
  const triples = coldestFirst(faces.map((n) => ({ n, missing: findIdx((round) => round.sicboTriple === n) })));
  const sums = coldestFirst(Array.from({ length: 14 }, (_, index) => index + 4).map((sum) => {
    const p = sumOutcomes[sum] / 216;
    return { sum, mean: (1 - p) / p, missing: findIdx((round) => round.sicboSum === sum) };
  }));

  return (
    <div className="sicbo-cold-sections" data-sicbo-cold-sections-react="true">
      <div className="sicbo-cold-section">
        <span className="sicbo-cold-title">{t('analyticsColdAggregate')}</span>
        <div className="sicbo-cold-grid cols-2">
          {tile({ tab: 'sicbo-pairs', pick: 'ad' }, aria(t('sicboAnyDouble')), `analytics-cold-tile tier-${coldTier(anyPair, 1.4)}`, (
            <>
              <strong>{t('sicboAnyDouble')}</strong>
              <span>{missText(anyPair)}</span>
              <em>{t('analyticsColdMeanShort').replace('%n%', '1,4')}</em>
            </>
          ))}
          {tile({ tab: 'sicbo-pairs', pick: 'any' }, aria(t('sicboAnyTriple')), `analytics-cold-tile tier-${coldTier(anyTriple, 35)}`, (
            <>
              <strong>{t('sicboAnyTriple')}</strong>
              <span>{missText(anyTriple)}</span>
              <em>{t('analyticsColdMeanShort').replace('%n%', '35')}</em>
            </>
          ))}
        </div>
      </div>
      <div className="sicbo-cold-section">
        <span className="sicbo-cold-title">{t('analyticsColdSingles')} · {t('analyticsColdMeanShort').replace('%n%', '1,37')}</span>
        <div className="sicbo-cold-grid cols-6">
          {singles.map(({ n, missing }) => tile({ tab: 'sicbo-single', pick: String(n) }, aria(`${t('tab_sicbo-single')} ${n}`), `analytics-cold-tile tier-${coldTier(missing, 1.37)}`, (
            <>
              <DiceFaceSvg n={n} size={22} />
              <span>{missText(missing)}</span>
            </>
          ), `s${n}`))}
        </div>
      </div>
      <div className="sicbo-cold-section">
        <span className="sicbo-cold-title">{t('analyticsColdDoubles')} · {t('analyticsColdMeanShort').replace('%n%', '12,5')}</span>
        <div className="sicbo-cold-grid cols-3">
          {doubles.map(({ n, missing }) => tile({ tab: 'sicbo-pairs', pick: `d${n}` }, aria(t('sicboDoubleN').replace('%n%', String(n))), `analytics-cold-tile tier-${coldTier(missing, 12.5)}`, (
            <>
              <span className="sicbo-cold-dice"><DiceFaceSvg n={n} size={16} /><DiceFaceSvg n={n} size={16} /></span>
              <span>{missText(missing)}</span>
            </>
          ), `d${n}`))}
        </div>
      </div>
      <div className="sicbo-cold-section">
        <span className="sicbo-cold-title">{t('analyticsColdTriples')} · {t('analyticsColdMeanShort').replace('%n%', '215')}</span>
        <div className="sicbo-cold-grid cols-3">
          {triples.map(({ n, missing }) => tile({ tab: 'sicbo-pairs', pick: `t${n}` }, aria(t('sicboTripleN').replace('%n%', String(n))), `analytics-cold-tile tier-${coldTier(missing, 215)}`, (
            <>
              <span className="sicbo-cold-dice"><DiceFaceSvg n={n} size={14} /><DiceFaceSvg n={n} size={14} /><DiceFaceSvg n={n} size={14} /></span>
              <span>{missText(missing)}</span>
            </>
          ), `t${n}`))}
        </div>
      </div>
      <div className="sicbo-cold-section">
        <span className="sicbo-cold-title">{t('analyticsColdSums')}</span>
        {/* Five columns, not seven. Every other section states its base rate in
            the section title ("média ~215"), but each sum has its own — 1 in 72
            for a 4, 1 in 8 for a 10 — so it has to live on the tile, and at
            seven columns there was no room to print it legibly. Without it the
            missing count is unreadable: 268 is unremarkable for a 1-in-216
            outcome and extraordinary for a 1-in-8 one, and the tile's colour
            already encodes exactly that comparison without telling anyone. */}
        <div className="sicbo-cold-grid cols-5">
          {sums.map(({ sum, mean, missing }) => tile({ tab: 'sicbo-sum', pick: `s${sum}` }, aria(`${t('sicboSumLabel')} ${sum}`), `analytics-cold-tile sum-tile tier-${coldTier(missing, mean)}`, (
            <>
              <strong>{sum}</strong>
              <em className="sum-odds">×{SICBO_SUM_ODDS[sum]}</em>
              <span>{missText(missing)}</span>
              <em className="sum-mean">{t('analyticsColdMeanShort').replace('%n%', fmtInt(Math.round(mean)))}</em>
            </>
          ), `sum${sum}`))}
        </div>
      </div>
    </div>
  );
}
